
const libPictSectionForm = require('pict-section-form');
const libPictSectionRecordSet = require('pict-section-recordset');
const libPictSectionPicker = require('pict-section-picker');
const libPictSectionAccordion = require('pict-section-accordion');
const libPictDataExplorer = require('pict-dataexplorer');
const libPictSectionDataImport = require('pict-section-dataimport');
const libPictTemplatePreprocessor = require('pict-template-preprocessor');
const libInlineDocumentation = require('pict-section-inlinedocumentation');
// The full-page "Headlight Academy" browser — reuses the inline-doc pipeline above (TOC + AcademyContent docs).
const AcademyProvider = require('./help/Academy-Provider.js');
const AcademyView = require('./help/Academy-View.js');

// pict-section-navigation (published npm module): powers the app's topic navigation,
// the ⌘K launcher, and the KPI jumbotrons. Config lives in source/navigation-graph.js
// (the nav tree) + source/navigation-topics.js (topics + KPIs).
const libPictSectionNavigation = require('pict-section-navigation');
const libNavigationGraph = require('./navigation-graph.js');
const libMasterListCatalog = require('./master-lists/Headlight-MasterLists.js');
const libMasterListBuilder = require('./master-lists/MasterList-Builder.js');
const libMasterListAssociations = require('./master-lists/MasterList-Associations.js');
const libConnectionsGenerated = require('./master-lists/Connections.generated.js');
const libAssociationChipDecoration = require('./master-lists/Association-Chip-Decoration.js');
const libReadViewTabBarDecoration = require('./views/ReadView-TabBar-Decoration.js');
const libUserRoleMembershipFilter = require('./user-role-filter/UserRoleMembershipFilter.js');
// Resolves "whose work is this?" once per session — which inspections the signed-in user reaches, and
// which teams they are on. A LENS, not a permission: see the header of [[InspectionScope-Service.js]].
const libInspectionScopeService = require('./access/InspectionScope-Service.js');
const libTabBar = require('./views/TabBar.js');
const libChildListTab = require('./views/ChildListTab.js');
const libSpecSetMaterialsTab = require('./views/SpecSetMaterialsTab.js');
const libMaterialSpecSetsTab = require('./views/MaterialSpecSetsTab.js');
const libOrganizationAddressTab = require('./views/OrganizationAddressTab.js');
const libMaterialOrgAssociationEditor = require('./views/MaterialOrganizationAssociationEditor.js');
// Platform documentation registry: route → AcademyContent doc, across every feature area. Docs are managed in
// AcademyContent (seeded via source/help/PlatformDocs-Seed.js) and surfaced in the F1 / `?` panel via the
// displayContent bridge; cross-tenant reads go through the API's PlatformDocument endpoint.
const libPlatformDocs = require('./help/PlatformDocs.js');
// The segmented CodeMirror markdown editor (same component the inline-doc module wraps and the Email Notifications
// editor reuses) — powers in-panel editing of platform docs. CodeMirror bundle is already vendored + loaded app-wide.
const libPlatformDocMarkdownEditor = require('pict-section-markdowneditor');
const libPlatformRecordCards = require('./record-cards/Platform-RecordCards.js');

// the user's chosen navigation style is persisted
// here. 'Sidebar' (a persistent docked rail) is the default; 'Hub' / 'List' / 'Palette'
// render the navigation as the Configurations home screen instead.
const NAV_STYLE_STORAGE_KEY = 'headlight-platform-config-nav-render-type';
const NAV_STYLE_DEFAULT = 'List';

// Scoped styles for the platform-documentation help panel's topic nav bar (shipped inline with the injected
// content so it needs no global CSS). Theme-aware via the shared --theme-color-* vars; active topic reads as a chip.
const PLATFORM_HELP_STYLE =
	'<style>'
	+ '.platform-help-nav { display:flex; flex-wrap:wrap; gap:0.35rem; padding:0 0 0.7rem; margin:0 0 0.8rem; border-bottom:1px solid var(--theme-color-border-light, #f0f2f6); }'
	// The pill color rules are scoped under `.platform-help-nav` on purpose: the panel body carries the
	// `.pict-content` class, whose `.pict-content a { color: brand-primary }` (specificity 0,1,1) would
	// otherwise beat a bare `.platform-help-navlink-active` (0,1,0) and paint the ACTIVE pill's text the brand
	// blue over its blue background — unreadable. The two-class scope (0,2,0) outranks it. Keep the scope.
	+ '.platform-help-nav .platform-help-navlink { font-size:0.78rem; font-weight:600; line-height:1; padding:0.32rem 0.6rem; border-radius:999px; text-decoration:none;'
	+ ' color:var(--theme-color-text-muted, #6b7686); background:var(--theme-color-background-subtle, #f4f6fa); border:1px solid var(--theme-color-border-light, #e6eaf1); }'
	+ '.platform-help-nav .platform-help-navlink:hover { color:var(--theme-color-text, #22303f); background:var(--theme-color-background-hover, #e9eef5); }'
	+ '.platform-help-nav .platform-help-navlink-active { color:#ffffff; background:var(--theme-color-primary, #2f6fed); border-color:var(--theme-color-primary, #2f6fed); }'
	+ '.platform-help-loading { color:var(--theme-color-text-muted, #8894a4); font-style:italic; }'
	// Edit affordances (shown only to editors): the pencil in read mode + the editor toolbar/buttons in edit mode.
	+ '.platform-help-editbar { display:flex; align-items:center; gap:0.5rem; margin:0 0 0.7rem; }'
	+ '.platform-help-editbar-spacer { flex:1 1 auto; }'
	+ '.platform-help-editbtn { font-size:0.76rem; font-weight:600; padding:0.28rem 0.6rem; border-radius:6px; cursor:pointer;'
	+ ' color:var(--theme-color-text-muted, #6b7686); background:var(--theme-color-background-subtle, #f4f6fa); border:1px solid var(--theme-color-border-light, #e6eaf1); }'
	+ '.platform-help-editbtn:hover { color:var(--theme-color-text, #22303f); background:var(--theme-color-background-hover, #e9eef5); }'
	+ '.platform-help-editmode { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; padding:0.2rem 0.5rem; border-radius:999px; }'
	+ '.platform-help-editmode-canonical { color:#ffffff; background:var(--theme-color-status-warning, #b8860b); }'
	+ '.platform-help-editmode-override { color:#ffffff; background:var(--theme-color-primary, #2f6fed); }'
	+ '.platform-help-editmount { min-height:16rem; border:1px solid var(--theme-color-border-light, #e6eaf1); border-radius:8px; }'
	+ '.platform-help-input { flex:1 1 auto; font:inherit; font-size:0.9rem; padding:0.4rem 0.55rem; border-radius:6px;'
	+ ' border:1px solid var(--theme-color-border-medium, #d5dbe3); background:var(--theme-color-background-primary, #fff); color:var(--theme-color-text-primary, #1f2733); }'
	+ '.platform-help-editnote { font-size:0.8rem; color:var(--theme-color-text-muted, #6b7686); margin:0 0 0.6rem; }'
	+ '.platform-help-btn { font-size:0.82rem; font-weight:600; padding:0.4rem 0.85rem; border-radius:6px; cursor:pointer; border:1px solid transparent; }'
	+ '.platform-help-btn-primary { color:#ffffff; background:var(--theme-color-primary, #2f6fed); }'
	+ '.platform-help-btn-ghost { color:var(--theme-color-text, #22303f); background:transparent; border-color:var(--theme-color-border-medium, #d5dbe3); }'
	+ '.platform-help-btn-danger { color:var(--theme-color-status-danger, #c0392b); background:transparent; border-color:var(--theme-color-status-danger, #c0392b); }'
	+ '.platform-help-btn[disabled] { opacity:0.55; cursor:default; }'
	+ '</style>';
const libHeadlightConfigurableApplication = require('headlight-configurable-application');

// First-draft per-record-set foreign-key → entity mappings for the PSRS entity-Picker feature. Clean
// `ID<Entity>` columns (IDOrganization, IDContract, IDRole, …) resolve automatically by convention; this
// map declares the exceptions the convention can't infer — prefixed / suffixed / self-referential columns.
// Applied to DefaultRecordSetConfigurations below as RecordSetConfiguration.ForeignKeyEntities.
const PSRS_FOREIGN_KEY_ENTITIES =
{
	RoleEdge:         { ParentIDRole: 'Role', ChildIDRole: 'Role' },
	Material:         { IDMaterialParent: 'Material' },
	Lab:              { IDLabParent: 'Lab' },
	Sample:           { ParentIDSample: 'Sample', SampledByIDUser: 'User', WitnessIDUser: 'User', AuthorizedIDUser: 'User', CMSIDUser: 'User' },
	Role:             { LinkedIDUser: 'User' },
	MixSpecification: { ApprovingIDUser: 'User' },
	Contract:         { ItemIDTestSpecificationSet: 'TestSpecificationSet', MaterialIDTestSpecificationSet: 'TestSpecificationSet' },
	// Line/bid items live under a Contract + Project — surface both as named columns AND first-class
	// entity-picker filters (an ID<Entity> column resolves to the referenced entity for display, and its
	// filter becomes the entity picker rather than a raw numeric match). LineItem also references its BidItem.
	LineItem:         { IDContract: 'Contract', IDProject: 'Project', IDBidItem: 'BidItem', IDPayItem: 'PayItem', IDFundingCategory: 'FundingCategory' },
	BidItem:          { IDContract: 'Contract', IDProject: 'Project', IDPayItem: 'PayItem', IDOrganization: 'Organization' },
};

// Per-record-set field-label overrides (RecordSetConfiguration.FieldLabels): the IDOrganization on a
// Contract / Project is the prime contractor.
const PSRS_FIELD_LABELS =
{
	Contract: { IDOrganization: 'Prime Contractor' },
	Project:  { IDOrganization: 'Prime Contractor' },
};
const HeadlightDynamicInputsBundle = require('headlight-dynamic-inputs-bundle');

const libPlatformConfigurationRouter = require('./providers/PlatformConfiguration-Router.js');

// PSRS filter data/persistence management
const libFilterDataProvider = require('pict-section-recordset/source/providers/Filter-Data-Provider.js');
const libFilterPersistenceView = require('pict-section-recordset/source/views/Filter-PersistenceView.js');

// PSRS dynamic recordset solver (required for manifest-based list rendering)
const libDynamicRecordsetSolver = require('pict-section-recordset/source/providers/RecordSet-DynamicRecordsetSolver.js');

// Views
const configEmptyView = require('./views/Empty.json');
const ConfigurationIndex = require('./views/ConfigurationIndex.js');
// Configurations home + per-topic dashboard pages.
const ConfigurationNavigation = require('./views/ConfigurationNavigation.js');
const TopicPage = require('./views/TopicPage.js');
const DataExplorer = require('./views/DataExplorer.js');
const SamplePlanProvider = require('./sample-plan/SamplePlan-Provider.js');
const SamplePlan = require('./views/SamplePlan.js');
const SamplePlanDashboardProvider = require('./sample-plan/SamplePlanDashboard-Provider.js');
const SamplePlanDashboard = require('./views/SamplePlanDashboard.js');
const SamplePlanCopy = require('./sample-plan/SamplePlanCopy-View.js');
const ObservationListProvider = require('./observations/ObservationList-Provider.js');
const ObservationListView = require('./views/ObservationList.js');
const DocumentListProvider = require('./documents/DocumentList-Provider.js');
const DocumentFrameSwitcherProvider = require('./documents/DocumentFrameSwitcher-Provider.js');
const DocumentListView = require('./views/DocumentList.js');
const InspectionScheduleProvider = require('./inspections/InspectionSchedule-Provider.js');
const InspectionScheduleView = require('./views/InspectionSchedule.js');
const DocumentThroughputProvider = require('./documents/DocumentThroughput-Provider.js');
const DocumentThroughputView = require('./views/DocumentThroughput.js');
// Customer Support console (#/Support): support calls are Support-Call Documents under a revolving monthly Support
// Project, worked through the SupportCallWorkflow, with screenshot/file attachments as Observations. See [[Support-Provider.js]].
const SupportProvider = require('./support/Support-Provider.js');
const SupportView = require('./views/Support.js');
// The merge-tickets wizard (fold duplicate/misfiled Support-Call tickets into a parent). See [[Support-Merge-Provider.js]].
const SupportMergeProvider = require('./support/Support-Merge-Provider.js');
// Customer-facing read-only open-cases dashboard (#/SupportPortal) for the Support-View tier. See [[SupportPortal-Provider.js]].
const SupportPortalProvider = require('./support/SupportPortal-Provider.js');
const SupportPortalView = require('./views/SupportPortal.js');
// Form Concierge (#/FormConcierge): super-user screen that drives the plansheet form pipeline through the same-origin
// façade (endpoints/FormConcierge-Service.js). See [[FormConcierge-Provider.js]].
const FormConciergeProvider = require('./form-concierge/FormConcierge-Provider.js');
const FormConciergeView = require('./views/FormConcierge.js');
// Asset-type metadata descriptors: the vocabulary every asset screen renders through, so the tooling
// is driven by the asset type rather than compiled against one standard. See [[AssetDescriptor.js]].
const libAssetDescriptorProvider = require('./assets/AssetDescriptor-Provider.js');
const PhysicalAssetDashboardProvider = require('./assets/PhysicalAssetDashboard-Provider.js');
const PhysicalAssetDashboardView = require('./views/PhysicalAssetDashboard.js');
const libImportantFilesProvider = require('./important-files/ImportantFiles-Provider.js');
const libImportantFiles = require('./views/ImportantFiles.js');
const libBulkUploadProvider = require('./providers/BulkUpload-Provider.js');
const libBulkUpload = require('./views/BulkUpload.js');
const libProjectFundingProvider = require('./project-funding/ProjectFunding-Provider.js');
const libProjectFunding = require('./views/ProjectFunding.js');
const libChangeOrdersProvider = require('./providers/ChangeOrders-Provider.js');
const libChangeOrders = require('./views/ChangeOrders.js');
const libPictSectionFormHost = require('pict-section-form-host');
// LIMS Samples — the configuration-based Sample screens rebuilt from web/lims. The create/edit/view forms are
// rendered from the customer's configured ManifestSchema via the shared manifest-packaging-service, and persisted
// through the shared web/providers/* LIMS providers (same save pipeline the LIMS app uses: QR minting, storage-
// location join, sample-log). See memory config-app-lims-samples. These shared providers extend pict-provider and
// the config-app bundler resolves ../../providers/* exactly as the base class resolves web/providers/Session-Provider.
const libManifestPackagingService = require('manifest-packaging-service');
// Reusable per-customer "custom metadata" form: renders an entity's CustomProperties blob via a ManifestSchema-
// driven pict-section-form (read-only/edit theme swap) with an entitlement-gated raw-JSON advanced toggle. See
// memory config-app-entity-metadata.
const libEntityMetadataFormProvider = require('./entity-metadata/EntityMetadataForm-Provider.js');
const libUtilityProvider = require('../../providers/UtilityProvider.js');
const libLocationProvider = require('../../providers/LocationProvider.js');
const libSampleProvider = require('../../providers/SampleProvider.js');
const libSampleLogProvider = require('../../providers/SampleLogProvider.js');
const libLineItemProvider = require('../../providers/LineItemProvider.js');
const libTestInstanceProvider = require('../../providers/TestInstanceProvider.js');
const libHeadlightSamples = require('@paviasystems/headlight-samples');
const libDocumentProvider = require('../../providers/DocumentProvider.js');
const libGeoProvider = require('../../providers/GeoProvider.js');
const libQRCodeProvider = require('../../providers/QRCodeProvider.js');
const libScanToOpenProvider = require('./providers/ScanToOpen-Provider.js');
const libRecordExtensionProvider = require('../../providers/RecordExtension-Provider.js');
const libFormInitialBundleProvider = require('../../providers/FormInitialBundle-Provider.js');
// SampleWorkspace is now a config-driven pict-section-workspace (registered via workspace-configs); its provider
// subclass is required by Sample.config.js. SampleFormHost (its manifest form runner, below) stays here.
// The Headlight Workspaces framework (pict-section-workspace) + the config-driven workspaces registered on it.
const libWorkspaceConfigs = require('./workspace-configs');
const DatalakeCatalog = require('./views/DatalakeCatalog.js');
const libDatalakeCatalogProvider = require('./providers/DatalakeCatalog-Provider.js');
const libDataDownloader = require('./views/DataDownloader.js');
const libDataDownloaderProvider = require('./providers/DataDownloader-Provider.js');
const DatalakeDataset = require('./views/DatalakeDataset.js');
const libDatalakeDatasetProvider = require('./providers/DatalakeDataset-Provider.js');
const DatalakeProjectionEditor = require('./views/DatalakeProjectionEditor.js');
const libDatalakeProjectionEditorProvider = require('./providers/DatalakeProjection-Editor-Provider.js');
const libNavigationTopics = require('./navigation-topics.js');
const libModuleAccess = require('./access/Module-Access.js');
const CustomerProvisioning = require('./views/CustomerProvisioning.js');
const ProjectClone = require('./views/ProjectClone.js');

// Providers
const libContractAdminProvisioningProvider = require('./providers/ContractAdminProvisioning-Provider.js');
const libCustomerProvisioningProvider = require('./providers/CustomerProvisioning-Provider.js');
const libProjectCloneProvider = require('./providers/ProjectClone-Provider.js');
const libMasterListRegistryProvider = require('./providers/MasterList-Registry-Provider.js');
const libCustomerRecordManifestsProvider = require('./providers/CustomerRecordManifests-Provider.js');
const libMasterListManager = require('./views/MasterListManager.js');
const libObservationTypeEditorProvider = require('./providers/ObservationType-Editor-Provider.js');
const libObservationTypeEditor = require('./views/ObservationTypeEditor.js');
const libArtifactTypeEditorProvider = require('./providers/ArtifactType-Editor-Provider.js');
const libArtifactTypeEditor = require('./views/ArtifactTypeEditor.js');
// ProjectWorkspace is now a config-driven pict-section-workspace (registered via workspace-configs); its provider
// subclass is required by Project.config.js.
const libProjectBulkDeleteProvider = require('./providers/ProjectBulkDelete-Provider.js');
const libProjectEditWorkspaceProvider = require('./providers/ProjectEditWorkspace-Provider.js');
const libProjectEditWorkspace = require('./views/ProjectEditWorkspace.js');
const libProjectCreateWorkspaceProvider = require('./providers/ProjectCreateWorkspace-Provider.js');
const libProjectCreateWorkspace = require('./views/ProjectCreateWorkspace.js');
// Approval-workflow visual editor (pict-section-flow graph over the 'ApprovalWorkflow' Configuration blob;
// replaces Home's editor). The provider is the data layer; the two views are the list + graph editor.
const libApprovalWorkflowProvider = require('./providers/ApprovalWorkflow-Provider.js');
const libApprovalWorkflowList = require('./views/ApprovalWorkflowList.js');
const libApprovalWorkflowEditor = require('./views/ApprovalWorkflowEditor.js');
const libApprovalWorkflowBindings = require('./views/ApprovalWorkflowBindings.js');
// Email Notifications (Email Rules) — migrated from Home's EmailRules.vue.
const libEmailNotificationsProvider = require('./providers/EmailNotifications-Provider.js');
const libEmailNotificationsList = require('./views/EmailNotifications-List.js');
const libEmailNotificationsEditor = require('./views/EmailNotifications-Editor.js');
const libConfigViewerProvider = require('./providers/ConfigViewer-Provider.js');
const libConfigViewer = require('./views/ConfigViewer.js');
const libDocumentationProvider = require('./providers/Documentation-Provider.js');
const libDocumentation = require('./views/Documentation.js');
const libEntitlementsProvider = require('./providers/Entitlements-Provider.js');
const libEntitlements = require('./views/Entitlements.js');
const libCertificationsProvider = require('./providers/Certifications-Provider.js');
const libCertifications = require('./views/Certifications.js');
const libATMDashboardConfigProvider = require('./providers/ATMDashboardConfig-Provider.js');
const libATMDashboardConfig = require('./views/ATMDashboardConfig.js');
const libAssetTypeDescriptorProvider = require('./providers/AssetTypeDescriptor-Provider.js');
const libAssetTypeDescriptor = require('./views/AssetTypeDescriptor.js');
const libSampleListColumnsConfigProvider = require('./providers/SampleListColumnsConfig-Provider.js');
const libSampleListColumnsConfig = require('./views/SampleListColumnsConfig.js');
const libSampleQRCodeConfigProvider = require('./providers/SampleQRCodeConfig-Provider.js');
const libSampleQRCodeConfig = require('./views/SampleQRCodeConfig.js');
const libPrinterCodes = require('./views/PrinterCodes.js');
const libScanSamplesPage = require('./views/ScanSamplesPage.js');
const libEntityDestinationsConfigProvider = require('./providers/EntityDestinationsConfig-Provider.js');
const libHeadlightDashboards = require('headlight-dashboards');
const libDashboardAccessConfigProvider = require('./providers/DashboardAccessConfig-Provider.js');
const libDashboardAccessConfig = require('./views/DashboardAccessConfig.js');
const libEntityDestinationsConfig = require('./views/EntityDestinationsConfig.js');
const libConfigScreens = require('./config-screens/Config-Screens.js');
const libCertificationTypesProvider = require('./providers/CertificationTypes-Provider.js');
const libCertificationTypes = require('./views/CertificationTypes.js');
const libAssetCatalogSyncProvider = require('./providers/AssetCatalogSync-Provider.js');
const libAssetCatalogSync = require('./views/AssetCatalogSync.js');
// AssetWorkspace is now a config-driven pict-section-workspace (registered via workspace-configs); its provider
// subclass is required by Asset.config.js. The AssetLocationMap ProjectCenterMap instance (below) still binds to it.
const libProjectContractTypesProvider = require('./providers/ProjectContractTypes-Provider.js');
const libProjectTypes = require('./views/ProjectTypes.js');
const libContractTypes = require('./views/ContractTypes.js');
const libRecordDatesProvider = require('./providers/RecordDates-Provider.js');
const libRecordDates = require('./views/RecordDates.js');
const libProjectStaffingProvider = require('./providers/ProjectStaffing-Provider.js');
const libSuperUserGrantProvider = require('./providers/SuperUserGrant-Provider.js');
const libSuperUserGrant = require('./views/SuperUserGrant.js');
// Internals (ported from the standalone web/internals app): Headlight-only service/application version screens.
const libInternalsServiceCatalog = require('./internals/Internals-ServiceCatalog.js');
const libInternalsServiceVersions = require('./internals/Internals-ServiceVersions.js');
const libInternalsServiceVersionsMultiEnv = require('./internals/Internals-ServiceVersionsMultiEnv.js');
const libManageCustomersProvider = require('./providers/ManageCustomers-Provider.js');
const libManageCustomers = require('./views/ManageCustomers.js');
const libCustomersListProvider = require('./providers/CustomersList-Provider.js');
const libCustomers = require('./views/Customers.js');
const libActivityObservabilityProvider = require('./providers/ActivityObservability-Provider.js');
const libUserActivity = require('./views/UserActivity.js');
const libUserLoginOverview = require('./views/UserLoginOverview.js');
const libProjectActivity = require('./views/ProjectActivity.js');
const libConfigCloneProvider = require('./providers/ConfigClone-Provider.js');
const libConfigClone = require('./views/ConfigClone.js');
const libRoleCloneProvider = require('./providers/RoleClone-Provider.js');
const libRoleClone = require('./views/RoleClone.js');
const libAccessWorkspaceProvider = require('./providers/AccessWorkspace-Provider.js');
const libAccessWorkspace = require('./views/AccessWorkspace.js');
const libPermissionMatrixProvider = require('./providers/PermissionMatrix-Provider.js');
const libPermissionVisualizerProvider = require('./providers/PermissionVisualizer-Provider.js');
const libEffectivePermissionsService = require('./services/Effective-Permissions-Service.js');
const libGroupCompositionProvider = require('./providers/GroupComposition-Provider.js');
const libUserGroupGrantsProvider = require('./providers/UserGroupGrants-Provider.js');
const libRoleAssignmentProvider = require('./providers/RoleAssignment-Provider.js');
const libDataMapper = require('./views/DataMapper.js');
const libDataImportProvider = require('./providers/DataImport-Provider.js');
const libDataImport = require('./views/DataImport.js');
const libContractPackageImportProvider = require('./providers/ContractPackageImport-Provider.js');
const libContractPackageImport = require('./views/ContractPackageImport.js');
const libProjectCenterMap = require('./views/ProjectCenterMap.js');
const libObservationLocationMap = require('./views/ObservationLocationMap.js');
// ContractWorkspace is now a config-driven pict-section-workspace (registered via workspace-configs); its
// provider subclass is required by Contract.config.js. LineItemBidItemMap (the project-scoped Bid Item Mapping
// tab body, embedded by the Project workspace's Bid Item Mapping tab) stays registered here.
const libLineItemBidItemMapProvider = require('./providers/LineItemBidItemMap-Provider.js');
const libProjectContractorMatrixProvider = require('./providers/ProjectContractorMatrix-Provider.js');
// UserWorkspace is now a config-driven pict-section-workspace (registered via workspace-configs); its provider
// subclass is required by User.config.js.
const libUserCreateWorkspaceProvider = require('./providers/UserCreateWorkspace-Provider.js');
const libUserCreateWorkspace = require('./views/UserCreateWorkspace.js');
const libContractCreateWorkspaceProvider = require('./providers/ContractCreateWorkspace-Provider.js');
const libContractCreateWorkspace = require('./views/ContractCreateWorkspace.js');
// Inspection CREATE. Creating an inspection means creating its asset join, its project scope and its document
// packet too — BridgeDocumentManager.createInspection() does all of that, and that service is already live
// app-wide (the HeadlightPanels view instantiates it). This screen is the form over that call.
const libInspectionCreateWorkspaceProvider = require('./providers/InspectionCreateWorkspace-Provider.js');
const libInspectionCreateWorkspace = require('./views/InspectionCreateWorkspace.js');
const libErrorLogViewerProvider = require('./providers/ErrorLogViewer-Provider.js');
const libErrorLogViewer = require('./views/ErrorLogViewer.js');
const libMyHeadlightProvider = require('./providers/MyHeadlight-Provider.js');
const libMyHeadlight = require('./views/MyHeadlight.js');
const libReportProjectAssignment = require('headlight-report-project-assignment');
const libContractProjectTypesImportProvider = require('./providers/ContractProjectTypesImportProvider.js');
const libContractProjectTypesSettingsAdapter = require('./providers/ContractProjectTypesSettingsAdapter.js');
const libRecordPhotoUploadAfterSave = require('./providers/RecordPhotoUploadAfterSave.js');
const libProjectTypeComprehensionBuilder = require('./providers/ProjectTypeComprehensionBuilder.js');
const libTypeComprehensionSaveProvider = require('./providers/TypeComprehensionSaveProvider.js');
const libTypeComprehensionReadProvider = require('./providers/TypeComprehensionReadProvider.js');

// Default Manifests — List views
const manifestUserList = require('./default_manifests/User-List.json');
const manifestCustomerList = require('./default_manifests/Customer-List.json');
const manifestOrganizationList = require('./default_manifests/Organization-List.json');
const manifestProjectList = require('./default_manifests/Project-List.json');
const manifestContractList = require('./default_manifests/Contract-List.json');
const manifestRoleList = require('./default_manifests/Role-List.json');
const manifestObservationManifestList = require('./default_manifests/ObservationManifest-List.json');
const manifestArtifactTypeList = require('./default_manifests/ArtifactType-List.json');
const manifestReportNamedInstanceList = require('./default_manifests/ReportNamedInstance-List.json');
const manifestObservationTagList = require('./default_manifests/ObservationTag-List.json');
const manifestUserRoleJoinList = require('./default_manifests/UserRoleJoin-List.json');
const manifestRoleEdgeList = require('./default_manifests/RoleEdge-List.json');
const manifestRolePermissionList = require('./default_manifests/RolePermission-List.json');
const manifestProjectTagTypeList = require('./default_manifests/ProjectTagType-List.json');
const manifestDocumentApprovalWorkflowList = require('./default_manifests/DocumentApprovalWorkflow-List.json');
const manifestDocumentApprovalStateList = require('./default_manifests/DocumentApprovalState-List.json');
const manifestDocumentApprovalStepList = require('./default_manifests/DocumentApprovalStep-List.json');
const manifestObservationApprovalWorkflowList = require('./default_manifests/ObservationApprovalWorkflow-List.json');
const manifestObservationApprovalStateList = require('./default_manifests/ObservationApprovalState-List.json');
const manifestObservationApprovalStepList = require('./default_manifests/ObservationApprovalStep-List.json');
const manifestContractTypeList = require('./default_manifests/ContractType-List.json');
const manifestProjectTypeList = require('./default_manifests/ProjectType-List.json');

// Default Manifests — View (read) manifests
const manifestUserView = require('./default_manifests/User-View.json');
const manifestCustomerView = require('./default_manifests/Customer-View.json');
const manifestOrganizationView = require('./default_manifests/Organization-View.json');
const manifestProjectView = require('./default_manifests/Project-View.json');
const manifestRoleView = require('./default_manifests/Role-View.json');
const manifestObservationManifestView = require('./default_manifests/ObservationManifest-View.json');
const manifestArtifactTypeView = require('./default_manifests/ArtifactType-View.json');
const manifestReportNamedInstanceView = require('./default_manifests/ReportNamedInstance-View.json');
const manifestObservationTagView = require('./default_manifests/ObservationTag-View.json');
const manifestUserRoleJoinView = require('./default_manifests/UserRoleJoin-View.json');
const manifestRoleEdgeView = require('./default_manifests/RoleEdge-View.json');
const manifestRolePermissionView = require('./default_manifests/RolePermission-View.json');
const manifestProjectTagTypeView = require('./default_manifests/ProjectTagType-View.json');
const manifestDocumentApprovalWorkflowView = require('./default_manifests/DocumentApprovalWorkflow-View.json');
const manifestDocumentApprovalStateView = require('./default_manifests/DocumentApprovalState-View.json');
const manifestDocumentApprovalStepView = require('./default_manifests/DocumentApprovalStep-View.json');
const manifestObservationApprovalWorkflowView = require('./default_manifests/ObservationApprovalWorkflow-View.json');
const manifestObservationApprovalStateView = require('./default_manifests/ObservationApprovalState-View.json');
const manifestObservationApprovalStepView = require('./default_manifests/ObservationApprovalStep-View.json');
const manifestContractTypeView = require('./default_manifests/ContractType-View.json');
const manifestProjectTypeView = require('./default_manifests/ProjectType-View.json');

// Default Manifests — Edit manifests
const manifestUserEdit = require('./default_manifests/User-Edit.json');
const manifestCustomerEdit = require('./default_manifests/Customer-Edit.json');
const manifestOrganizationEdit = require('./default_manifests/Organization-Edit.json');
const manifestOrganizationCreate = require('./default_manifests/Organization-Create.json');
// Generated View/Edit/Create manifests for the catalog master-list entities (see build-catalog-manifests.js) —
// registered into Manifests + wired onto their recordset configs below so their forms are sectioned/curated
// instead of raw-schema. Keyed for that wiring.
const libCatalogManifests = require('./default_manifests/catalog-manifest-index.js');
const CATALOG_MANIFEST_ENTITIES = new Map(libCatalogManifests.ENTITIES.map((pEntry) => [ pEntry.Entity, pEntry ]));
const manifestProjectEdit = require('./default_manifests/Project-Edit.json');
const manifestRoleEdit = require('./default_manifests/Role-Edit.json');
const manifestObservationManifestEdit = require('./default_manifests/ObservationManifest-Edit.json');
const manifestArtifactTypeEdit = require('./default_manifests/ArtifactType-Edit.json');
const manifestReportNamedInstanceEdit = require('./default_manifests/ReportNamedInstance-Edit.json');
// Cross-app reuse: the forms app's RNI metadata editor (Name/Hash/archetype/flags + the Options JSON
// pict-section-code editor, gated by the ReportNamedInstance-EditJSON entitlement). Registered below and
// wired as the ReportNamedInstance record set's custom View/Edit/Create screen via RecordSetCustomViews.
const libReportNamedInstanceMetadataEditor = require('../../headlight-form-editor/source/views/ReportNamedInstance-Create.js');
const manifestObservationTagEdit = require('./default_manifests/ObservationTag-Edit.json');
const manifestUserRoleJoinEdit = require('./default_manifests/UserRoleJoin-Edit.json');
const manifestRoleEdgeEdit = require('./default_manifests/RoleEdge-Edit.json');
const manifestRolePermissionEdit = require('./default_manifests/RolePermission-Edit.json');
const manifestProjectTagTypeEdit = require('./default_manifests/ProjectTagType-Edit.json');
const manifestDocumentApprovalWorkflowEdit = require('./default_manifests/DocumentApprovalWorkflow-Edit.json');
const manifestDocumentApprovalStateEdit = require('./default_manifests/DocumentApprovalState-Edit.json');
const manifestDocumentApprovalStepEdit = require('./default_manifests/DocumentApprovalStep-Edit.json');
const manifestObservationApprovalWorkflowEdit = require('./default_manifests/ObservationApprovalWorkflow-Edit.json');
const manifestObservationApprovalStateEdit = require('./default_manifests/ObservationApprovalState-Edit.json');
const manifestObservationApprovalStepEdit = require('./default_manifests/ObservationApprovalStep-Edit.json');
const manifestContractTypeEdit = require('./default_manifests/ContractType-Edit.json');
const manifestProjectTypeEdit = require('./default_manifests/ProjectType-Edit.json');

// Dashboard views and providers
// Shared dashboard chart/data provider (used by the Activity Observability views). The Analytics dashboard
// views that also used it were retired.
const PlatformDashboardDataProvider = require('./views/dashboards/PlatformDashboard-DataProvider.js');

/** @typedef {import('fable')} Fable */

const customLoginPageOptions = {
	title: 'HeadLight Platform Configuration',
	titleStyles: `
		z-index: 1;
		text-shadow:  5px 9px 2px var(--bulma-black);
		word-wrap: break-word;
		overflow-wrap: break-word;
		text-align: center;
		font-size: 4rem !important;
		margin: 0 0 5vh 0;`,
	wallpaperStyles: `
		background-image: url('../../images/headlight-config-bg.jpg');
		background-size: cover;
		background-repeat: no-repeat;
		background-color: var(--bulma-grey);
		background-blend-mode: multiply;`,
	closePanelsOnLogout: true,
};

class PlatformConfiguration extends libHeadlightConfigurableApplication
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		// A keyboard scanner opens Sample Details here (the panels' scan handler); form apps leave it off.
		this.pict.providers.HeadlightQR.enableWedge();
		/** @type {any} */
		this.fable;
		/** @type {any} */
		this.options;

		// Expose the pict instance as the conventional `window.pict` global. This headlight
		// bootstrap only sets `window._Pict`, but pict-section-modal resolves its panel
		// chrome icons (the resize/collapse-tab chevrons) via `window.pict.icon(...)`, so
		// without this the help panel's collapse tab renders without its chevron glyph.
		if (typeof window !== 'undefined' && !window.pict)
		{
			window.pict = this.pict;
		}

		// Font Awesome passthrough for pict.icon(). The navigation graph, the topic jumbotrons, and the
		// ConfigurationNavigation topic buttons author icons as Font Awesome classes (e.g. 'fas fa-city'),
		// but pict's Icon provider is an SVG registry keyed by PascalCase names — so an FA class misses and
		// every surface that renders through pict.icon() (the ⌘K launcher Palette, the Hub category pages,
		// the Sidebar, the List) shows the registry's "?" fallback. Font Awesome is loaded by html/index.html,
		// so emit an <i class="fa…"> for FA-class names and defer to the original SVG lookup for the provider's
		// own PascalCase names (Search, Menu, ChevronDown, …).
		const tmpIconProvider = this.pict.providers.Icon;
		if (tmpIconProvider && (typeof tmpIconProvider.get === 'function') && !tmpIconProvider._FontAwesomePassthrough)
		{
			tmpIconProvider._FontAwesomePassthrough = true;
			const tmpRegistryGet = tmpIconProvider.get.bind(tmpIconProvider);
			tmpIconProvider.get = (pName, pOpts) =>
			{
				if ((typeof pName === 'string') && (pName.indexOf('fa-') > -1))
				{
					const tmpOpts = pOpts || {};
					let tmpClass = 'pict-icon';
					if ((typeof tmpOpts.class === 'string') && (tmpOpts.class.length > 0)) { tmpClass += ' ' + tmpOpts.class; }
					let tmpStyle = '';
					if ((typeof tmpOpts.size === 'number') && (tmpOpts.size > 0)) { tmpStyle = ` style=\"font-size:${tmpOpts.size}px\"`; }
					else if ((typeof tmpOpts.size === 'string') && (tmpOpts.size.length > 0)) { tmpStyle = ` style=\"font-size:${tmpOpts.size}\"`; }
					let tmpAria = ' aria-hidden=\"true\"';
					if ((typeof tmpOpts.ariaLabel === 'string') && (tmpOpts.ariaLabel.length > 0)) { tmpAria = ` role=\"img\" aria-label=\"${tmpOpts.ariaLabel.replace(/\"/g, '&quot;')}\"`; }
					return `<span class=\"${tmpClass}\"${tmpStyle}${tmpAria}><i class=\"${pName.replace(/\"/g, '&quot;')}\"></i></span>`;
				}
				return tmpRegistryGet(pName, pOpts);
			};
		}

		// Prevent the router from resolving the current URL as each route is
		// registered. The router would otherwise resolve '/' (the first route
		// added) and render the default Dashboard view before the route the user
		// deep-linked to has been registered. A single explicit resolve happens
		// in onAfterLoginAsync once every route — including the PSRS routes — is
		// registered. (Also set on the PictRouter provider config in the router.)
		this.pict.settings.RouterSkipRouteResolveOnAdd = true;

		// Template preprocessor — register + instantiate first so its render-speedup wrappers install
		// automatically before the form/recordset services build their templates (mirrors the LIMS app).
		this.pict.addServiceType('PictTemplatePreprocessor', libPictTemplatePreprocessor);
		this.pict.instantiateServiceProvider('PictTemplatePreprocessor');

		// Add the pict form and recordset services
		this.pict.addServiceType('PictSectionForm', libPictSectionForm);
		this.pict.addServiceType('PictSectionRecordSet', libPictSectionRecordSet);

		// Document render service — renders a Document on the server and opens the same-origin HTML form viewer/editor
		// (Report/{GUID}/index.html — editable when the document isn't Locked). Used by the Documents list. Never Fieldbook.
		this.pict.addServiceType('HeadlightDocumentRender', require('../../../lib/service/web-headlight-document-render/Web-Headlight-Document-Render.js'));
		this.pict.instantiateServiceProviderIfNotExists('HeadlightDocumentRender');

		// Observation render service — schema-driven observation → HTML in many formats (full / card / strip / tooltip /
		// thumbnail / marker), intelligent about built-in AND custom schemas. The one place observations become HTML;
		// consumed by the Observation workspace, the Explorer map/list, the dashboards' geo popups, and card decks.
		this.pict.addServiceType('HeadlightObservationRender', require('../../../lib/service/web-headlight-observation-render/Web-Headlight-Observation-Render.js'));
		this.pict.instantiateServiceProviderIfNotExists('HeadlightObservationRender');

		this.dynamicInputsBundle = new HeadlightDynamicInputsBundle(this.pict);
		this.dynamicInputsBundle.attachProviders();
		this.dynamicInputsBundle.attachInstructions();
		// The Leaflet view the GeolocationSelectorWithMap input renders its map with (e.g. the Location map picker).
		// Registered with its own configuration, which carries the 'GeolocationSelector' renderable the input
		// renders; it renders only when a map field mounts, never at load. A field with no coordinates yet opens on
		// the continental US (the input's own no-fix default) rather than Leaflet's default of Honolulu.
		const libGeolocationSelectorView = HeadlightDynamicInputsBundle.getViewMap().GeolocationSelector;
		this.pict.addView('GeolocationSelector', Object.assign({}, libGeolocationSelectorView.default_configuration,
			{ RenderOnLoad: false, LeafletInitialCenter: [ 39.5, -98.35 ], LeafletInitialZoom: 4 }), libGeolocationSelectorView);

		// PSRS filter data/persistence management (required by ComplexFilterProvider)
		this.pict.addProvider('FilterDataProvider', libFilterDataProvider.default_configuration, libFilterDataProvider);
		this.pict.addView('FilterPersistenceView', libFilterPersistenceView.default_configuration, libFilterPersistenceView);

		// PSRS dynamic recordset solver (required for manifest-based list/dashboard rendering)
		this.pict.addProviderSingleton('DynamicRecordsetSolver', libDynamicRecordsetSolver.default_configuration, libDynamicRecordsetSolver);

		// Add shared PSRS styling providers (Bulma-styled templates for tables, pagination, filters)
		this.pict.addProvider('ComplexFilterProvider',
			{
				endCapTemplate: '',
			},
			require('../../providers/ComplexFilterProvider.js'));

		this.pict.addProvider('ImprovedTabularRecordSetProvider',
			{
				// Optional extra action slot in the list title bar (sits after the title, before the record
				// count). The built-in "New" button is rendered by ImprovedTabularRecordSetProvider itself.
				endCapTemplate: '',
				includePrint: false,
				overrideURLTemplate: `#/PSRS/{~D:Record.Payload.Payload.RecordSet~}/View/{~DVBK:Record.Payload.Data:Record.Payload.Payload.GUIDAddress~}`,
			},
			require('../../providers/ImprovedTabularRecordSetProvider.js'));

		this.fable.serviceManager.addServiceType('HeadlightRestClient', require('headlight-rest-client'));
		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });

		// Reads through this.fable.HeadlightRestClient, so it is registered after it. The Tests tab and the TestInstance
		// popover share its per-sample scopes, so they show the same result for a test.
		this.fable.serviceManager.addServiceType('HeadlightSamples', libHeadlightSamples);
		this.fable.serviceManager.instantiateServiceProvider('HeadlightSamples', {});
		const tmpQuickSummaries = this.pict.services && this.pict.services.HeadlightQuickSummaries;
		if (tmpQuickSummaries && (typeof tmpQuickSummaries.addRecordDecorator === 'function'))
		{
			tmpQuickSummaries.addRecordDecorator('TestInstance', (pSummaryRecord, pRecord) => this.fable.HeadlightSamples.decorateTestInstanceSummary(pSummaryRecord, pRecord));
		}

		// Project cloning service (used by the ProjectClone workflow view). It is a
		// fable service (extends fable-serviceproviderbase) that reads
		// this.fable.HeadlightRestClient, so it is registered after the rest client
		// is instantiated above. Not an npm package — required by path from lib/.
		this.fable.serviceManager.addServiceType('ProjectCloningAdapter', require('../../../lib/service/headlight-project-cloning-adapter/Headlight-Project-Cloning-Adapter.js'));
		this.fable.serviceManager.instantiateServiceProvider('ProjectCloningAdapter', {});

		// The canonical type-save (build comprehension -> push -> pointer -> prune -> statuses).
		// Registered as a fable service so both the editor's PSRS wrap and the spreadsheet
		// ImportProvider resolve it via this.fable.TypeComprehensionSaveProvider.
		this.fable.serviceManager.addServiceType('TypeComprehensionSaveProvider', libTypeComprehensionSaveProvider);
		this.fable.serviceManager.instantiateServiceProvider('TypeComprehensionSaveProvider', {});

		// The read-side counterpart: shared hydration of statuses / default dates / allowed
		// project types / prototype for a type. The bespoke Project/Contract Type editors
		// resolve it via this.fable.TypeComprehensionReadProvider.
		this.fable.serviceManager.addServiceType('TypeComprehensionReadProvider', libTypeComprehensionReadProvider);
		this.fable.serviceManager.instantiateServiceProvider('TypeComprehensionReadProvider', {});

		// Effective-permissions aggregation — the swap seam for the Access workspace's Effective visualization
		// (and, later, the UI permission-expression layer). Composes the Permissions/* endpoints client-side
		// today; swappable for a server aggregation endpoint later without touching any view. Reads the rest
		// client, so registered after it is instantiated. Reached via this.fable.EffectivePermissionsService.
		this.fable.serviceManager.addServiceType('EffectivePermissionsService', libEffectivePermissionsService);
		this.fable.serviceManager.instantiateServiceProvider('EffectivePermissionsService', {});

		this.AppData.Interactive = true;

		// Application views
		this.pict.addView('Empty', configEmptyView);
		this.pict.addView('ConfigurationIndex', ConfigurationIndex.default_configuration, ConfigurationIndex);
		this.pict.addView('ConfigurationNavigation', ConfigurationNavigation.default_configuration, ConfigurationNavigation);
		this.pict.addView('TopicPage', TopicPage.default_configuration, TopicPage);
		this.pict.addView('CustomerProvisioning', CustomerProvisioning.default_configuration, CustomerProvisioning);
		this.pict.addView('ProjectClone', ProjectClone.default_configuration, ProjectClone);

		this.pict.addProvider('ContractAdminProvisioning', libContractAdminProvisioningProvider.default_configuration, libContractAdminProvisioningProvider);
		this.pict.addProvider('CustomerProvisioningProvider', libCustomerProvisioningProvider.default_configuration, libCustomerProvisioningProvider);
		this.pict.addProvider('ProjectCloneProvider', libProjectCloneProvider.default_configuration, libProjectCloneProvider);
		// Master List configuration tool: the registry provider (data + ConfigFragment persistence
		// + live apply) and its product-manager-facing editor screen.
		this.pict.addProvider('MasterListRegistry', libMasterListRegistryProvider.default_configuration, libMasterListRegistryProvider);
		this.pict.addView('MasterListManager', libMasterListManager.default_configuration, libMasterListManager);
		this.pict.addProvider('ObservationTypeEditor', libObservationTypeEditorProvider.default_configuration, libObservationTypeEditorProvider);
		this.pict.addView('ObservationTypeEditor', libObservationTypeEditor.default_configuration, libObservationTypeEditor);
		this.pict.addProvider('ArtifactTypeEditor', libArtifactTypeEditorProvider.default_configuration, libArtifactTypeEditorProvider);
		this.pict.addView('ArtifactTypeEditor', libArtifactTypeEditor.default_configuration, libArtifactTypeEditor);
		// Reusable project-scoped bulk-delete tool for the workspace's long lists (line/bid items, equipment,
		// personnel, labels). Gated by the 'Project-BulkDelete' entitlement (access/Task-Catalog.js).
		this.pict.addProvider('ProjectBulkDelete', libProjectBulkDeleteProvider.default_configuration, libProjectBulkDeleteProvider);
		this.pict.addView('ProjectCenterMap', libProjectCenterMap.default_configuration, libProjectCenterMap);
		// A second instance of the same Leaflet picker for the bespoke Project CREATE screen — bound to the
		// ProjectCreateWorkspace provider's working center and its own #PCW-MapHost (see ProjectCenterMap's
		// configurable ProviderName / MapHostID).
		this.pict.addView('ProjectCreateMap', Object.assign({}, libProjectCenterMap.default_configuration,
			{ ViewIdentifier: 'ProjectCreateMap', ProviderName: 'ProjectCreateWorkspace', MapHostID: 'PCW-MapHost' }), libProjectCenterMap);
		// Bespoke Project EDIT workspace: a hand-built form that locks the Financial & Schedule fields once the
		// project is Active (the manifest recordset form can't express per-record read-only). Router bridges
		// #/PSRS/Project/Edit/<GUID> to it.
		this.pict.addProvider('ProjectEditWorkspace', libProjectEditWorkspaceProvider.default_configuration, libProjectEditWorkspaceProvider);
		this.pict.addView('ProjectEditWorkspace', libProjectEditWorkspace.default_configuration, libProjectEditWorkspace);
		// Bespoke Project CREATE workspace: the curated create form that "+ New" opens (via the router bridge
		// #/PSRS/Project/Create) instead of the plain manifest form. Starts blank, POSTs a Project, then lands
		// on the bespoke read workspace for the new record.
		this.pict.addProvider('ProjectCreateWorkspace', libProjectCreateWorkspaceProvider.default_configuration, libProjectCreateWorkspaceProvider);
		this.pict.addView('ProjectCreateWorkspace', libProjectCreateWorkspace.default_configuration, libProjectCreateWorkspace);
		// Approval-workflow editor: the pict-section-flow graph editor over the 'ApprovalWorkflow' Config
		// blob (list + read-only graph now; editing in a later phase). The flow view self-registers all its
		// internal services; its Form properties panels (P2) resolve a 'PictFormMetacontroller' fable service
		// type, so register that from pict-section-form here (idempotent; pict-section-form is already a dep).
		if (!this.pict.fable.servicesMap.hasOwnProperty('PictFormMetacontroller'))
		{
			this.pict.addServiceType('PictFormMetacontroller', libPictSectionForm.PictFormMetacontroller);
		}
		this.pict.addProvider('ApprovalWorkflow', libApprovalWorkflowProvider.default_configuration, libApprovalWorkflowProvider);
		this.pict.addView('ApprovalWorkflowList', libApprovalWorkflowList.default_configuration, libApprovalWorkflowList);
		this.pict.addView('ApprovalWorkflowEditor', libApprovalWorkflowEditor.default_configuration, libApprovalWorkflowEditor);
		this.pict.addView('ApprovalWorkflowBindings', libApprovalWorkflowBindings.default_configuration, libApprovalWorkflowBindings);
		// Email Notifications (Email Rules) editor — migrated from Home's EmailRules.vue. Meadow EmailRule
		// entities + per-handler Configuration blobs; list now, editor in a later phase.
		this.pict.addProvider('EmailNotifications', libEmailNotificationsProvider.default_configuration, libEmailNotificationsProvider);
		this.pict.addView('EmailNotificationsList', libEmailNotificationsList.default_configuration, libEmailNotificationsList);
		this.pict.addView('EmailNotificationsEditor', libEmailNotificationsEditor.default_configuration, libEmailNotificationsEditor);
		// Bid Item Mapping embed (hosted by the config-driven ContractWorkspace's "Bid Item Mapping" tab).
		this.pict.addProvider('LineItemBidItemMap', libLineItemBidItemMapProvider.default_configuration, libLineItemBidItemMapProvider);
		// Project-level contractor matrix: ONE generic grid provider (ProjectContractorMatrix-Provider) registered
		// as TWO parameterized instances — one for line items, one for bid items — each embedded into a host div on
		// the bespoke Project workspace (openInto('Project', id, mount), like ProjectStaffing). Manages the M:N
		// LineItemOrganizationJoin / BidItemOrganizationJoin (contractors ↔ items) with a per-chip DBE toggle.
		this.pict.addProvider('ProjectContractorMatrixLine', Object.assign({}, libProjectContractorMatrixProvider.default_configuration,
			{
				ProviderIdentifier: 'ProjectContractorMatrixLine', AutoInitializeOrdinal: 223,
				ItemEntity: 'LineItem', ItemsSet: 'LineItems', ItemIDField: 'IDLineItem', ItemNumberField: 'ItemNumber', ItemNameField: 'Name', SortField: 'ItemNumber',
				JoinEntity: 'LineItemOrganizationJoin', JoinsSet: 'LineItemOrganizationJoins', JoinPKField: 'IDLineItemOrganizationJoin',
				MountSelector: '#PW-LineContractorHost', ItemNoun: 'Line item',
			}), libProjectContractorMatrixProvider);
		this.pict.addProvider('ProjectContractorMatrixBid', Object.assign({}, libProjectContractorMatrixProvider.default_configuration,
			{
				ProviderIdentifier: 'ProjectContractorMatrixBid', AutoInitializeOrdinal: 224,
				ItemEntity: 'BidItem', ItemsSet: 'BidItems', ItemIDField: 'IDBidItem', ItemNumberField: '', ItemNameField: 'Name', SortField: 'Name',
				JoinEntity: 'BidItemOrganizationJoin', JoinsSet: 'BidItemOrganizationJoins', JoinPKField: 'IDBidItemOrganizationJoin',
				MountSelector: '#PW-BidContractorHost', ItemNoun: 'Bid item',
			}), libProjectContractorMatrixProvider);
			// Equipment ↔ contractor: the same matrix, but its rows are DERIVED from ProjectEquipmentJoin (Equipment is
			// a shared master list, not project-owned), the join is a true ternary (stamp IDProject on write, filter the
			// load by it), there is no per-link DBE column, and a "+ Add equipment" control adds equipment to the project.
			this.pict.addProvider('ProjectContractorMatrixEquipment', Object.assign({}, libProjectContractorMatrixProvider.default_configuration,
				{
					ProviderIdentifier: 'ProjectContractorMatrixEquipment', AutoInitializeOrdinal: 225,
					ItemEntity: 'Equipment', GateEntity: 'Project', ItemsSet: 'Equipments', AddItemsSet: 'Equipments',
					ItemIDField: 'IDEquipment', ItemGUIDField: 'GUIDEquipment', ItemNumberField: '', ItemNameField: 'Name', SortField: 'Name',
					JoinEntity: 'ProjectEquipmentJoin', JoinsSet: 'ProjectEquipmentJoins', JoinPKField: 'IDProjectEquipmentJoin',
					JoinProjectField: 'IDProject', ItemsViaJoin: true, SupportsDBE: false,
					MountSelector: '#PW-EquipmentContractorHost', ItemNoun: 'Equipment',
				}), libProjectContractorMatrixProvider);
		// Bespoke User CREATE workspace: the curated create form that "+ New" opens (via the router bridge
		// #/PSRS/User/Create) instead of the plain manifest form. POSTs a User, then sets the login password
		// (POST /1.0/Password), then lands on the bespoke read workspace for the new record.
		this.pict.addProvider('UserCreateWorkspace', libUserCreateWorkspaceProvider.default_configuration, libUserCreateWorkspaceProvider);
		this.pict.addView('UserCreateWorkspace', libUserCreateWorkspace.default_configuration, libUserCreateWorkspace);
		// Bespoke Contract CREATE workspace: the curated create form that "+ New" opens (via the router bridge
		// #/PSRS/Contract/Create) instead of the plain manifest form. POSTs a Contract, then lands on the
		// bespoke read workspace for the new record.
		this.pict.addProvider('ContractCreateWorkspace', libContractCreateWorkspaceProvider.default_configuration, libContractCreateWorkspaceProvider);
		this.pict.addView('ContractCreateWorkspace', libContractCreateWorkspace.default_configuration, libContractCreateWorkspace);

		this.pict.addProvider('InspectionCreateWorkspace', libInspectionCreateWorkspaceProvider.default_configuration, libInspectionCreateWorkspaceProvider);
		this.pict.addView('InspectionCreateWorkspace', libInspectionCreateWorkspace.default_configuration, libInspectionCreateWorkspace);
		// Error Log Viewer: read-only diagnostics page (recent HLWebErrorLog artifacts — per-day chart + table).
		this.pict.addProvider('ErrorLogViewer', libErrorLogViewerProvider.default_configuration, libErrorLogViewerProvider);
		this.pict.addView('ErrorLogViewer', libErrorLogViewer.default_configuration, libErrorLogViewer);
		// "My Headlight": the signed-in organization's own config landing — starting with the customer logo.
		this.pict.addProvider('MyHeadlight', libMyHeadlightProvider.default_configuration, libMyHeadlightProvider);
		this.pict.addView('MyHeadlight', libMyHeadlight.default_configuration, libMyHeadlight);
		// Shared Report->Project assignment view (headlight-report-project-assignment), rendered into this app's
		// container. No FormGroups injected here, so the materials-only bulk-add modal stays hidden.
		this.pict.addView('ReportNamedInstanceProjectForms', Object.assign({}, libReportProjectAssignment.default_configuration,
			{ DefaultDestinationAddress: '#PlatformConfiguration-Application-Container' }), libReportProjectAssignment);
		// pict-section-picker: the searchable entity picker the recordset Association editor uses.
		this.pict.addProvider('Pict-Section-Picker', libPictSectionPicker.default_configuration, libPictSectionPicker);
		// pict-section-accordion: multi-step container (accordion / wizard / stepper); drives the Support merge wizard.
		this.pict.addProvider('Pict-Section-Accordion', libPictSectionAccordion.default_configuration, libPictSectionAccordion);

		// pict-dataexplorer: the operational-database "folders" explorer (Database -> Data Explorer). The
		// provider owns createExplorer; the DataExplorer host view builds + renders the tree on demand and
		// reuses the post-login RecordSetCardManager cards for the record ⓘ popouts.
		this.pict.addProvider('Pict-DataExplorer', libPictDataExplorer.default_configuration, libPictDataExplorer);
		this.pict.addView('DataExplorer', DataExplorer.default_configuration, DataExplorer);

		// Sample Plan editor: a project's material sampling/testing plan as a master/detail navigator built on
		// pict-dataexplorer (Section/Line-Item → Material → Rule, satisfaction rollups, layered AcademyContent docs).
		this.pict.addProvider('SamplePlan', SamplePlanProvider.default_configuration, SamplePlanProvider);
		this.pict.addView('SamplePlan', SamplePlan.default_configuration, SamplePlan);
		// Net-new SamplePlan dashboard (a shareable provider + a thin standalone view; embed elsewhere via openInto).
		this.pict.addProvider('SamplePlanDashboard', SamplePlanDashboardProvider.default_configuration, SamplePlanDashboardProvider);
		this.pict.addView('SamplePlanDashboard', SamplePlanDashboard.default_configuration, SamplePlanDashboard);
		// Sample Plan Copy: on a route with no menu entry, on purpose.
		this.pict.addServiceType('SamplePlanService', require('headlight-lims-sampleplan'));
		this.pict.addView('SamplePlanCopy', SamplePlanCopy.default_configuration, SamplePlanCopy);
		this.pict.addProvider('ObservationList', ObservationListProvider.default_configuration, ObservationListProvider);
		this.pict.addView('ObservationList', ObservationListView.default_configuration, ObservationListView);
		this.pict.addProvider('DocumentList', DocumentListProvider.default_configuration, DocumentListProvider);
		this.pict.addView('DocumentList', DocumentListView.default_configuration, DocumentListView);
		// A record's documents opened side by side with a quick-switch rail, each in its own frame running the longhand form page.
		this.pict.addProvider('DocumentFrameSwitcher', DocumentFrameSwitcherProvider.default_configuration, DocumentFrameSwitcherProvider);
		this.pict.addProvider('InspectionSchedule', InspectionScheduleProvider.default_configuration, InspectionScheduleProvider);
		this.pict.addView('InspectionSchedule', InspectionScheduleView.default_configuration, InspectionScheduleView);
		// Customer Support console. The provider owns the triage list / create-edit / approval-action surface; the
		// SupportCallFormHost (below) renders the customer's configured Support-Call manifest into #Support-FormHost.
		this.pict.addProvider('Support', SupportProvider.default_configuration, SupportProvider);
		this.pict.addView('Support', SupportView.default_configuration, SupportView);
		// The merge-tickets wizard (pict-section-accordion in wizard mode): folds duplicate/misfiled tickets into a parent.
		this.pict.addProvider('SupportMerge', SupportMergeProvider.default_configuration, SupportMergeProvider);
		// Read-only customer-facing open-cases portal (#/SupportPortal) for the Support-View tier — curated fields + the
		// caller-facing acknowledgement/resolution updates, no triage or internal notes.
		this.pict.addProvider('SupportPortal', SupportPortalProvider.default_configuration, SupportPortalProvider);
		this.pict.addView('SupportPortal', SupportPortalView.default_configuration, SupportPortalView);
		// Form Concierge: files + one markdown request → plansheet form sessions (create / update / build / examples).
		this.pict.addProvider('FormConcierge', FormConciergeProvider.default_configuration, FormConciergeProvider);
		this.pict.addView('FormConcierge', FormConciergeView.default_configuration, FormConciergeView);
		// The support-call dynamic-form runner (pict-section-form-host): the Support provider drives loadForm on demand
		// with the fetched Support-Call manifest, marshaling to AppData.DocumentData.ReportData.FormData so the
		// ObservationImageSet attachment field and the production document save agree on one FormData object.
		this.pict.addProvider('SupportCallFormHost',
			{
				ProviderIdentifier: 'SupportCallFormHost',
				DestinationType: 'div',
				DestinationAddress: '#Support-FormHost',
				DataAddress: 'AppData.DocumentData.ReportData.FormData',
				CreateDestinationElement: false,
				AutoRenderOnLoad: true,
			}, libPictSectionFormHost);
		this.pict.addProvider('DocumentThroughput', DocumentThroughputProvider.default_configuration, DocumentThroughputProvider);
		this.pict.addView('DocumentThroughput', DocumentThroughputView.default_configuration, DocumentThroughputView);
		this.pict.addProvider('InspectionScope', libInspectionScopeService.default_configuration, libInspectionScopeService);
		this.pict.addProvider('AssetDescriptor', libAssetDescriptorProvider.default_configuration, libAssetDescriptorProvider);
		this.pict.addProvider('PhysicalAssetDashboard', PhysicalAssetDashboardProvider.default_configuration, PhysicalAssetDashboardProvider);
		this.pict.addView('PhysicalAssetDashboard', PhysicalAssetDashboardView.default_configuration, PhysicalAssetDashboardView);
		// Important Project Files: a standalone project-scoped folder/file manager (Phase 1 = read-only navigator).
		this.pict.addProvider('ImportantFiles', libImportantFilesProvider.default_configuration, libImportantFilesProvider);
		this.pict.addView('ImportantFiles', libImportantFiles.default_configuration, libImportantFiles);
		this.pict.addProvider('BulkUpload', libBulkUploadProvider.default_configuration, libBulkUploadProvider);
		this.pict.addView('BulkUpload', libBulkUpload.default_configuration, libBulkUpload);
		this.pict.addProvider('ProjectFunding', libProjectFundingProvider.default_configuration, libProjectFundingProvider);
		this.pict.addView('ProjectFunding', libProjectFunding.default_configuration, libProjectFunding);
		// Contract ▸ Change Orders (ProjectWorkspace tab): list/browse/view the project's ChangeOrder headers +
		// their LineItemChangeOrder lines. Phase 1 (read); create/edit via pict-section-form-host in Phase 2.
		this.pict.addProvider('ChangeOrders', libChangeOrdersProvider.default_configuration, libChangeOrdersProvider);
		this.pict.addView('ChangeOrders', libChangeOrders.default_configuration, libChangeOrders);
		// The change-order dynamic-form runner (pict-section-form-host): renders the Default-ChangeOrder
		// HeadlightDynamicForm manifest into #CO-FormHost. Registered empty (no Form) so the ChangeOrders provider
		// drives loadForm/getFormData/save on demand for create/edit.
		this.pict.addProvider('ChangeOrderFormHost',
			{
				ProviderIdentifier: 'ChangeOrderFormHost',
				DestinationType: 'div',
				DestinationAddress: '#CO-FormHost',
				DataAddress: 'AppData.ChangeOrderForm',
				CreateDestinationElement: false,
				AutoRenderOnLoad: true,
			}, libPictSectionFormHost);

		// Reusable entity custom-metadata form (see EntityMetadataForm-Provider): the provider drives loadForm into
		// the caller's host bound to the record's CustomProperties address; AutoRenderOnLoad is OFF so the provider
		// sets the read/edit theme BEFORE rendering. The ManifestPackagingService (registered below) supplies the
		// client-side manifest compose; registration order is fine (the provider resolves the service at call time).
		this.pict.addProvider('EntityMetadataForm', libEntityMetadataFormProvider.default_configuration, libEntityMetadataFormProvider);
		this.pict.addProvider('EntityMetaFormHost',
			{
				ProviderIdentifier: 'EntityMetaFormHost',
				DestinationType: 'div',
				DestinationAddress: '#EMF-Body',
				DataAddress: 'AppData.EntityMetaValues',
				CreateDestinationElement: false,
				AutoRenderOnLoad: false,
			}, libPictSectionFormHost);

		// --- LIMS Samples -------------------------------------------------------------------------------------
		// The configuration-based Sample screens rebuilt from web/lims. The shared manifest-packaging-service
		// loads the customer's configured LIMS-Sample-{Create,Edit,View} ManifestSchema; the shared web/providers/*
		// LIMS providers own the Sample CRUD/read fan-out and the save pipeline (QR minting, storage-location join,
		// sample-log). The base class already provides HeadlightQRCodeHash + SessionProvider + HeadlightRestClient;
		// MPS reuses the existing HeadlightRestClient (so it hits the app's /1.0/ base, not QA).
		this.pict.addAndInstantiateServiceType('ManifestPackagingService', libManifestPackagingService);
		this.pict.addProvider('CustomerRecordManifests', libCustomerRecordManifestsProvider.default_configuration, libCustomerRecordManifestsProvider);
		this.pict.addProvider('UtilityProvider', libUtilityProvider.default_configuration, libUtilityProvider);
		this.pict.addProvider('LocationProvider', libLocationProvider.default_configuration, libLocationProvider);
		this.pict.addProvider('SampleLogProvider', libSampleLogProvider.default_configuration, libSampleLogProvider);
		this.pict.addProvider('SampleProvider', libSampleProvider.default_configuration, libSampleProvider);
		this.pict.addProvider('LineItemProvider', libLineItemProvider.default_configuration, libLineItemProvider);
		this.pict.addProvider('TestInstanceProvider', libTestInstanceProvider.default_configuration, libTestInstanceProvider);
		this.pict.addProvider('DocumentProvider', libDocumentProvider.default_configuration, libDocumentProvider);
		this.pict.addProvider('GeoProvider', libGeoProvider.default_configuration, libGeoProvider);
		// QRCodeProvider backs the Sample QR thumbnail + "Print Label" (printDecoratedEntity). The base class already
		// provides HeadlightQuickSummaries (printable decoration) + HeadlightQRCodeHash; RecordExtension is registered
		// below. Scans (keyboard scanner and camera) go through HeadlightQR's scan handlers, which the panels register.
		this.pict.addProvider('QRCodeProvider', (libQRCodeProvider.default_configuration || {}), libQRCodeProvider);
		// Off here (scans open Sample Details); a micro app turns it on to open the scanned record's screen.
		this.pict.addProvider('ScanToOpen', libScanToOpenProvider.default_configuration, libScanToOpenProvider);
		this.pict.addProvider('RecordExtension', libRecordExtensionProvider.default_configuration, libRecordExtensionProvider);
		this.pict.addProvider('FormInitialBundle', libFormInitialBundleProvider.default_configuration, libFormInitialBundleProvider);
		// The Sample dynamic-form runner (pict-section-form-host): renders the customer's configured
		// LIMS-Sample-{View,Create,Edit} manifest into #Sample-FormHost. DataAddress 'AppData' makes the form marshal
		// to AppData.SampleDetails.* exactly as the LIMS app does (descriptor DataAddresses are SampleDetails.*), so
		// the SampleWorkspace provider reads/writes AppData.SampleDetails directly. Registered empty; the provider
		// drives loadForm/getFormData/save on demand per mode.
		this.pict.addProvider('SampleFormHost',
			{
				ProviderIdentifier: 'SampleFormHost',
				DestinationType: 'div',
				DestinationAddress: '#Sample-FormHost',
				DataAddress: 'AppData',
				CreateDestinationElement: false,
				AutoRenderOnLoad: true,
			}, libPictSectionFormHost);

		// Register the config-driven Headlight Workspaces (pict-section-workspace): each config becomes a
		// provider + view pair; the router registers the matching bridge routes in onBeforeLoadDataAsync.
		libWorkspaceConfigs.registerWorkspaces(this.pict);

		// Data Lake (materialized projections): the catalog of imported clones + computed projections.
		this.pict.addProvider('DatalakeCatalogProvider', libDatalakeCatalogProvider.default_configuration, libDatalakeCatalogProvider);
		this.pict.addView('DatalakeCatalog', DatalakeCatalog.default_configuration, DatalakeCatalog);
		// Data Downloader: gated download page for the desktop apps we ship (first of three binary apps).
		this.pict.addProvider('DataDownloaderProvider', libDataDownloaderProvider.default_configuration, libDataDownloaderProvider);
		this.pict.addView('DataDownloader', libDataDownloader.default_configuration, libDataDownloader);
		this.pict.addProvider('DatalakeDatasetProvider', libDatalakeDatasetProvider.default_configuration, libDatalakeDatasetProvider);
		this.pict.addView('DatalakeDataset', DatalakeDataset.default_configuration, DatalakeDataset);
		this.pict.addProvider('DatalakeProjectionEditorProvider', libDatalakeProjectionEditorProvider.default_configuration, libDatalakeProjectionEditorProvider);
		this.pict.addView('DatalakeProjectionEditor', DatalakeProjectionEditor.default_configuration, DatalakeProjectionEditor);

		// Configuration viewer/editor: provenance-aware cascading config + record CustomProperties.
		this.pict.addProvider('ConfigViewer', libConfigViewerProvider.default_configuration, libConfigViewerProvider);
		this.pict.addView('ConfigViewer', libConfigViewer.default_configuration, libConfigViewer);
		this.pict.addProvider('Documentation', libDocumentationProvider.default_configuration, libDocumentationProvider);
		this.pict.addView('Documentation', libDocumentation.default_configuration, libDocumentation);
		// Reused forms-app RNI metadata editor. Keep the hash 'ReportNamedInstanceCreate' — its template's inline
		// handlers reference _Pict.views.ReportNamedInstanceCreate — and override the container + return routes so
		// it renders inside the config app and returns to the Document Types list (see RecordSetCustomViews below).
		this.pict.addView('ReportNamedInstanceCreate', Object.assign({}, libReportNamedInstanceMetadataEditor.default_configuration,
			{
				ContainerDestination: '#PlatformConfiguration-Application-Container',
				DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
				AfterSaveRoute: '#/PSRS/ReportNamedInstance/List',
				ListRoute: '#/PSRS/ReportNamedInstance/List',
			}), libReportNamedInstanceMetadataEditor);
		this.pict.addProvider('Entitlements', libEntitlementsProvider.default_configuration, libEntitlementsProvider);
		this.pict.addView('Entitlements', libEntitlements.default_configuration, libEntitlements);
		this.pict.addProvider('Certifications', libCertificationsProvider.default_configuration, libCertificationsProvider);
		this.pict.addView('Certifications', libCertifications.default_configuration, libCertifications);
		this.pict.addProvider('ATMDashboardConfig', libATMDashboardConfigProvider.default_configuration, libATMDashboardConfigProvider);
		this.pict.addView('ATMDashboardConfig', libATMDashboardConfig.default_configuration, libATMDashboardConfig);
		// Sample configuration screens: choose the Samples-list columns (writes the MasterListCatalog Configuration
		// that drives #/PSRS/Sample/List) + the sample QR-code format (Customer.CustomProperties.CustomSampleGUID).
		this.pict.addProvider('AssetTypeDescriptor', libAssetTypeDescriptorProvider.default_configuration, libAssetTypeDescriptorProvider);
		this.pict.addView('AssetTypeDescriptor', libAssetTypeDescriptor.default_configuration, libAssetTypeDescriptor);
		this.pict.addProvider('SampleListColumnsConfig', libSampleListColumnsConfigProvider.default_configuration, libSampleListColumnsConfigProvider);
		this.pict.addView('SampleListColumnsConfig', libSampleListColumnsConfig.default_configuration, libSampleListColumnsConfig);
		this.pict.addProvider('SampleQRCodeConfig', libSampleQRCodeConfigProvider.default_configuration, libSampleQRCodeConfigProvider);
		this.pict.addView('SampleQRCodeConfig', libSampleQRCodeConfig.default_configuration, libSampleQRCodeConfig);
		this.pict.addView('PrinterCodes', libPrinterCodes.default_configuration, libPrinterCodes);
		// Scan Samples as a page of its own (#/ScanSamples), the LIMS top nav's Scanning Cart: the panel's cart, separately kept.
		this.pict.addView('ScanSamplesPage', libScanSamplesPage.default_configuration, libScanSamplesPage);
		this.pict.addProvider('EntityDestinationsConfig', libEntityDestinationsConfigProvider.default_configuration, libEntityDestinationsConfigProvider);
		this.pict.addView('EntityDestinationsConfig', libEntityDestinationsConfig.default_configuration, libEntityDestinationsConfig);
		this.installDashboards();
		this.pict.addProvider('DashboardAccessConfig', libDashboardAccessConfigProvider.default_configuration, libDashboardAccessConfigProvider);
		this.pict.addView('DashboardAccessConfig', libDashboardAccessConfig.default_configuration, libDashboardAccessConfig);
		libConfigScreens.registerConfigScreens(this.pict);
		this.pict.addProvider('CertificationTypes', libCertificationTypesProvider.default_configuration, libCertificationTypesProvider);
		this.pict.addView('CertificationTypes', libCertificationTypes.default_configuration, libCertificationTypes);
		this.pict.addProvider('AssetCatalogSync', libAssetCatalogSyncProvider.default_configuration, libAssetCatalogSyncProvider);
		this.pict.addView('AssetCatalogSync', libAssetCatalogSync.default_configuration, libAssetCatalogSync);
		// Third ProjectCenterMap instance — the asset Location tab's interactive Leaflet map, bound to the
		// AssetWorkspace provider's working center (center/hasCenter/setCenter) + its own #AW-MapHost.
		this.pict.addView('AssetLocationMap', Object.assign({}, libProjectCenterMap.default_configuration,
			{ ViewIdentifier: 'AssetLocationMap', ProviderName: 'AssetWorkspace', MapHostID: 'AW-MapHost' }), libProjectCenterMap);
		// Read-only 2-marker (Capture + User) map for the Observation workspace's Location tab (its own #OW-MapHost).
		this.pict.addView('ObservationLocationMap', libObservationLocationMap.default_configuration, libObservationLocationMap);
		// The same read-only map for the Location workspace's Details tab: one marker at the Location's coordinates.
		this.pict.addView('LocationViewMap', Object.assign({}, libObservationLocationMap.default_configuration,
			{ ViewIdentifier: 'LocationViewMap', ProviderName: 'LocationWorkspace', MapHostID: 'LW-MapHost', LeafletDestinationAddress: 'LW-MapHost' }), libObservationLocationMap);
		this.pict.addProvider('ProjectContractTypes', libProjectContractTypesProvider.default_configuration, libProjectContractTypesProvider);
		this.pict.addView('ProjectTypes', libProjectTypes.default_configuration, libProjectTypes);
		this.pict.addView('ContractTypes', libContractTypes.default_configuration, libContractTypes);
		this.pict.addProvider('RecordDates', libRecordDatesProvider.default_configuration, libRecordDatesProvider);
		this.pict.addView('RecordDates', libRecordDates.default_configuration, libRecordDates);
		this.pict.addProvider('ProjectStaffing', libProjectStaffingProvider.default_configuration, libProjectStaffingProvider);
		this.pict.addProvider('SuperUserGrant', libSuperUserGrantProvider.default_configuration, libSuperUserGrantProvider);
		this.pict.addView('SuperUserGrant', libSuperUserGrant.default_configuration, libSuperUserGrant);
		// Internals version screens (Headlight-only; gated in the router + nav): one shared inventory provider
		// feeds both the single-environment and cross-environment version views.
		this.pict.addProvider('InternalsServiceCatalog', libInternalsServiceCatalog.default_configuration, libInternalsServiceCatalog);
		this.pict.addView('ServiceVersions', libInternalsServiceVersions.default_configuration, libInternalsServiceVersions);
		this.pict.addView('ServiceVersionsMultiEnv', libInternalsServiceVersionsMultiEnv.default_configuration, libInternalsServiceVersionsMultiEnv);
		this.pict.addProvider('ManageCustomers', libManageCustomersProvider.default_configuration, libManageCustomersProvider);
		this.pict.addView('ManageCustomers', libManageCustomers.default_configuration, libManageCustomers);
		this.pict.addProvider('CustomersList', libCustomersListProvider.default_configuration, libCustomersListProvider);
		this.pict.addView('Customers', libCustomers.default_configuration, libCustomers);
		this.pict.addProvider('ActivityObservability', libActivityObservabilityProvider.default_configuration, libActivityObservabilityProvider);
		this.pict.addView('UserActivity', libUserActivity.default_configuration, libUserActivity);
		this.pict.addView('UserLoginOverview', libUserLoginOverview.default_configuration, libUserLoginOverview);
		this.pict.addView('ProjectActivity', libProjectActivity.default_configuration, libProjectActivity);
		this.pict.addProvider('ConfigCloneProvider', libConfigCloneProvider.default_configuration, libConfigCloneProvider);
		this.pict.addView('ConfigClone', libConfigClone.default_configuration, libConfigClone);
		this.pict.addProvider('RoleCloneProvider', libRoleCloneProvider.default_configuration, libRoleCloneProvider);
		this.pict.addView('RoleClone', libRoleClone.default_configuration, libRoleClone);

		// Data Mapper: the visual data-mapper (pict-section-mapping) embedded against the PDO /1.0/mapper surface.
		this.pict.addView('DataMapper', libDataMapper.default_configuration, libDataMapper);

		// Data Import: the generic single-entity importer (pict-section-dataimport) for simple reference
		// entities (Materials, Pay Items, ...). The DataImportManager provider supplies Headlight schema +
		// push seams so the wizard runs entirely through HeadlightRestClient (no pict.EntityProvider).
		this.pict.addProvider('Pict-Section-DataImport', libPictSectionDataImport.default_configuration, libPictSectionDataImport);
		this.pict.addProvider('DataImportManager', libDataImportProvider.default_configuration, libDataImportProvider);
		this.pict.addView('DataImport', libDataImport.default_configuration, libDataImport);

		// Contract Package Import: the bespoke multi-entity importer (Contract → Project → Bid Item → Line
		// Item) — reuses DataImportManager's ordered push + engine, driven by an entity-aware GUID strategy.
		this.pict.addProvider('ContractPackageImportManager', libContractPackageImportProvider.default_configuration, libContractPackageImportProvider);
		this.pict.addView('ContractPackageImport', libContractPackageImport.default_configuration, libContractPackageImport);

		// Access & Permissions workspace: the cohesive RBAC screen (roles, permission groups, the permission
		// matrix, the effective-permissions visualizer, per-user role assignment) — replaces the raw lists.
		this.pict.addProvider('AccessWorkspace', libAccessWorkspaceProvider.default_configuration, libAccessWorkspaceProvider);
		this.pict.addView('AccessWorkspace', libAccessWorkspace.default_configuration, libAccessWorkspace);
		this.pict.addProvider('PermissionMatrix', libPermissionMatrixProvider.default_configuration, libPermissionMatrixProvider);
		this.pict.addProvider('PermissionVisualizer', libPermissionVisualizerProvider.default_configuration, libPermissionVisualizerProvider);
		this.pict.addProvider('GroupComposition', libGroupCompositionProvider.default_configuration, libGroupCompositionProvider);
		this.pict.addProvider('UserGroupGrants', libUserGroupGrantsProvider.default_configuration, libUserGroupGrantsProvider);
		this.pict.addProvider('RoleAssignment', libRoleAssignmentProvider.default_configuration, libRoleAssignmentProvider);

		this.pict.addProvider('ContractProjectTypesImportProvider', libContractProjectTypesImportProvider.default_configuration, libContractProjectTypesImportProvider);

		// Dashboard data provider — shared with the Activity Observability views. (The Analytics dashboard views
		// that also consumed it were retired, so only the provider remains.)
		this.pict.addProvider('PlatformDashboardData', PlatformDashboardDataProvider.default_configuration, PlatformDashboardDataProvider);

		// In-app documentation (pict-section-inlinedocumentation). Registered under its own
		// ProviderIdentifier ('Pict-InlineDocumentation') because its own views resolve the
		// provider by that hash. Its constructor registers the doc browser views; the content
		// is loaded from /docs/ and wired up after login in _initializeHelpDocumentation().
		this.pict.addProvider('Pict-InlineDocumentation', libInlineDocumentation.default_configuration, libInlineDocumentation);

		// The full-page Headlight Academy (route #/Academy[/:Topic]). Its provider reads the inline-doc TOC + fetches
		// articles through that provider, so it stays in sync with the panel and the doc editor.
		this.pict.addProvider('Academy', AcademyProvider.default_configuration, AcademyProvider);
		this.pict.addView('Academy', AcademyView.default_configuration, AcademyView);

		// the navigation section holds the app's
		// configuration graph and registers the render-type views (Navigation-Hub/Sidebar/
		// Palette/List). OnNavigate routes for real by setting the hash route.
		this.pict.addProvider('Pict-Navigation', { NavigationGraph: libNavigationGraph }, libPictSectionNavigation);
		this.pict.providers['Pict-Navigation'].onNavigate = (pNode) =>
		{
			if (pNode && typeof pNode.Route === 'string' && pNode.Route.charAt(0) === '#')
			{
				window.location.hash = pNode.Route.replace(/^#/, '');
			}
		};

		this.pict.ContentAssignment.removeClass('.is-loading-splash', 'is-hidden');
	}

	/**
	 * Called during initialization to register templates.
	 */
	registerTemplates()
	{
		super.registerTemplates();
		for (const templateSet of Object.values(HeadlightDynamicInputsBundle.getThemeMap()))
		{
			this.pict.providers.PictFormSectionDefaultTemplateProvider.injectTemplateSet(templateSet);
		}
	}

	/**
	 * Change the theme to one of the available themes.
	 *
	 * @param {string} pTheme - The theme to change to.
	 */
	changeTheme(pTheme)
	{
		const newTheme = 'PictProvider-Theme-' + pTheme;
		this.pict.log.info(`Changing theme to: ${newTheme}`);
		this.theme = newTheme;
		if (this.pict.views.PictFormMetacontroller)
		{
			this.pict.views.PictFormMetacontroller.formTemplatePrefix = newTheme;
		}
	}

	onInitialize()
	{
		this.changeTheme('Read');

		this.pict.addView('HeadlightLogin', {}, require('headlight-view-login'));

		this.pict.views.PictFormMetacontroller.viewMarshalDestination = 'AppData';

		return super.onInitialize();
	}

	onAfterInitialize()
	{
		this.pict.views.PictFormMetacontroller.generateMetatemplate();
		return super.onAfterInitialize();
	}

	async onInitializeAsync(fCallback)
	{
		this.pict.views.PictFormMetacontroller.viewMarshalDestination = 'AppData';
		this.pict.instantiateServiceProvider('PictSectionRecordSet', this.options);
		this.pict.PictSectionRecordSet.sessionProviders.push(async (capability) =>
		{
			return this.pict.providers.SessionProvider.getSession().then((session) => session.LoggedIn).catch((error) => false);
		});
		// NOTE: Router must be added BEFORE PSRS initialize so router hooks attach to PSRS routes
		this.pict.addProvider('PlatformConfigurationRouter', libPlatformConfigurationRouter.default_configuration, libPlatformConfigurationRouter);
		// Initialize the views for the recordset metacontroller service
		this.pict.PictSectionRecordSet.initialize();

		await super.onInitializeAsync(fCallback);
	}

	async onAfterInitializeAsync(fCallback)
	{
		try
		{
			// Register CSS for the PSRS read-page template overrides through the
			// Pict CSS cascade rather than inline <style> tags in the templates.
			this.pict.CSSMap.addCSS('PlatformConfiguration-PSRS-Read-CSS', /*css*/`
				.record-button-bar-hidden { display: none; }
				.record-button-bar { display: flex; justify-content: flex-end; margin-top: 15px; gap: 10px; }
				.psrs-split-view { display: flex; height: 100%; gap: 1.25rem; align-items: flex-start; }
				.psrs-left-panel { overflow: auto; }
				.psrs-right-panel { overflow: auto; }
				#psrs-resize { width: 1px; padding-left: 10px; padding-right: 10px; cursor: col-resize; display: none; }
				#psrs-resize > div { width: 1px; height: 100%; background-color: rgba(0,0,0,0.5); }
				/* Read-tab folder chrome (.psrs-tab / #PRSP-Read-Tab-Nav / .psrs-tab-body) is styled once,
				   scoped to #PRSP-Read-Tabs-Container, in css/platform-configuration.css. Do NOT redeclare it
				   here — the old unscoped copies fought that block (centered nav, boxed active tab). */

				/* Generic read-view tab strip → the app's shared TabBar look (see ReadView-TabBar-Decoration.js,
				   which rewrites each tab into an icon+label button, and views/TabBar.js for the canonical look).
				   Compact underline-active tabs + FontAwesome icons + horizontal-scroll overflow, matching the
				   bespoke workspaces. Scoped under the app container id so it outranks the .psrs-split-tabnav pill
				   rules (css/platform-configuration.css) AND the module's own .psrs-tab CSS by specificity — no
				   !important. MIGRATION: fold into pict-section-recordset alongside the _structureTabs change. */
				${ libTabBar.TABBAR_CSS }
				#PlatformConfiguration-Application-Container .psrs-split-tabnav { display: block; margin: 0.25rem 0 0; }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav {
					display: flex; align-items: stretch; flex-wrap: nowrap; justify-content: flex-start;
					width: 100%; gap: 0.1rem; margin: 0.25rem 0 0; padding: 0;
					overflow-x: clip; overflow-y: visible;
					border-bottom: 1px solid var(--theme-color-border-default, #d7dce3); }
				/* "Full Record" stays first/left; drop the auto-margin that scattered the strip to the right. */
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav #PSRS-TabNav-FullRecord { margin-right: 0; }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab {
					flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap;
					cursor: pointer; border: none; border-radius: 0; background: transparent;
					padding: 0.55rem 0.9rem; margin-bottom: -1px; border-bottom: 2px solid transparent;
					font: inherit; font-size: 0.85rem; font-weight: 500; line-height: 1.2;
					color: var(--theme-color-text-secondary, #45505f);
					transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease; }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab:hover { color: var(--theme-color-text-primary, #1f2733); background: var(--theme-color-background-secondary, #f0f2f6); }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab:focus-visible { outline: 2px solid var(--theme-color-brand-primary, #5650e6); outline-offset: -2px; }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab .icon { font-size: 0.92em; opacity: 0.8; }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab.is-active { color: var(--theme-color-brand-primary, #5650e6); font-weight: 650; background: transparent; border: none; border-bottom: 2px solid var(--theme-color-brand-primary, #5650e6); }
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab.is-active .icon { opacity: 1; }
				/* Overflow tabs the TabBar menu-layout moved into the "More" dropdown must actually hide. The id-scoped
				   .psrs-tab rule above would otherwise out-rank TabBar's .hltab.is-overflow display:none (2 ids vs
				   2 classes) and leave them taking space, shoving the More button off-screen — so hide them id-scoped. */
				#PlatformConfiguration-Application-Container #PRSP-Read-Tab-Nav .psrs-tab.is-overflow { display: none; }
				/* Fuse the tab strip to the content below it (Project .pw-panel folder look): no gap under the strip,
				   and in the collapsed / Full Record state the record card fills full width, drops its top border +
				   top radius, and connects under the strip's bottom border so tabs + record read as one card. */
				#PlatformConfiguration-Application-Container .psrs-split-view.psrs-collapsed .psrs-left-panel { padding: 0; }
				#PlatformConfiguration-Application-Container .psrs-split-view.psrs-collapsed .pcfg-record-panel { border-top: none; border-top-left-radius: 0; border-top-right-radius: 0; }
				/* Manifest-form boolean captions render via Bulma's .is-checkbox / .control-status at the base 16px/600,
				   which dwarfs the shrunk field labels (e.g. a Material's "Brand Name Required" / "Active" towered over
				   NAME, CATALOG CODE, …). Bring the checkbox caption down to the field-label scale so it reads as a
				   label, not a heading, in both the read and the manifest edit/create forms. */
				#PlatformConfiguration-Application-Container p.control.control-status { font-size: 0.82rem; font-weight: 600; }
				#PlatformConfiguration-Application-Container p.control.control-status .control-status-icon { font-size: 1rem; }
			`, 500);

			this.pict.CSSMap.injectCSS();

			// Set PSRS child view destination addresses
			this.pict.PictSectionRecordSet.childViews.read.options.DefaultDestinationAddress = '#PlatformConfiguration-Application-Container';
			this.pict.PictSectionRecordSet.childViews.list.options.DefaultDestinationAddress = '#PlatformConfiguration-Application-Container';
			this.pict.PictSectionRecordSet.childViews.dashboard.options.DefaultDestinationAddress = '#PlatformConfiguration-Application-Container';
			// The Create child view defaults to '#PRSP_Container' which this app does not render — point it at the
			// shared application container so `/PSRS/<entity>/Create` routes actually mount their form. Affects every
			// configurable record-set (ContractType, ProjectType, User, etc.).
			this.pict.PictSectionRecordSet.childViews.create.options.DefaultDestinationAddress = '#PlatformConfiguration-Application-Container';
			// The association screens (bulk Assign / Matrix / Unlink) also default to '#PRSP_Container';
			// point them at the shared container so #/PSRS/<entity>/Associate/<association> (and the
			// Matrix / Unlink routes) actually render in this app.
			[ 'associate', 'associateMatrix', 'associateUnlink' ].forEach((pChildViewKey) =>
			{
				const tmpChildView = this.pict.PictSectionRecordSet.childViews[pChildViewKey];
				if (tmpChildView && tmpChildView.options)
				{
					tmpChildView.options.DefaultDestinationAddress = '#PlatformConfiguration-Application-Container';
				}
			});

			// Activate Bulma-styled template overrides for PSRS views
			this.pict.providers.ComplexFilterProvider.prepareComplexFilter();
			this.pict.providers.ImprovedTabularRecordSetProvider.prepareTable();

			// Bulma color classes emitted by list-cell {~TIfAbs:~} status mappings (e.g. Project Status tag)
			this.pict.TemplateProvider.addTemplate('PSRS-StatusTag-Success', 'is-success');
			this.pict.TemplateProvider.addTemplate('PSRS-StatusTag-Info', 'is-info');
			this.pict.TemplateProvider.addTemplate('PSRS-StatusTag-Danger', 'is-danger');

			// Override the default PSRS record button bar with Bulma-styled buttons
			this.pict.TemplateProvider.addTemplate('PRSP-Read-RecordButtonBar-Template', /*html*/`
				<div class="record-button-bar">
					<button id="PRSP-Read-EditButton" type="button" class="button is-link" onclick="_Pict.views['RSP-RecordSet-Read'].edit()"><span class="icon"><i class="fas fa-pen-to-square"></i></span><span>Edit</span></button>
					<button id="PRSP-Read-SaveButton" type="button" class="button is-success" onclick="_Pict.views['RSP-RecordSet-Read'].save()"><span class="icon"><i class="fas fa-floppy-disk"></i></span><span>Save</span></button>
					<button id="PRSP-Read-CancelButton" type="button" class="button is-warning" onclick="_Pict.views['RSP-RecordSet-Read'].cancel()"><span class="icon"><i class="fas fa-xmark"></i></span><span>Cancel</span></button>
				</div>
			`);

			// Match the Create page's button bar to the Bulma-styled Read bar (the
			// default template ships bare buttons + an inline <style>). Reuses the
			// .record-button-bar CSS registered in the PSRS Read CSS block above.
			this.pict.TemplateProvider.addTemplate('PRSP-Create-RecordButtonBar-Template', /*html*/`
				<div class="record-button-bar">
					<button id="PRSP-Create-SaveButton" type="button" class="button is-success" onclick="_Pict.views['RSP-RecordSet-Create'].save()"><span class="icon"><i class="fas fa-floppy-disk"></i></span><span>Save</span></button>
					<button id="PRSP-Create-ClearButton" type="button" class="button is-warning" onclick="_Pict.views['RSP-RecordSet-Create'].clear()"><span class="icon"><i class="fas fa-eraser"></i></span><span>Clear</span></button>
				</div>
			`);

			// Register meta template patterns for human-readable entity names
			this.pict.MetaTemplate.addPattern('{~RecordSetPageItemName:', '~}', (pHash, pData, fCallback) =>
			{
				const recordSet = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
				// Prefer the Read view's display-name heuristic (NameFirst/Last, Name, Title, …); fall back to a literal Name, then the GUID.
					return this.pict.AppData.PRSP_RecordAudit?.DisplayName || this.pict.AppData[`${recordSet}Details`]?.Name || this.pict.AppData[`${recordSet}Details`]?.[`GUID${recordSet}`];
			});
			this.pict.MetaTemplate.addPattern('{~ParsedEntityType:', '~}', (pHash, pData, fCallback) =>
			{
				const recordSet = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
				return recordSet.replace(/([a-z])([A-Z])/g, '$1 $2');
			});
			// Format a numeric value with thousands separators (e.g. 20748629 -> "20,748,629")
			this.pict.MetaTemplate.addPattern('{~CommaNumber:', '~}', (pHash, pData, fCallback) =>
			{
				const value = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
				if (value === null || typeof (value) === 'undefined' || value === '' || isNaN(Number(value)))
				{
					return (value === null || typeof (value) === 'undefined') ? '' : value;
				}
				return this.pict.DataFormat.formatterAddCommasToNumber(Math.round(Number(value)));
			});

			// Resolve a Sample.Status CODE to its friendly label for the Samples list column (and anywhere else).
			// Mirrors the LIMS resolveSampleStatusName: prefer a live customer-curated vocabulary
			// (AppData.SampleStatusVocab, if loaded), else the historical SiteManager 13-code set, else the raw code.
			const tmpSampleStatusLabels =
			{
				'N/A': 'N/A', 'LOGD': 'Logged', 'RECV': 'Received', 'INFO': 'Info', 'PREA': 'Preauthorized',
				'PEND': 'Pending', 'TEST': 'In Testing', 'TSTR': 'Test Run', 'FAIL': 'Failed',
				'FAIC': 'Failed with Comments', 'PASS': 'Passed', 'PASC': 'Passed with Comments', 'VOID': 'Voided',
			};
			this.pict.MetaTemplate.addPattern('{~SampleStatusName:', '~}', (pHash, pData, fCallback) =>
			{
				const value = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
				if (value === null || typeof (value) === 'undefined' || value === '') { return ''; }
				const tmpVocab = (this.pict.AppData && this.pict.AppData.SampleStatusVocab) || {};
				return tmpVocab[value] || tmpSampleStatusLabels[value] || String(value);
			});

			// Override the Basic layout template (default for all entities)
			this.pict.TemplateProvider.addTemplate('PRSP-Read-Basic-Template', /*html*/`
				<!-- Platform Configuration entity page template (Basic) -->
				{~T:PRSP-Read-Header-Template~}
				<div class="pcfg-record-panel">
					{~T:PRSP-Read-RecordRead-Template~}
				</div>
				<!-- End entity page template (Basic) -->
			`);

			// Match the Create page to the Read Basic panel framing (the default
			// Create template is a bare <h1> + unstyled form). Wraps the create form
			// + button bar in the same soft panel used by the read/edit views.
			this.pict.TemplateProvider.addTemplate('PRSP-Create-Basic-Template', /*html*/`
				<!-- Platform Configuration entity create template -->
				<div class="prsp-read-head"><div class="prsp-read-eyebrow">New</div><h1 class="prsp-read-title">{~ParsedEntityType:Record.RecordSet~}</h1></div>
				<div class="pcfg-record-panel">
					{~T:PRSP-Create-RecordCreate-Template~}
					{~T:PRSP-Create-RecordButtonBar-Template~}
				</div>
				<!-- End entity create template -->
			`);

			// Override the Split layout template (the default for records with connections). The record is
			// the page; its connection tabs are a right-justified strip ON TOP (.psrs-split-tabnav). The view
			// opens collapsed (record full-width); choosing a connection tab expands its editor beside the
			// record, and re-choosing it collapses back. The module's "Full Record" collapse toggle is hidden
			// in CSS (#PSRS-TabNav-FullRecord) so only connection tabs show.
			this.pict.TemplateProvider.addTemplate('PRSP-Read-Split-Template', /*html*/`
				<!-- Platform Configuration entity page template (Split) -->
				{~T:PRSP-Read-Header-Template~}
				<div class="psrs-split-tabnav">{~T:PRSP-Read-RecordTabNav-Template~}</div>
				<div class="psrs-split-view psrs-collapsed">
					<div class="psrs-left-panel column">
						<div class="pcfg-record-panel psrs-details-body">
							{~T:PRSP-Read-RecordRead-Template~}
						</div>
					</div>
					<div id="psrs-resize"><div></div></div>
					<div class="psrs-right-panel column">
						{~T:PRSP-Read-RecordTab-Template~}
					</div>
				</div>
				<!-- End entity page template (Split) -->
			`);

			this.pict.views.HeadlightPanels.setPanelConfiguration({
				PanelOptions: {
					Context: 'PlatformConfiguration',
				},
				// This app runs in application mode (not document/form mode), so the
				// bottom navbar is configured to omit the document-context buttons:
				// Solver Log, Download, and Save. The Brand button (Approval Actions
				// toggle) is kept in the config so the Brand template can still render
				// without errors, but its wrapper is hidden via `is-hidden`.
				NavBar: {
					hideNavbar: false,
					colorClass: 'is-dark',
					location: 'bottom',
					Brand: {
						id: 'HeadlightPanels-NavBar-Brand',
						name: 'Approval Actions',
						displayName: '',
						hideIcon: 'always',
						iconClass: '',
						buttonClass: 'is-hidden',
						wrapperClass: 'is-hidden',
						onclick: '',
					},
					Buttons: [
						{
							id: 'HeadlightPanels-NavBar-Help-Button',
							name: 'Documentation',
							displayName: null,
							hideIcon: null,
							iconClass: 'fa-circle-question',
							buttonClass: 'is-dark',
							wrapperClass: null,
							onclick: '_Pict.PictApplication.toggleHelp()',
						},
						{
							id: 'HeadlightPanels-NavBar-Validations-Button',
							name: 'Validation Logs',
							displayName: null,
							hideIcon: null,
							iconClass: 'fa-exclamation-triangle',
							buttonClass: 'is-dark',
							wrapperClass: null,
							onclick: '_Pict.views.HeadlightPanels.handlePanelToggle(\'Validations\')',
						},
						{
							id: 'HeadlightPanels-NavBar-FullScreen-Button',
							name: 'screenResize',
							displayName: null,
							hideIcon: null,
							iconClass: 'fa-up-right-and-down-left-from-center',
							buttonClass: 'is-dark',
							wrapperClass: 'is-hidden-mobile is-hidden-tablet-only',
							onclick: '_Pict.providers.DocumentProvider.handleScreenResize()',
						},
						{
							id: 'HeadlightPanels-NavBar-Actions-Button',
							name: 'actions',
							displayName: null,
							hideIcon: null,
							iconClass: 'fa-bars',
							buttonClass: 'is-dark',
							wrapperClass: null,
							onclick: '_Pict.views.HeadlightPanels.handlePanelToggle(\'Navigation\')',
						},
					],
				},
				CustomNavigationList: [
					{
						Hash: 'Customers',
						Name: 'Customers',
						Icon: 'fas fa-city',
						Address: '#/PSRS/Customer/List',
						PanelSource: null,
					},
					{
						Hash: 'Users',
						Name: 'Users',
						Icon: 'fas fa-users',
						Address: '#/PSRS/User/List',
						PanelSource: null,
					},
					{
						Hash: 'Projects',
						Name: 'Projects',
						Icon: 'fas fa-folder-open',
						Address: '#/PSRS/Project/List',
						PanelSource: null,
					},
					{
						Hash: 'Roles',
						Name: 'Roles',
						Icon: 'fas fa-user-shield',
						Address: '#/PSRS/Role/List',
						PanelSource: null,
					},
					{
						Hash: 'ObservationTypes',
						Name: 'Observation Types',
						Icon: 'fas fa-clipboard-check',
						Address: '#/PSRS/ObservationManifest/List',
						PanelSource: null,
					},
					{
						Hash: 'Organizations',
						Name: 'Organizations',
						Icon: 'fas fa-building',
						Address: '#/PSRS/Organization/List',
						PanelSource: null,
					},
					{
						Hash: 'DocumentTypes',
						Name: 'Document Types',
						Icon: 'fas fa-file-lines',
						Address: '#/PSRS/ReportNamedInstance/List',
						PanelSource: null,
					},
					{
						Hash: 'ObservationTags',
						Name: 'Observation Tags',
						Icon: 'fas fa-tags',
						Address: '#/PSRS/ObservationTag/List',
						PanelSource: null,
					},
					{
						Hash: 'Settings',
						Name: 'Settings',
						Icon: 'fas fa-cog',
						Address: null,
						PanelSource: 'settings',
					},
				],
			});

			this._updateUserProfile();

			this.pict.providers.PlatformConfigurationRouter.initialize();
			this.pict.providers.RecordSetRouter.initialize();

			this.pict.views.HeadlightLogin.isUserLoggedIn((error, loggedIn) =>
			{
				if (error)
				{
					this.pict.log.error(`Error checking login status: ${error.message || error}`, { error });
				}
				if (!loggedIn)
				{
					// Reveal the login page by dismissing the boot splash. When the user
					// IS logged in we deliberately leave the splash up: route resolution
					// and the first view render replace the container content (removing
					// the splash) exactly when content is ready, so the user sees one
					// continuous splash instead of a hide-then-reshow flicker.
					this.pict.ContentAssignment.addClass('.is-loading-splash', 'is-hidden');
					this.pict.views.HeadlightLogin.customizeLoginPage(customLoginPageOptions);
					this.pict.views.HeadlightLogin.show();
				}
				else
				{
					this.onAfterLogin().catch((error) =>
					{
						this.pict.log.error(`Error loading data after login: ${error.message || error}`, { error });
					});
				}
				super.onAfterInitializeAsync(fCallback);
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error initializing Platform Configuration: ${error.message || error}`);
			super.onAfterInitializeAsync(fCallback);
		}
	}

	onAfterLoginAsync(fCallback)
	{
		super.onAfterLoginAsync(async (pError) =>
		{
			try
			{
				// onAfterLoginAsync runs on EVERY loginAsync() pass — the initial boot, an interactive
				// login, AND a logout (logout() calls loginAsync() to re-evaluate the now-dead session).
				// Refresh the navbar avatar/initials here so the chrome always matches the live session.
				// Previously _updateUserProfile() ran only at page-load init, so logging out and back in as
				// a different user left the prior user's initials in the header until a manual reload.
				this._updateUserProfile();
				if (!this.loggedIn)
				{
					// Logged out: arm onAfterLogin() to run again on the next sign-in (it is one-shot per login).
					this._afterLoginPromise = null;
					this.pict.views.HeadlightLogin.customizeLoginPage(customLoginPageOptions);
				}
				else
				{
					// Run the post-login setup (recordset configs, nav-gate filter, KPI counts, doc/nav shell).
					// onAfterInitializeAsync only calls onAfterLogin() when the user is ALREADY logged in at
					// page load, so an INTERACTIVE login (the login form, or switching users) never triggered it —
					// which left Analytics unfiltered in the nav, the KPI strip empty, and PSRS recordset configs
					// unloaded until a manual reload. Run it here too; it self-guards to once per login and must
					// complete before the PSRS routes are registered + resolved below.
					await this.onAfterLogin();

					this.pict.providers.RecordSetRouter.addRoutes(this.pict.providers.RecordSetRouter.pictRouter);

					// Master Lists: load the cascading per-list configuration (merged Global -> Customer -> Project -> User) and apply it to
					// the live recordset configs before the first route renders, so a deep-linked list
					// shows its curated columns immediately.
					try
					{
						await this.pict.providers.MasterListRegistry.loadAndApplyAllAsync();
					}
					catch (pMasterListError)
					{
						this.pict.log.error(`Master list config load failed: ${pMasterListError.message || pMasterListError}`);
					}

					// Master-list associations: register the join definitions so the Material / PayItem /
					// Test Specification read views show their association editor tabs (pict-section-recordset
					// >= 1.17). The API stamps the tenant (IDCustomer) onto each join from the session.
					try
					{
						const tmpAssociationManager = this.pict.providers.RecordSetAssociationManager;
						if (tmpAssociationManager)
						{
							Object.keys(libMasterListAssociations.ASSOCIATIONS).forEach((pKey) =>
							{
								tmpAssociationManager.addAssociation(pKey, libMasterListAssociations.ASSOCIATIONS[pKey]);
							});

							// Project <-> ObservationManifest: the showcase for the new PSRS capabilities — a
							// NAME-keyed side (the join references the manifest by ObservationManifestName), a RICH
							// join (per-row Journal/Spreadsheet/Ordinal config), and an overridable defaults hook.
							tmpAssociationManager.addAssociation('ProjectObservationManifest',
								{
									JoinEntity: 'ProjectObservationManifestJoin',
									JoinURLPrefix: '/1.0/',
									JoinEditableFields:
									[
										{ Field: 'Journal', Label: 'Journal', Type: 'checkbox' },
										{ Field: 'Spreadsheet', Label: 'Spreadsheet', Type: 'checkbox' },
										{ Field: 'Ordinal', Label: 'Order', Type: 'number' },
									],
									SideA: { RecordSet: 'Project', IDField: 'IDProject', DisplayField: 'Name', SearchFields: [ 'Name', 'Status' ], Title: 'Project' },
									SideB: { RecordSet: 'ObservationManifest', IDField: 'Name', JoinField: 'ObservationManifestName', DisplayField: 'DisplayName', SearchFields: [ 'Name', 'DisplayName' ], Title: 'Observation Type' },
									// The overridable defaults seam: seed a project from its customer's default manifest
									// set (the IDProject=0 rows), carrying each one's Journal/Spreadsheet/Ordinal config.
									// This is directional — it only makes sense anchored on a Project ("seed this project
									// with its default types"); anchored on a type the question ("seed which projects?") has no
									// sensible answer, so synthesis is a no-op there (and the editor hides the button via
									// AllowSynthesize:false on the manifest-side instance).
									SynthesizeDefaults: async (pContext) =>
									{
										if (!pContext.thisSide || (pContext.thisSide.RecordSet !== 'Project')) { return []; }
										const tmpRestClient = pContext.pict.fable.HeadlightRestClient || pContext.pict.HeadlightRestClient;
										const fGet = (pURL) => new Promise((resolve) => { tmpRestClient.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody)); });
										const tmpProject = await fGet(`Project/${pContext.thisID}`);
										const tmpCustomer = (tmpProject && (tmpProject.IDCustomer != null)) ? tmpProject.IDCustomer : 0;
										let tmpDefaults = await fGet(`ProjectObservationManifestJoins/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~${tmpCustomer}`);
										if (!Array.isArray(tmpDefaults) || (tmpDefaults.length === 0))
										{
											tmpDefaults = await fGet('ProjectObservationManifestJoins/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~0');
										}
										return (Array.isArray(tmpDefaults) ? tmpDefaults : []).map((pJoin) =>
											({ value: pJoin.ObservationManifestName, joinValues: { Journal: pJoin.Journal, Spreadsheet: pJoin.Spreadsheet, Ordinal: pJoin.Ordinal, Priority: pJoin.Priority } }));
									},
								});

							// User <-> Project: the user's project assignments (ProjectUserJoin). Anchored on the
							// User side in the bespoke User workspace's Projects tab. Plain link/unlink for v1 — the
							// API stamps IDCustomer from the session; the project role (IDRole) is not edited here yet.
							tmpAssociationManager.addAssociation('ProjectUser',
								{
									JoinEntity: 'ProjectUserJoin',
									JoinURLPrefix: '/1.0/',
									SideA: { RecordSet: 'Project', IDField: 'IDProject', DisplayField: 'Name', SearchFields: [ 'Name', 'Status' ], Title: 'Project' },
									SideB: { RecordSet: 'User', IDField: 'IDUser', DisplayField: 'NameLast', SearchFields: [ 'NameFirst', 'NameLast', 'Email', 'LoginID' ], ChipFields: [ 'NameFirst', 'Email' ], Title: 'User' },
								});

							// (Project <-> Equipment is a TERNARY join — ProjectEquipmentJoin carries IDProject + IDEquipment +
							// IDOrganization, used many-to-many. It is NOT an association-editor tab: the Connections ▸ Equipment
							// tab is the ProjectContractorMatrixEquipment M:N grid instead, so no addAssociation is registered.)

							// Personnel (ProjectContactJoin): the GENERATED ContactProject association keys the Contact
							// side on 'Title' (DisplayField + SearchFields), but Title is a job-title descriptor that is
							// frequently blank (e.g. LADOTD certified-payroll "crew" contacts) — so the picker would
							// search job titles (a last-name search returns nothing) and every current-personnel row
							// would render as "#<id>". Override the Contact side to display + search by NAME (NameFull,
							// falling through NameFirst/NameLast on search), with Title as a disambiguation chip —
							// mirroring the ProjectUser registration. Project stays on SideB (the Personnel tab anchors
							// on Project); addAssociation replaces the generated entry by hash.
							tmpAssociationManager.addAssociation('ContactProject',
								{
									JoinEntity: 'ProjectContactJoin',
									JoinURLPrefix: '/1.0/',
									SideA: { RecordSet: 'Contact', IDField: 'IDContact', DisplayField: 'NameFull', SearchFields: [ 'NameFull', 'NameFirst', 'NameLast', 'Email' ], ChipFields: [ 'Title' ], Title: 'Personnel' },
									SideB: { RecordSet: 'Project', IDField: 'IDProject', DisplayField: 'Name', SearchFields: [ 'Name', 'Status' ], Title: 'Project' },
								});

							// AASHTO element type <-> defect (PhysicalAssetElementTypePhysicalAssetDefectJoin): which defects apply to
							// an element type. The catalog Sync (#/AssetCatalogSync) seeds this join from the public lake; this tab
							// surfaces it and lets an admin add/remove links. Plain link/unlink — the base association editor handles it.
							tmpAssociationManager.addAssociation('PhysicalAssetElementTypeDefect',
								{
									JoinEntity: 'PhysicalAssetElementTypePhysicalAssetDefectJoin',
									JoinURLPrefix: '/1.0/',
									SideA: { RecordSet: 'PhysicalAssetElementType', IDField: 'IDPhysicalAssetElementType', DisplayField: 'Name', SearchFields: [ 'Name', 'Code', 'Section' ], ChipFields: [ 'Code' ], Title: 'Element Type' },
									SideB: { RecordSet: 'PhysicalAssetDefect', IDField: 'IDPhysicalAssetDefect', DisplayField: 'Name', SearchFields: [ 'Name', 'Code' ], ChipFields: [ 'Code' ], Title: 'Defect' },
								});

							// LineItem <-> Organization: the "Organizations" tab on a Line Item read view (associating a
							// Contractor to a line item). Registered INLINE to OVERRIDE the generated plain association of
							// the same hash (addAssociation replaces by hash; the generated read-tab still points at it), so
							// the Organization picker can (2) show each org's IDOrganization to disambiguate same-named
							// contractors, and (1) float THIS line item's project Prime Contractor (Project.IDOrganization)
							// to the top of the list. The pin needs runtime context (the anchor line item -> its project),
							// so it closes over the app REST client here — like ProjectUser / ProjectObservationManifest.
							tmpAssociationManager.addAssociation('LineItemOrganization',
								{
									JoinEntity: 'LineItemOrganizationJoin',
									JoinURLPrefix: '/1.0/',
									SideA: { RecordSet: 'LineItem', IDField: 'IDLineItem', DisplayField: 'Name', SearchFields: [ 'Name', 'ItemNumber' ],
										TableColumns: [ { Key: 'ItemNumber', DisplayName: 'Item Number' }, { Key: 'Name', DisplayName: 'Name' }, { Key: 'Quantity', DisplayName: 'Quantity' } ] },
									SideB:
									{
										RecordSet: 'Organization', IDField: 'IDOrganization', DisplayField: 'Name', SearchFields: [ 'Name', 'Type', 'VendorCode' ],
										TableColumns: [ { Key: 'Name', DisplayName: 'Name' }, { Key: 'VendorCode', DisplayName: 'Vendor Code' }, { Key: 'Type', DisplayName: 'Type' } ],
										// (2) The Organization's Vendor Code, shown as a chip after the name on each picker option AND
										// each current-association row — many contractors share similar names, so the code disambiguates.
										// (Matches the app-wide Organization identifier chip; see Association-Chip-Decoration.js.)
										ChipFields: [ { Field: 'VendorCode', Label: 'Vendor' } ],
										// (1) Pin the project's Prime Contractor to the top of the add-picker's browse list. The
										// anchor is the Line Item (pContext.ThisID); walk LineItem -> its Project -> IDOrganization.
										PriorityValues: async (pContext) =>
										{
											const tmpAnchorID = pContext && pContext.ThisID;
											if ((tmpAnchorID === undefined) || (tmpAnchorID === null) || (tmpAnchorID === '')) { return []; }
											const tmpRestClient = pContext.pict.fable.HeadlightRestClient || pContext.pict.HeadlightRestClient;
											if (!tmpRestClient) { return []; }
											const fGet = (pURL) => new Promise((resolve) => { tmpRestClient.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody)); });
											const tmpLineItem = await fGet(`LineItem/${tmpAnchorID}`);
											const tmpIDProject = tmpLineItem && tmpLineItem.IDProject;
											if (!tmpIDProject) { return []; }
											const tmpProject = await fGet(`Project/${tmpIDProject}`);
											const tmpPrime = tmpProject && tmpProject.IDOrganization;
											return ((tmpPrime !== undefined) && (tmpPrime !== null) && (Number(tmpPrime) > 0)) ? [ Number(tmpPrime) ] : [];
										},
									},
								});

								// Material <-> Organization: the "Organizations" tab on a Material read view. The join
								// (OrganizationMaterialJoin) carries an ExpirationDate the base editor already flags with a
								// read-only "Expired" badge; expose it as an inline-editable `date` on each relationship row
								// so a user can set / update / backfill that expiration. The generated association is a plain
								// link, so we enrich the already-registered definition in place rather than restating its
								// sides; the custom editor subclass (registered with the bespoke view tabs below) renders the
								// `date` kind the base editor does not know.
								const tmpMaterialOrgAssociation = tmpAssociationManager.getAssociation('MaterialOrganization');
								if (tmpMaterialOrgAssociation)
								{
									tmpMaterialOrgAssociation.JoinEditableFields = [ { Field: 'ExpirationDate', Label: 'Expires', Type: 'date' } ];
								}

								// Project <-> Organization (ProjectOrganizationJoin): the "Contractors" tab on the bespoke
								// Project workspace — link external organizations (contractors) to a project. The generated
								// OrganizationProject association is a plain link; enrich it in place so (a) the join's
								// DisadvantagedBusiness boolean is an inline "DBE" checkbox per row, and (b) the Organization
								// picker is findable by vendor code and disambiguated by it (many contractors share names).
								const tmpOrgProjectAssociation = tmpAssociationManager.getAssociation('OrganizationProject');
								if (tmpOrgProjectAssociation)
								{
									tmpOrgProjectAssociation.JoinEditableFields = [ { Field: 'DisadvantagedBusiness', Label: 'DBE', Type: 'checkbox' } ];
									if (tmpOrgProjectAssociation.SideA)
									{
										tmpOrgProjectAssociation.SideA.SearchFields = [ 'Name', 'VendorCode', 'Type' ];
										tmpOrgProjectAssociation.SideA.ChipFields = [ { Field: 'VendorCode', Label: 'Code' } ];
									}
								}
						}
					}
					catch (pAssociationError)
					{
						this.pict.log.error(`Master list association registration failed: ${pAssociationError.message || pAssociationError}`);
					}

					// Identifier chips on EVERY association (after all associations are registered above):
					// (A) give each side a default code chip (Material Code / Item Code / Vendor Code / …) so
					// similarly-named records are distinguishable in the inline current-associations list AND the
					// add-picker; (B) extend the add-picker's user-selectable ⚙ field-decoration choice to the
					// current-associations list rows (one per-entity choice decorates both). App-local; best-effort.
					try
					{
						const tmpChipManager = this.pict.providers.RecordSetAssociationManager;
						if (tmpChipManager)
						{
							libAssociationChipDecoration.applyDefaultAssociationChips(tmpChipManager);
							libAssociationChipDecoration.patchAssociationListDecoration(tmpChipManager);
						}
					}
					catch (pChipError)
					{
						this.pict.log.error(`Association chip decoration setup failed: ${pChipError.message || pChipError}`);
					}

					// Users list "Role" filter → full role-membership union (base User.IDRole + UserRoleJoin +
					// UserContextRole). Patches the User record provider to expand the marked membership clause at
					// fetch time; best-effort (degrades to the stock UserRoleJoin filter if it can't patch).
					try
					{
						libUserRoleMembershipFilter.patchUserRoleMembershipFilter(this.pict);
					}
					catch (pRoleFilterError)
					{
						this.pict.log.error(`User role-membership filter patch failed: ${pRoleFilterError.message || pRoleFilterError}`);
					}

					// Read-view tab strip -> shared TabBar look: rewrite the generic read view's tab templates so
					// EVERY master-list read view gets the compact underline tabs + icons + scroll overflow the
					// bespoke workspaces already have (paired with the CSS injected in the read-page CSS block).
					// One patch lifts all generic read entities at once; setTab is untouched (buttons keep .psrs-tab + id).
					try
					{
						const tmpReadView = this.pict.views['RSP-RecordSet-Read'];
						if (tmpReadView)
						{
							libReadViewTabBarDecoration.patchReadViewTabBar(tmpReadView);
							// Overflow -> "More" dropdown (the shared TabBar menu strategy the bespoke workspaces use)
							// so tabs that don't fit stay reachable instead of scrolling off unreachably.
							libReadViewTabBarDecoration.wrapReadViewMenuOverflow(tmpReadView, libTabBar);
						}
					}
					catch (pReadTabError)
					{
						this.pict.log.error(`Read-view tab-bar decoration setup failed: ${pReadTabError.message || pReadTabError}`);
					}

					// FK child-list tabs — register one ChildListTab view instance per (parent, child, FK)
					// relationship the connection graph surfaces, so the read view's { Type: 'View' } child-list
					// tabs (emitted by MasterList-Builder) resolve. Registered from the full generated set (not
					// just the seed defaults) so a GUI-enabled child list always has its view ready.
					try
					{
						const tmpEntityConnections = libConnectionsGenerated.EntityConnections || {};
						Object.keys(tmpEntityConnections).forEach((pEntity) =>
						{
							(tmpEntityConnections[pEntity].ChildLists || []).forEach((pChild) =>
							{
								const tmpViewID = libMasterListBuilder.childListViewId(pEntity, pChild);
								if (this.pict.views[tmpViewID]) { return; }
								this.pict.addView(tmpViewID, Object.assign({}, libChildListTab.default_configuration,
									{
										ViewIdentifier: tmpViewID,
										ParentRecordSet: pEntity,
										ChildRecordSet: pChild.RecordSet,
										FilterField: pChild.FilterField,
										BaseField: `ID${ pEntity }`,
										DisplayField: pChild.DisplayField,
										Columns: pChild.Columns,
										ChipFields: pChild.ChipFields,
										Title: pChild.Title
									}), libChildListTab);
							});
						});
					}
					catch (pChildListError)
					{
						this.pict.log.error(`Child-list tab registration failed: ${pChildListError.message || pChildListError}`);
					}

					// Bespoke "view" read tabs (Type:'View' tabs pointing at a hand-written view rather than a
					// ChildListTab). SpecSetMaterials shows the materials transitively reachable from a Test
					// Specification Set's specs; MaterialSpecSets is its reciprocal — the specification sets a
					// material's specs belong to — see Headlight-MasterLists ViewTabs on TestSpecificationSet / Material.
					try
					{
						if (!this.pict.views['SpecSetMaterials'])
						{
							this.pict.addView('SpecSetMaterials', Object.assign({}, libSpecSetMaterialsTab.default_configuration), libSpecSetMaterialsTab);
						}
						if (!this.pict.views['MaterialSpecSets'])
						{
							this.pict.addView('MaterialSpecSets', Object.assign({}, libMaterialSpecSetsTab.default_configuration), libMaterialSpecSetsTab);
						}
						// The Organization view's Split-layout "Addresses" tab: a master/detail editor for the org's
						// OrganizationAddress children (click a row to edit its form in the right panel).
						if (!this.pict.views['OrganizationAddresses'])
						{
							this.pict.addView('OrganizationAddresses', Object.assign({}, libOrganizationAddressTab.default_configuration), libOrganizationAddressTab);
						}
						// The Material↔Organization relationship tab (Material read view's "Organizations", and the
						// reciprocal Organization read view's "Materials"): a RecordSet-AssociationEditor subclass
						// that adds an inline date editor for each relationship's ExpirationDate (set / update /
						// backfill / clear). The association is symmetric — its ExpirationDate JoinEditableField
						// applies to both sides — so register the subclass for each anchor recordset. Pre-registered
						// under the hash the read view derives for the tab so it is reused instead of the plain base
						// editor (RecordSet-Read._buildValidTabs only creates one when absent).
						[ 'Material', 'Organization' ].forEach((pThisRecordSet) =>
						{
							const tmpEditorHash = `RSP-AssocEditor-${ pThisRecordSet }-MaterialOrganization`;
							if (!this.pict.views[tmpEditorHash])
							{
								this.pict.addView(tmpEditorHash,
									Object.assign({}, libMaterialOrgAssociationEditor.default_configuration,
										{
											ViewIdentifier: tmpEditorHash,
											AssociationHash: 'MaterialOrganization',
											ThisRecordSet: pThisRecordSet,
											PickerMode: 'single',
										}), libMaterialOrgAssociationEditor);
							}
						});
					}
					catch (pViewTabError)
					{
						this.pict.log.error(`View-tab registration failed: ${pViewTabError.message || pViewTabError}`);
					}

					// Record preview cards: register the config-driven mini-card layouts so every foreign-key
					// reference to these entities (Customer, Project, Contract, Organization, User, …) renders
					// as a clickable popover card in list + read views; association-row names pick them up too.
					try
					{
						const tmpCardManager = this.pict.providers.RecordSetCardManager;
						if (tmpCardManager)
						{
							Object.keys(libPlatformRecordCards.RECORD_CARDS).forEach((pKey) =>
							{
								tmpCardManager.registerCard(pKey, libPlatformRecordCards.RECORD_CARDS[pKey]);
							});
							// Rich cards (the Data Explorer) checkbox-render schema booleans — give the card manager a
							// way to resolve an entity's column types through the authenticated REST client.
							tmpCardManager.SchemaSource = (pEntity, fCallback) => this.pict.fable.HeadlightRestClient.getJSON(`${pEntity}/Schema`, fCallback);
						}
					}
					catch (pRecordCardError)
					{
						this.pict.log.error(`Record card registration failed: ${pRecordCardError.message || pRecordCardError}`);
					}

					// Every route (application + PSRS) is now registered. Resolve the
					// current browser hash exactly once so a deep-linked route loads
					// directly, falling back to the default route only when no hash is
					// present — the initial route is never rendered ahead of it.
					this.pict.providers.PlatformConfigurationRouter.resolveCurrentRoute();
				}
			}
			catch (error)
			{
				this.pict.log.error(`Error during onAfterLoginAsync: ${error.message || error}`);
			}
			finally
			{
				fCallback(pError);
			}
		});
	}

	/**
	 * Show the user profile panel
	 */
	showProfile()
	{
		this.pict.views.HeadlightPanels.handlePanelToggle('Profile', 'open');
	}

	/**
	 * Update the user profile display in the navbar
	 */
	_updateUserProfile()
	{
		const session = this.AppData.CurrentUser;
		if (session?.LoggedIn)
		{
			let userHash = session.LoginID;
			if (!userHash)
			{
				userHash = session.Email;
			}
			if (!userHash)
			{
				userHash = `${session.NameFirst} ${session.NameLast}`;
			}
			if (!userHash)
			{
				userHash = 'no user name';
			}
			const userColor = this.pict.providers.UtilityProvider.stringToHSLColor(userHash, 75, 50);
			this.pict.ContentAssignment.setAttribute('#Header_ProfileIcon', 'style', `color: ${userColor};`);
			this.pict.ContentAssignment.assignContent('#Header_ProfileName', `${session.NameFirst.charAt(0)}${session.NameLast.charAt(0)}`);
		}
		else
		{
			this.pict.ContentAssignment.setAttribute('#Header_ProfileIcon', 'style', '');
			this.pict.ContentAssignment.assignContent('#Header_ProfileName', 'Profile');
		}
		this._syncSuperUserShell();
	}

	/**
	 * Reflect the level-5 super-user state in the app chrome: show the "Level 5" badge, hide the standard top-nav
	 * topic links, and reveal the two super-user destinations (Manage Customers + Grant Access). Runs on every
	 * profile update (login + user switch). UI only — the server stays authoritative. The single level check is
	 * _isSuperUser() (kept in one place, per the forward-compatibility note).
	 */
	_syncSuperUserShell()
	{
		if (typeof document === 'undefined') { return; }
		const tmpSuper = this._isSuperUser();
		const tmpBadge = document.getElementById('Header_SuperUserBadge');
		if (tmpBadge) { tmpBadge.classList.toggle('is-hidden', !tmpSuper); }
		document.querySelectorAll('.pc-topnav-standard').forEach((pElement) => pElement.classList.toggle('is-hidden', tmpSuper));
		document.querySelectorAll('.pc-topnav-super').forEach((pElement) => pElement.classList.toggle('is-hidden', !tmpSuper));
		// Customer Support is a top-level destination for anyone who can use the Support console (super-users +
		// Support-Agent / Support-Manage holders) — not just level-5, so support staff get it prominently too.
		const tmpEP = this.fable.EffectivePermissionsService;
		const tmpCanSupport = tmpSuper || !tmpEP || (typeof tmpEP.canHash === 'function' && (tmpEP.canHash('Support-Agent') || tmpEP.canHash('Support-Manage') || tmpEP.canHash('Document-Update')));
		document.querySelectorAll('.pc-topnav-support').forEach((pElement) => pElement.classList.toggle('is-hidden', !tmpCanSupport));
	}

	onInteractiveLogin()
	{
		// headlight-view-login already calls PictApplication.loginAsync() on a successful
		// authenticate, and THEN calls this onInteractiveLogin() hook. Calling loginAsync()
		// again here would run the entire login -> data-load -> route-resolve cycle a SECOND
		// time; the second cycle's main-viewport render (Empty) lands after resolveCurrentRoute
		// and blanks the screen. So this hook only paints a loading splash while that single
		// login cycle runs — route resolution is owned by onAfterLoginAsync -> resolveCurrentRoute(),
		// which honors the deep-linked hash.
		this.pict.ContentAssignment.assignContent('#PlatformConfiguration-Application-Container', `<div class="is-loading-splash"><h1 class="title">Loading...</h1></div>`);
	}

	/**
	 * Run the post-login setup once per sign-in. Called from BOTH onAfterInitializeAsync (page load when already
	 * signed in, not awaited) and onAfterLoginAsync (every login pass, awaited before the route resolves). Every
	 * caller gets the SAME run, so the login pass waits for the setup already in flight: returning early instead let
	 * the first page paint before the nav permission gate was installed, listing entries the user can't open.
	 * Re-armed on logout (onAfterLoginAsync's not-logged-in branch).
	 * @return {Promise<void>}
	 */
	onAfterLogin()
	{
		if (!this._afterLoginPromise)
		{
			this._afterLoginPromise = this._runAfterLogin();
		}
		return this._afterLoginPromise;
	}

	/**
	 * The post-login setup itself; reached only through onAfterLogin().
	 * @return {Promise<void>}
	 */
	async _runAfterLogin()
	{
		this._updateUserProfile();

		// Permission-expression layer (Phase 2): resolve the current user's capability map ONCE, before the
		// recordset configs load + the nav gate installs below, so create buttons + nav entries can be gated
		// synchronously. Fail-open on error (the service leaves its cache un-ready → gates show everything);
		// the server stays authoritative regardless.
		const tmpEffectivePermissions = this.fable.EffectivePermissionsService;
		const tmpCurrentUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		if (tmpEffectivePermissions && tmpCurrentUser.UserID)
		{
			try
			{
				await tmpEffectivePermissions.loadCurrentUserAsync(tmpCurrentUser.UserID, tmpCurrentUser.CustomerID);
			}
			catch (pError)
			{
				this.pict.log.warn(`Could not resolve current-user capabilities for UI gating: ${pError && pError.message ? pError.message : pError}`);
			}
		}

		if (!(await this._microAppAccessAllowedAsync()))
		{
			return;
		}

		// Work out which inspections belong to this session BEFORE any screen fetches, so a field user
		// never sees the whole program paint and then narrow. Never fatal: the service fails closed on
		// its own and reports it through the scope object.
		const tmpInspectionScope = this.pict.providers.InspectionScope;
		if (tmpInspectionScope)
		{
			try { await tmpInspectionScope.resolveAsync(); }
			catch (pError) { this.pict.log.warn(`Could not resolve the inspection scope: ${ pError && pError.message ? pError.message : pError }`); }
		}

		// Stamp the list "+ New" create-button gate onto the LIVE record-set configs. The providers are
		// registered from `fable.settings.DefaultRecordSetConfigurations` during MetaController init — BEFORE the
		// effective-permissions map is available — and each config is COPIED at registration, so mutating the
		// source options array (below) never reaches the live providers. The list title template reads
		// `Record.RecordSetConfiguration.RecordSetListShowCreateButton`, which resolves to the MetaController's
		// `recordSetProviderConfigurations[<RecordSet>]` (getRecordSetConfiguration). Fill in only the configs that
		// have NOT already decided the flag (a hand-written literal like Organization, or the master-list registry,
		// sets an explicit true/false — honor those); everything else is gated by can(entity,'Create').
		const tmpRecordSetService = this.pict.services && this.pict.services.PictSectionRecordSet;
		if (tmpEffectivePermissions && tmpRecordSetService && tmpRecordSetService.recordSetProviderConfigurations)
		{
			Object.keys(tmpRecordSetService.recordSetProviderConfigurations).forEach((pRecordSet) =>
			{
				const tmpConfig = tmpRecordSetService.recordSetProviderConfigurations[pRecordSet];
				if (!tmpConfig || typeof tmpConfig.RecordSetListShowCreateButton === 'boolean')
				{
					return;
				}
				tmpConfig.RecordSetListShowCreateButton = tmpEffectivePermissions.can(pRecordSet, 'Create');
			});
		}

		// Form-mode UI elements (Save/Solver/Download buttons + Brand/Approval-Actions
		// toggle) are excluded at the HeadlightPanels configuration level via the
		// NavBar override passed to setPanelConfiguration() above. This app runs in
		// application mode, not document/form mode, so those buttons are not rendered.

		// Load recordset configurations for PSRS entities. Route resolution is
		// deferred to onAfterLoginAsync (after the PSRS routes are registered), so
		// no navigation happens here — that is what kept the default route from
		// rendering ahead of a deep-linked route.
		if (Array.isArray(this.options.DefaultRecordSetConfigurations))
		{
			// Whole-row click opens the record's View (with a "⋯" actions menu revealed on row hover) across
			// every config-app list. This is opt-in per record set via the recordset module's
			// RowClickOpensRecord flag; set it here rather than on each of the ~20 literals so they all get the
			// interaction. A config can still opt out by declaring RowClickOpensRecord:false in its own literal.
			this.options.DefaultRecordSetConfigurations.forEach((pRecordSetConfiguration) =>
			{
				if (!pRecordSetConfiguration || typeof pRecordSetConfiguration !== 'object')
				{
					return;
				}
				if (!('RowClickOpensRecord' in pRecordSetConfiguration))
				{
					pRecordSetConfiguration.RowClickOpensRecord = true;
				}
				// Permission-expression layer (Phase 2): gate the list "New" button by the current user's
				// create right. The button lives in ImprovedTabularRecordSetProvider's title template, which
				// reads this flag off the entity's own config (Record.RecordSetConfiguration) — reliable
				// regardless of route timing. An explicit `false` in a literal is honored as an opt-out.
				if (tmpEffectivePermissions && pRecordSetConfiguration.RecordSet)
				{
					pRecordSetConfiguration.RecordSetListShowCreateButton = (pRecordSetConfiguration.RecordSetListShowCreateButton === false)
						? false
						: tmpEffectivePermissions.can(pRecordSetConfiguration.RecordSet, 'Create');
				}
			});
			this.pict.services.PictSectionRecordSet.loadRecordSetConfigurationArray(this.options.DefaultRecordSetConfigurations);
		}

		// ContractType / ProjectType editor: bind the PSRS providers through the
		// Settings adapter so list-shaped Settings keys round-trip as friendly
		// newline-joined text in `_DisplaySettings`. Both the manifest descriptors
		// and the user-facing inputs see the text form; the API still sees the
		// canonical Settings.* shape.
		this._installSettingsAdapterForRecordSets([ 'ContractType', 'ProjectType' ]);
		// New User creation: the generic form's "Login Password" is a raw column that POSTing does NOT turn into a
		// login credential — only POST /1.0/Password does (what "Reset Password" calls). Wire the create so it makes
		// that follow-up call automatically, so a user can sign in with the password the admin set at creation.
		this._installUserPasswordOnCreate();
		// Records with a photo field: the photo route needs a saved record, so upload a held photo after the save.
		this._installPhotoUploadAfterSave([ 'Location' ]);

		// Bring the in-app documentation online (loads the sidebar, topics, and renders the
		// doc browser into the help panel). Safe to call more than once — it self-guards.
		this._initializeHelpDocumentation();

		// bind ⌘K (launcher) + F1 (help) and dock the
		// navigation rail if the user's style is Sidebar. Self-guards against re-running.
		// Load the customer's enabled Modules (CustomerModuleJoin) AND install the nav permission gate BEFORE the
		// navigation shell renders, so the nav's FIRST paint is already filtered — no flash of hidden/ungated
		// entries before capability + module gating catches up. The current user's capability map was resolved
		// above (loadCurrentUserAsync), so EffectivePermissionsService is ready here; modules fail closed until
		// loaded; and the shell then paints the already-gated graph.
		await this.loadCustomerModulesAsync();
		this._applyCustomerNavGate();
		// Dock the rail / bind the launcher and render the (now-gated) navigation shell.
		this._initializeNavigationShell();
		// KPI counts that feed the nav-page jumbotrons (non-blocking).
		this.loadDashboardStatsAsync();
	}

	/**
	 * @returns {*} the registered inline documentation provider, or null if not present.
	 */
	get _helpProvider()
	{
		return (this.pict.providers && this.pict.providers['Pict-InlineDocumentation']) || null;
	}

	/**
	 * Where this app's documentation comes from, what it opens on, and what it calls itself.
	 *
	 * This exists because a micro app built on this one (pict-microapp) is a DIFFERENT PRODUCT to a
	 * different audience: a bridge inspector opening the Academy should not land on "Platform
	 * Configuration — Overview" and a table of contents about Pay Items and Customer Provisioning.
	 * Every place that used to hard-code 'docs/', 'overview.md' or "Headlight Academy" reads this
	 * instead, so a subclass re-points the whole documentation surface by overriding one getter.
	 *
	 * ContentHashPrefix namespaces this app's AcademyContent rows. AcademyContent has no application
	 * column, so the Hash is the only place a per-app dimension can live — two apps sharing a prefix
	 * would overwrite each other's docs on push.
	 *
	 * @returns {{DocsBaseURL:String, TopicsURL:String, LandingArticle:String, ContentHashPrefix:String, Title:String, Subtitle:String}}
	 */
	get documentationProfile()
	{
		return (
			{
				DocsBaseURL: 'docs/',
				TopicsURL: 'docs/_topics.json',
				LandingArticle: 'overview.md',
				ContentHashPrefix: 'Doc-PlatformConfig-',
				Title: 'Headlight Academy',
				Subtitle: 'Guides and reference for configuring your platform.',
			});
	}

	/**
	 * Initialize the in-app documentation browser once. Loads the sidebar and topic map named by
	 * documentationProfile, renders the browser into the help panel container, and shows the landing
	 * article. Relative URLs resolve against the app's served path, so this works regardless of the
	 * mount point.
	 */
	_initializeHelpDocumentation()
	{
		const tmpProvider = this._helpProvider;
		if (!tmpProvider || this._helpInitialized)
		{
			return;
		}
		this._helpInitialized = true;
		// Published so the Academy view and provider read the same profile the help panel does, without
		// either of them reaching back into the application class.
		this.AppData.DocumentationProfile = this.documentationProfile;
		this._createHelpPanel();

		// In-place documentation editing persists to the AcademyContent Meadow entity, keyed by a stable
		// per-doc Hash. AcademyContent is session-scoped — the server owns the tenant/default layering — so
		// a platform (Customer 1) session edits the default and any other customer edits their own override.
		// Editing is gated on the AcademyContent-Update capability (Permission-Catalog.json / entitlements).
		const tmpCanEdit = !!(this.fable.EffectivePermissionsService && this.fable.EffectivePermissionsService.can('AcademyContent', 'Update'));

		// Layer AcademyContent over the shipped static docs/: a doc resolves to its AcademyContent row
		// (customer override or platform default) when one exists, else the static file. Wrap the provider's
		// document fetch so every load path (route sync, nav clicks, in-doc links) goes through the layering.
		this._installAcademyContentDocLoader(tmpProvider);

		tmpProvider.initializeDocumentation(
			{
				DocsBaseURL: this.documentationProfile.DocsBaseURL,
				TopicsURL: this.documentationProfile.TopicsURL,
				ContainerAddress: '#InlineDoc-Container',
				EditEnabled: tmpCanEdit,
				// Document content editing persists to AcademyContent (see below). The topic↔route binding
				// editor is held back for now: topics still load from the static _topics.json, so enabling it
				// would let bindings be saved but appear to revert on reload. onTopicsSave is wired ahead of
				// layering the topics LOAD from AcademyContent (the follow-up that turns this on).
				TopicManagerEnabled: false,
				onSave: (pSaveData, fCallback) => this._saveAcademyDoc(pSaveData, fCallback),
				onTopicsSave: (pTopics, fCallback) => this._saveAcademyTopics(pTopics, fCallback),
			},
			() =>
			{
				// Load the persisted route→topic bindings before the first route sync so a route-bound topic
				// resolves on open. Show the overview by default, then sync to the current route if the panel is
				// open. Skip the overview preload on an AcademyContent-help route so its async fetch can't land over
				// the doc that updateHelpForRoute is about to drive into the panel (updateHelpForRoute shows a
				// loading state immediately, so the panel is never blank).
				this._loadRouteBindingsAsync(() =>
				{
					const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
					if (!this._helpHashForRoute(tmpHash.replace(/^#/, '') || '/'))
					{
						tmpProvider.loadDocument(this.documentationProfile.LandingArticle);
					}
					this.updateHelpForRoute();
				});
			});
	}

	/** The HeadlightRestClient (Meadow REST over the /1.0 API) used for AcademyContent persistence. */
	get _academyRestClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/** Stable AcademyContent Hash for a documentation path — namespaced so help docs never collide with
	 *  other AcademyContent producers (e.g. the Sample Plan MSM docs) or with another app's doc set. */
	_academyDocHash(pPath)
	{
		return `${ this.documentationProfile.ContentHashPrefix }${ String(pPath || '').replace(/^\/+/, '') }`;
	}

	/**
	 * Read a doc's markdown by Hash, or null when no row exists. Callback: (error, markdown|null).
	 *
	 * Goes through the bespoke `PlatformDocument/:Hash` endpoint — the SAME route the F1/? area docs use —
	 * which reads the canonical customer-1 copy plus the caller's own override and prefers the override,
	 * for any authenticated session regardless of tenant.
	 *
	 * This used to be a plain Meadow read (`AcademyContents/FilteredTo/...`), which was wrong twice over
	 * and failed silently both times — a doc that will not load falls back to the shipped static file, so
	 * the page still renders and nothing looks broken.
	 *
	 * First, that route is TENANT-SCOPED. Measured on QA as a customer-117 session: an exact-Hash read
	 * returns only that customer's own rows, so Headlight's canonical customer-1 documentation could
	 * never reach a tenant through it, no matter what was pushed. Every logged-in user is supposed to
	 * get help; only this endpoint delivers that, and it is already proven to — PlatformDocumentIndex
	 * serves customer-1 rows to customer-117 sessions today.
	 *
	 * Second, the old hashes carried a '/' ('PlatformConfigDoc/overview.md'). URL-encoded into a
	 * FilteredTo segment the %2F breaks the filter, and the server answers HTTP 200 with
	 * {Error:'UNAUTHORIZED ACCESS IS NOT ALLOWED', ErrorCode:405} — the same shape it uses to refuse a
	 * broad table read. So every academy doc lookup failed on the hash alone. Hence 'Doc-<App>-' with no
	 * separator that needs escaping.
	 *
	 * The endpoint is namespace-gated to hashes beginning 'Doc-', which is why the app doc prefixes are
	 * 'Doc-<App>-' — and why they carry no '/', since the Hash travels in a path parameter.
	 */
	_loadAcademyDoc(pHash, fCallback)
	{
		const tmpClient = this._academyRestClient;
		if (!tmpClient) { return fCallback(null, null); }
		tmpClient.getJSON(`PlatformDocument/${ encodeURIComponent(pHash) }`, (pError, pBody) =>
		{
			if (pError) { return fCallback(pError, null); }
			// The endpoint answers {} when no row exists for the hash — that is "fall back to the file".
			const tmpContent = (pBody && typeof pBody === 'object' && !Array.isArray(pBody)) ? pBody.Content : null;
			return fCallback(null, (typeof tmpContent === 'string' && tmpContent.length) ? tmpContent : null);
		});
	}

	/**
	 * Wrap the inline-doc provider's _fetchDocument so an AcademyContent doc (override/default) wins over
	 * the shipped static file, with the static file as the fallback. Idempotent. Only .md documents are
	 * layered; images and other assets pass straight through to the static server.
	 */
	_installAcademyContentDocLoader(pProvider)
	{
		if (pProvider._academyLoaderInstalled) { return; }
		pProvider._academyLoaderInstalled = true;
		const tmpBaseFetch = pProvider._fetchDocument.bind(pProvider);
		pProvider._fetchDocument = (pURL, fCallback) =>
		{
			const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
			if (pProvider._ContentCache[pURL]) { return tmpCb(null, pProvider._ContentCache[pURL].html); }
			if (!/\.md($|[?#])/.test(String(pURL || ''))) { return tmpBaseFetch(pURL, tmpCb); }
			const tmpPath = String(pURL || '').replace(/^docs\//, '');
			this._loadAcademyDoc(this._academyDocHash(tmpPath), (pError, pMarkdown) =>
			{
				if (pError || pMarkdown == null) { return tmpBaseFetch(pURL, tmpCb); }
				const tmpHTML = pProvider._ContentProvider.parseMarkdown(pMarkdown, pProvider._createLinkResolver(), pProvider._createImageResolver(pURL), pProvider._VocabularyResolver);
				pProvider._ContentCache[pURL] = { html: tmpHTML, markdown: pMarkdown };
				return tmpCb(null, tmpHTML);
			});
		};

		// Layer the SIDEBAR (TOC) and TOPICS (route→doc map) the same way as bodies: an AcademyContent row
		// (PlatformConfigDoc/_sidebar.md, PlatformConfigDoc/_topics.json — session-scoped, so customer-1 = platform
		// default) wins over the shipped static file. This makes the whole doc set — structure included — publishable
		// to a tenant/platform purely as data (an hldi comprehension of AcademyContent upserts), no app deploy. Falls
		// back to the static file whenever no row exists (so local dev + un-seeded environments are unchanged).
		const tmpBaseSidebar = pProvider._loadSidebar.bind(pProvider);
		pProvider._loadSidebar = (fCallback) =>
		{
			const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
			this._loadAcademyDoc(this._academyDocHash('_sidebar.md'), (pError, pMarkdown) =>
			{
				if (pError || pMarkdown == null) { return tmpBaseSidebar(tmpCb); }
				try { pProvider.pict.AppData.InlineDocumentation.SidebarGroups = pProvider._parseSidebarMarkdown(pMarkdown); }
				catch (pParseError) { return tmpBaseSidebar(tmpCb); }
				return tmpCb();
			});
		};
		const tmpBaseTopics = pProvider._loadTopics.bind(pProvider);
		pProvider._loadTopics = (pTopicsURL, fCallback) =>
		{
			const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
			this._loadAcademyDoc(this._academyDocHash('_topics.json'), (pError, pJSON) =>
			{
				if (pError || pJSON == null) { return tmpBaseTopics(pTopicsURL, tmpCb); }
				let tmpParsed = null;
				try { tmpParsed = JSON.parse(pJSON); }
				catch (pParseError) { return tmpBaseTopics(pTopicsURL, tmpCb); }
				if (tmpParsed && (typeof tmpParsed === 'object') && !Array.isArray(tmpParsed))
				{
					pProvider.pict.AppData.InlineDocumentation.Topics = tmpParsed;
					return tmpCb();
				}
				return tmpBaseTopics(pTopicsURL, tmpCb);
			});
		};
	}

	/**
	 * Upsert an AcademyContent row (by Hash) with the given content, as THIS session's customer: update the
	 * caller's own row when they already have one, else create it (the server stamps IDCustomer).
	 *
	 * WHOSE ROW IS THIS? is answered by PlatformDocument, not by a Meadow read, and that is the whole point.
	 * PlatformDocument already resolves canonical-vs-override and now reports the IDCustomer and row id of
	 * whichever copy it served. Asking Meadow instead means reading by Hash, which fetches the canonical
	 * customer-1 row the caller does not own; MyCustomer is a post-fetch per-record assertion, so it refuses
	 * the ENTIRE read — and a tenant could never author their first override. Interpreting that refusal is not
	 * a way out: it says "a row you do not own matched", which cannot distinguish "only canonical exists" from
	 * "canonical plus your override exists", so treating it as "no row" would POST a duplicate override on
	 * every edit after the first.
	 *
	 * If the endpoint answers without an IDCustomer (an API older than this change), fall back to the Meadow
	 * probe. That path still works for a caller editing a doc only they hold, and fails loudly rather than
	 * silently duplicating rows.
	 */
	_upsertAcademyContent(pHash, pTitle, pContent, fCallback)
	{
		const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
		const tmpClient = this._academyRestClient;
		if (!tmpClient) { return tmpCb('Documentation store unavailable.'); }
		const tmpCustomerID = Number((this.AppData.CurrentUser && this.AppData.CurrentUser.CustomerID) || 0);

		tmpClient.getJSON(`PlatformDocument/${ encodeURIComponent(pHash) }`, (pError, pBody) =>
		{
			if (pError) { return tmpCb(pError); }
			const tmpServed = (pBody && typeof pBody === 'object' && !Array.isArray(pBody)) ? pBody : {};
			const tmpServedCustomer = Number(tmpServed.IDCustomer || 0);

			// Served nothing at all — no canonical, no override. Straight create.
			if (!tmpServed.Hash) { return this._createAcademyContent(pHash, pTitle, pContent, tmpCb); }

			// Served a row but the API did not say whose: older API, use the legacy probe.
			if (!tmpServedCustomer) { return this._upsertAcademyContentViaMeadow(pHash, pTitle, pContent, tmpCb); }

			// The copy on screen is the caller's own — update it in place.
			if (tmpCustomerID && (tmpServedCustomer === tmpCustomerID) && tmpServed.IDAcademyContent)
			{
				return tmpClient.putJSON('AcademyContent',
					{ IDAcademyContent: tmpServed.IDAcademyContent, Hash: pHash, Title: pTitle || tmpServed.Title, Content: pContent },
					(pPutError) => tmpCb(pPutError || null));
			}

			// The copy on screen belongs to someone else (the canonical customer-1 doc) — author an override.
			return this._createAcademyContent(pHash, pTitle, pContent, tmpCb);
		});
	}

	/** Create this customer's own row for a hash. The server stamps IDCustomer from the session. */
	_createAcademyContent(pHash, pTitle, pContent, fCallback)
	{
		const tmpClient = this._academyRestClient;
		return tmpClient.postJSON('AcademyContent', { Hash: pHash, Title: pTitle || pHash, Content: pContent },
			(pPostError) => fCallback(pPostError || null));
	}

	/**
	 * The pre-PlatformDocument upsert, kept only as the fallback for an API that does not report IDCustomer.
	 * Reads by Hash and matches the caller's own row; refused outright when a row the caller does not own is
	 * in range, which is exactly the limitation the primary path exists to avoid.
	 */
	_upsertAcademyContentViaMeadow(pHash, pTitle, pContent, fCallback)
	{
		const tmpClient = this._academyRestClient;
		const tmpCustomerID = Number((this.AppData.CurrentUser && this.AppData.CurrentUser.CustomerID) || 0);
		tmpClient.getJSON(`AcademyContents/FilteredTo/FBV~Hash~EQ~${ encodeURIComponent(pHash) }`, (pError, pBody) =>
		{
			if (pError) { return fCallback(pError); }
			const tmpRows = Array.isArray(pBody) ? pBody : [];
			const tmpOwnRow = tmpRows.find((pRow) => Number(pRow.IDCustomer) === tmpCustomerID);
			if (tmpOwnRow)
			{
				const tmpPayload = Object.assign({}, tmpOwnRow, { Title: pTitle || tmpOwnRow.Title, Content: pContent });
				return tmpClient.putJSON('AcademyContent', tmpPayload, (pPutError) => fCallback(pPutError || null));
			}
			return this._createAcademyContent(pHash, pTitle, pContent, fCallback);
		});
	}

	/** onSave handler: persist an edited doc's markdown to AcademyContent (the provider re-displays it). */
	_saveAcademyDoc(pSaveData, fCallback)
	{
		const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
		const tmpPath = (pSaveData && pSaveData.Path) ? pSaveData.Path : '';
		if (!tmpPath) { return tmpCb('No document path to save.'); }
		this._upsertAcademyContent(this._academyDocHash(tmpPath), tmpPath, (pSaveData && pSaveData.Content) || '', tmpCb);
	}

	/** onTopicsSave handler: persist the topic ↔ route bindings to AcademyContent as a JSON doc. */
	_saveAcademyTopics(pTopics, fCallback)
	{
		const tmpCb = (typeof fCallback === 'function') ? fCallback : () => {};
		let tmpJSON = '';
		try { tmpJSON = JSON.stringify(pTopics || {}); }
		catch (pError) { return tmpCb('Could not serialize topics for save.'); }
		this._upsertAcademyContent(this._academyDocHash('_topics.json'), 'Documentation Topics', tmpJSON, tmpCb);
	}

	/**
	 * Attach pict-section-modal's resizable/collapsible panel behavior to the help dock
	 * (drag handle + collapse tab + width persistence). Idempotent. The modal view is
	 * registered by the inline-documentation provider as 'Pict-Section-Modal'.
	 */
	_createHelpPanel()
	{
		if (this._helpPanelHandle)
		{
			return;
		}
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.panel !== 'function')
		{
			this.pict.log.warn('Pict-Section-Modal panel API unavailable — help panel not created.');
			return;
		}
		this._helpPanelHandle = tmpModal.panel('#PlatformConfig-HelpPanel',
			{
				position: 'right',
				width: 440,
				minWidth: 300,
				maxWidth: 820,
				collapsible: true,
				collapsed: true,
				persist: true,
				persistKey: 'platformconfig-help',
				onResize: () => { this._applyHelpPush(); },
				onToggle: (pCollapsed) =>
				{
					this._helpPanelOpen = !pCollapsed;
					if (!pCollapsed)
					{
						this.updateHelpForRoute();
					}
					this._applyHelpPush();
				},
			});
		// Always greet the user with the panel CLOSED. The modal's persist:true would otherwise restore a
		// previously-open panel — and since it opens before the deep-linked route resolves, it would show an
		// empty doc (which reads as "the documentation is broken"). Width stays persisted; only the open/closed
		// state is forced shut on load. The user opens it deliberately via Help, and it loads the live route then.
		this._helpPanelOpen = false;
		if (this._helpPanelHandle && typeof this._helpPanelHandle.collapse === 'function')
		{
			this._helpPanelHandle.collapse();
		}
	}

	/**
	 * Push the main content in by the docked help panel's width when it's open, so the panel sits beside the
	 * content instead of overlapping it. Reset to 0 when closed. Called on open/close (onToggle) + drag-resize
	 * (onResize). The panel is fixed (top→bottom on the right), so we only shift the content section horizontally.
	 */
	_applyHelpPush()
	{
		if (typeof document === 'undefined') { return; }
		const tmpWrapper = document.querySelector('.app-wrapper-container');
		if (!tmpWrapper) { return; }
		if (this._helpPanelOpen)
		{
			const tmpDock = document.getElementById('HelpDock');
			const tmpPanel = document.getElementById('PlatformConfig-HelpPanel');
			// Prefer the dock's measured width (panel + resize edge); fall back to the panel's set width.
			const tmpWidth = (tmpDock && tmpDock.offsetWidth) || (tmpPanel && (parseInt(tmpPanel.style.width, 10) || tmpPanel.offsetWidth)) || 440;
			tmpWrapper.style.marginRight = `${tmpWidth}px`;
		}
		else
		{
			tmpWrapper.style.marginRight = '';
		}
	}

	/**
	 * Open or close the documentation panel (expand/collapse the resizable modal panel).
	 * The panel's onToggle callback keeps `_helpPanelOpen` and the route-aware content in step.
	 *
	 * @param {boolean} [pForceOpen] - true to open, false to close; omit to toggle.
	 */
	toggleHelp(pForceOpen)
	{
		// Ensure the browser + panel exist (e.g. toggled before onAfterLogin completed).
		this._initializeHelpDocumentation();
		const tmpHandle = this._helpPanelHandle;
		if (!tmpHandle)
		{
			return;
		}
		const tmpOpen = (typeof pForceOpen === 'boolean') ? pForceOpen : !this._helpPanelOpen;
		if (tmpOpen)
		{
			tmpHandle.expand();
		}
		else
		{
			tmpHandle.collapse();
		}
	}

	/**
	 * Point the documentation panel at the topic mapped to the current route (route-aware
	 * help, _topics.json). No-op unless the browser is initialized and the panel is open,
	 * so navigation incurs no doc fetches while help is closed. Called by the router on
	 * every route change and by toggleHelp when the panel opens.
	 */
	updateHelpForRoute()
	{
		const tmpProvider = this._helpProvider;
		// _helpSuppressRouteDoc: a host feature (e.g. the Sample Plan Docs button) is driving the panel's content
		// for this open, so don't load the route's app documentation over it.
		if (this._helpSuppressRouteDoc || !tmpProvider || !this._helpInitialized || !this._helpPanelOpen)
		{
			return;
		}
		// Keep the header "Add help for this page" button in sync with the edit gate whenever the panel is live.
		this._syncAddPageHelpButton();
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
		const tmpRoute = tmpHash.replace(/^#/, '') || '/';
		// AcademyContent-managed route docs (platform help). When the route has one, drive the panel with
		// it via the displayContent bridge and stop — the file-based _topics.json flow owns every other route.
		if (this._showAcademyHelpForRoute(tmpRoute))
		{
			return;
		}
		// If a host feature left its own content in the shared panel (e.g. the Sample Plan's sampling-manual docs),
		// restore the app documentation first — navigateToRoute would otherwise leave that foreign content in place
		// when the route maps to no topic, so "?" on another page would keep showing the sampling manual.
		if (this._helpShowingForeignDocs)
		{
			this._helpShowingForeignDocs = false;
			tmpProvider.loadDocument(this.documentationProfile.LandingArticle);
		}
		// navigateToRoute leaves the current doc in place when no topic matches the route.
		tmpProvider.navigateToRoute(tmpRoute);
	}

	// ── AcademyContent-managed help (platform docs) ────────────────────────────
	// A parallel to the file-based _topics.json help: routes in the PlatformDocs registry resolve to a doc stored
	// in the AcademyContent table (Hash-keyed, seeded via source/help/PlatformDocs-Seed.js) rather than a file.
	// We fetch by Hash and inject into the shared inline-doc panel with the same displayContent bridge the Sample
	// Plan editor uses — reusing the panel, its markdown parser, and its open/close host with no library change.

	/**
	 * If the route has an AcademyContent-managed help doc, render it into the panel and return true; else false
	 * (so updateHelpForRoute falls through to the file-based route help). Called only while the panel is open.
	 * @param {string} pRoute @returns {boolean}
	 */
	_showAcademyHelpForRoute(pRoute)
	{
		const tmpHash = this._helpHashForRoute(pRoute);
		if (!tmpHash)
		{
			return false;
		}
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function'))
		{
			return false;
		}
		this._renderPlatformHelp(tmpHash);
		return true;
	}

	/**
	 * Nav-link click target (from the in-panel doc nav): show a specific platform help doc by Hash.
	 * Referenced from injected HTML as `_Pict.PictApplication.openPlatformHelpDoc('<hash>')`.
	 * @param {string} pHash
	 */
	openPlatformHelpDoc(pHash)
	{
		this.toggleHelp(true);
		this._renderPlatformHelp(pHash);
	}

	/**
	 * Fetch a platform help doc by Hash from AcademyContent and inject it (nav bar + parsed markdown) into the
	 * shared documentation panel. Shows a loading state immediately, then swaps in the content when it resolves.
	 * Sets `_helpShowingForeignDocs` so the file-based flow restores the app docs when the user leaves this route.
	 * @param {string} pHash
	 */
	_renderPlatformHelp(pHash)
	{
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function'))
		{
			return;
		}
		this._helpShowingForeignDocs = true;
		this._activePlatformHelpHash = pHash;
		// Make sure the inline-doc content container exists before we write into it. On a platform-doc route the
		// module's loadDocument (which is what normally renders the content view's template) is intentionally
		// skipped, so on a FRESH panel open #InlineDoc-Content-Body has not been created yet and displayContent
		// would land nowhere — leaving the panel blank. Render the content view once to create it.
		if ((typeof document !== 'undefined') && !document.getElementById('InlineDoc-Content-Body') && (typeof tmpContentView.render === 'function'))
		{
			tmpContentView.render();
		}
		tmpContentView.displayContent(this._platformHelpHTML(pHash, '<p class="platform-help-loading">Loading…</p>'));
		// Fetch the doc AND the area's topic index together, so the nav can include topics the static registry
		// doesn't know (customer-authored / newly created). Both are cached, so this is instant after the first hit.
		const tmpArea = libPlatformDocs.areaFromHash(pHash);
		Promise.all([ this._fetchAcademyDoc(pHash), this._fetchAreaIndex(tmpArea) ]).then((pResults) =>
		{
			// Ignore a stale fetch (the user clicked another doc / left the route) or a panel that has since closed.
			if ((this._activePlatformHelpHash !== pHash) || !this._helpPanelOpen)
			{
				return;
			}
			const tmpDoc = pResults[0];
			// The article body couldn't be resolved (PlatformDocument returned no Content for this Hash — a
			// tenant that can't read the canonical customer-1 doc, or a topic with no row yet). Show a calm,
			// cause-agnostic end-user empty state rather than the old developer "seed the platform docs"
			// instruction; the sibling-topic nav above still lets the reader jump to any article that resolved.
			const tmpMarkdown = (tmpDoc && tmpDoc.Content)
				? tmpDoc.Content
				: '_This help topic isn’t available right now._';
			// displayContent (pict-section-content's view) renders the parser's KaTeX/Mermaid placeholders itself
			// once window.katex / window.mermaid are on the page (loaded in index.html) — no extra pass needed.
			tmpContentView.displayContent(this._platformHelpHTML(pHash, this._parseHelpMarkdown(tmpMarkdown)));
		});
	}

	/**
	 * Fetch + cache an area's topic index (canonical + the caller's own topics) from the PlatformDocumentIndex
	 * endpoint, so the nav can show topics the static registry lacks. Resolves an array of {Hash, Title}.
	 * @param {string} pArea @returns {Promise<Array>}
	 */
	_fetchAreaIndex(pArea)
	{
		if (!this._platformAreaIndexCache) { this._platformAreaIndexCache = {}; }
		if (!pArea) { return Promise.resolve([]); }
		if (this._platformAreaIndexCache[pArea] !== undefined) { return Promise.resolve(this._platformAreaIndexCache[pArea]); }
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return Promise.resolve([]); }
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocumentIndex/${encodeURIComponent(pArea)}`, (pError, pBody) =>
			{
				this._platformAreaIndexCache[pArea] = (!pError && Array.isArray(pBody)) ? pBody : [];
				resolve(this._platformAreaIndexCache[pArea]);
			});
		});
	}

	/**
	 * Fetch + cache a single documentation record by Hash. Resolves the record (or null on miss/error).
	 *
	 * Reads the bespoke `PlatformDocument/:Hash` endpoint, which serves the canonical (customer-1) doc — and the
	 * caller's own override if they have authored one — to ANY authenticated session regardless of tenant, as a
	 * single `{Hash, Title, Content}`. A missing doc (or endpoint) resolves null, which the panel renders as a
	 * "not loaded yet" note.
	 * @param {string} pHash @returns {Promise<object|null>}
	 */
	_fetchAcademyDoc(pHash)
	{
		if (!this._academyHelpCache)
		{
			this._academyHelpCache = {};
		}
		if (this._academyHelpCache[pHash] !== undefined)
		{
			return Promise.resolve(this._academyHelpCache[pHash]);
		}
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient)
		{
			return Promise.resolve(null);
		}
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocument/${encodeURIComponent(pHash)}`, (pError, pBody) =>
			{
				this._academyHelpCache[pHash] = (!pError && pBody && pBody.Content) ? pBody : null;
				resolve(this._academyHelpCache[pHash]);
			});
		});
	}

	// ── AcademyContent-backed route→topic bindings (supersedes the static topics.json for platform help) ──────
	// Which help topic F1 opens on a route was historically fixed in the static registry (PlatformDocs.js Routes)
	// and the inline-doc module's docs/_topics.json. To let admins bind topics to routes AND add new route-bound
	// topics that persist, bindings live in a single AcademyContent row (Hash 'Doc-RouteBindings', Content = JSON
	// { Routes: { "<route>": "<Doc-Area-Topic hash>" } }). It layers the same way docs do — a customer-1 session
	// edits the canonical bindings, any other customer edits their own override — and resolves cross-tenant through
	// the PlatformDocument endpoint. A persisted binding wins; routes with none fall back to the static registry.

	/** Load (or reload) the resolved route→topic binding map into this._routeBindings. */
	_loadRouteBindingsAsync(fCallback)
	{
		const fDone = (typeof fCallback === 'function') ? fCallback : () => {};
		if (!this._routeBindings) { this._routeBindings = {}; }
		this._fetchAcademyDoc('Doc-RouteBindings').then((pDoc) =>
		{
			this._routeBindings = this._parseRouteBindings(pDoc && pDoc.Content);
			fDone();
		});
	}

	/** Parse a bindings-row Content string into a flat { route: hash } map (tolerant of a bare map or a { Routes } wrapper). */
	_parseRouteBindings(pContent)
	{
		if (!pContent) { return {}; }
		try
		{
			const tmpParsed = JSON.parse(pContent);
			if (tmpParsed && (typeof tmpParsed === 'object'))
			{
				const tmpMap = (tmpParsed.Routes && (typeof tmpParsed.Routes === 'object')) ? tmpParsed.Routes : tmpParsed;
				return (tmpMap && (typeof tmpMap === 'object') && !Array.isArray(tmpMap)) ? tmpMap : {};
			}
		}
		catch (pError) { /* corrupt bindings row → fall back to the static registry */ }
		return {};
	}

	/**
	 * The help-doc Hash F1 should open for a route: a persisted binding (longest-matching route prefix) wins, else
	 * the static registry. '' when neither has one (the caller then falls back to the file-based route help).
	 * @param {string} pRoute @returns {string}
	 */
	_helpHashForRoute(pRoute)
	{
		const tmpRoute = String(pRoute || '');
		const tmpBindings = this._routeBindings || {};
		let tmpHash = '';
		let tmpBest = -1;
		Object.keys(tmpBindings).forEach((pKey) =>
		{
			if (tmpBindings[pKey] && ((tmpRoute === pKey) || (tmpRoute.indexOf(pKey + '/') === 0)) && (pKey.length > tmpBest))
			{
				tmpHash = tmpBindings[pKey];
				tmpBest = pKey.length;
			}
		});
		return tmpHash || libPlatformDocs.hashForRoute(tmpRoute);
	}

	/**
	 * Persist a route→topic binding (or remove it when pHash is falsy): read-modify-write the caller's own bindings
	 * row so a customer's override keeps the canonical bindings it started from. Refreshes the cache + in-memory map.
	 * @param {string} pRoute @param {string} pHash @param {Function} fCallback (error|null)
	 */
	_bindRouteToHashAsync(pRoute, pHash, fCallback)
	{
		const fDone = (typeof fCallback === 'function') ? fCallback : () => {};
		const tmpRoute = String(pRoute || '').replace(/^#/, '');
		if (!tmpRoute) { return fDone('No route to bind.'); }
		this._fetchAcademyDoc('Doc-RouteBindings').then((pDoc) =>
		{
			const tmpMap = this._parseRouteBindings(pDoc && pDoc.Content);
			if (pHash) { tmpMap[tmpRoute] = pHash; }
			else { delete tmpMap[tmpRoute]; }
			const tmpJSON = JSON.stringify({ Routes: tmpMap });
			this._upsertAcademyContent('Doc-RouteBindings', 'Documentation Route Bindings', tmpJSON, (pError) =>
			{
				if (this._academyHelpCache) { this._academyHelpCache['Doc-RouteBindings'] = undefined; }
				this._routeBindings = tmpMap;
				fDone(pError || null);
			});
		});
	}

	/** Render markdown → HTML via the inline-doc provider's content parser (falls back to the raw string). */
	_parseHelpMarkdown(pMarkdown)
	{
		const tmpProvider = this._helpProvider;
		return (tmpProvider && tmpProvider._ContentProvider && (typeof tmpProvider._ContentProvider.parseMarkdown === 'function'))
			? tmpProvider._ContentProvider.parseMarkdown(pMarkdown)
			: pMarkdown;
	}


	/**
	 * The panel body for an import help doc: a nav bar linking every import topic (active one highlighted) above
	 * the rendered content. Hand-built HTML so the nav links carry reliable inline click handlers (matches the
	 * Sample Plan spec-index approach). A scoped <style> ships with it so it needs no global CSS.
	 * @param {string} pHash @param {string} pBodyHTML
	 */
	_platformHelpHTML(pHash, pBodyHTML)
	{
		const fEscape = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
		// Nav shows the sibling topics in the SAME feature area (not every doc across the app).
		// Nav = the curated static topics (order + friendly labels) MERGED with any discovered topics (customer-
		// authored or newly created) that the static registry doesn't know — those use their Title as the label.
		const tmpArea = libPlatformDocs.areaFromHash(pHash);
		const tmpStatic = libPlatformDocs.docsInArea(tmpArea);
		const tmpKnown = {};
		tmpStatic.forEach((pDoc) => { tmpKnown[pDoc.Hash] = true; });
		const tmpDiscovered = (this._platformAreaIndexCache && this._platformAreaIndexCache[tmpArea]) || [];
		const tmpTopics = tmpStatic.concat(tmpDiscovered
			.filter((pDoc) => pDoc && pDoc.Hash && !tmpKnown[pDoc.Hash])
			.map((pDoc) => ({ Hash: pDoc.Hash, Nav: pDoc.Title || pDoc.Hash })));
		const tmpEditMode = this._platformDocsEditMode();
		let tmpNav = tmpTopics.map((pDoc) =>
		{
			const tmpActiveClass = (pDoc.Hash === pHash) ? ' platform-help-navlink-active' : '';
			return `<a href="#" class="platform-help-navlink${tmpActiveClass}" onclick="_Pict.PictApplication.openPlatformHelpDoc('${fEscape(pDoc.Hash)}'); return false;">${fEscape(pDoc.Nav)}</a>`;
		}).join('');
		// Editors can append a new topic to the area (customer-1 admins add a canonical topic; customer admins add their own).
		if (tmpEditMode && tmpArea)
		{
			tmpNav += `<a href="#" class="platform-help-navlink" title="Add a topic to this area" onclick="_Pict.PictApplication.addPlatformHelpTopic('${fEscape(tmpArea)}'); return false;"><i class="fas fa-plus"></i> Topic</a>`;
		}
		// Editors get a pencil (edit the doc) and — when this topic isn't already what opens on the current page — a
		// "Set as this page's help" pin that binds it to the route (persisted in AcademyContent, no topics.json).
		let tmpBindBtn = '';
		if (tmpEditMode)
		{
			const tmpRoute = ((typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '').replace(/^#/, '') || '/';
			if (this._helpHashForRoute(tmpRoute) !== pHash)
			{
				tmpBindBtn = `<button class="platform-help-editbtn" title="Make this the help topic that opens on this page" onclick="_Pict.PictApplication.bindCurrentHelpToRoute('${fEscape(pHash)}'); return false;"><i class="fas fa-thumbtack"></i> Set as this page’s help</button>`;
			}
		}
		const tmpEditBar = tmpEditMode
			? `<div class="platform-help-editbar"><span class="platform-help-editbar-spacer"></span>${tmpBindBtn}<button class="platform-help-editbtn" onclick="_Pict.PictApplication.editPlatformHelpDoc('${fEscape(pHash)}'); return false;"><i class="fas fa-pen"></i> Edit</button></div>`
			: '';
		return `${PLATFORM_HELP_STYLE}<div class="platform-help"><nav class="platform-help-nav">${tmpNav}</nav>${tmpEditBar}<div class="platform-help-body">${pBodyHTML}</div></div>`;
	}

	// ── Platform-doc editing (customer-1 canonical + customer overrides) ───────
	// One in-panel markdown editor (pict-section-markdowneditor, the component the inline-doc module wraps and the
	// Email editor reuses) serves both modes; the difference is only WHERE the write lands — the canonical
	// customer-1 row, or the caller's own override — which the server derives from the session. The override read
	// path already exists (PlatformDocument), so a customer's save shows for their users at once, and a revert is a
	// delete of their own row.

	/** True for a customer-1 super-user (role index 5, customer 1). UI gate only; the server is authoritative. */
	_isSuperUser()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		return !!(tmpUser && (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1));
	}

	/**
	 * True when the session may impersonate a customer's account on #/ManageCustomers: a HeadLight (customer 1) user of
	 * at least level 2 who is either a level-5 super-admin OR holds the `Customer-Impersonate` capability (granted to
	 * internal support staff via Entitlements). UI gate only — the server (Authentication.authorizeImpersonation) is
	 * authoritative. Fail-open before the capability map loads, matching the app's other capability gates.
	 * @return {boolean}
	 */
	canImpersonate()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		if (!tmpUser || (Number(tmpUser.CustomerID) !== 1) || !(Number(tmpUser.UserRoleIndex) >= 2)) { return false; }
		if (Number(tmpUser.UserRoleIndex) >= 5) { return true; }
		const tmpService = this.fable && this.fable.EffectivePermissionsService;
		if (!tmpService || (typeof tmpService.canHash !== 'function') || (typeof tmpService.currentUserReady !== 'function')) { return true; }
		if (!tmpService.currentUserReady()) { return true; }
		return !!tmpService.canHash('Customer-Impersonate');
	}

	/**
	 * Impersonate a customer's configuration admin, then reload to the start screen as that admin. The SINGLE client
	 * seam for "become a customer's admin" (per the forward-compatibility note — the future level-4 cross-customer
	 * flow reuses this method + the same server route). The server resolves the admin and enforces the gate; here we
	 * just call it and react. On "no config admin yet" the session is unchanged and we route to Grant Access.
	 * Referenced from injected HTML as `_Pict.PictApplication.impersonateCustomerAdmin(<IDCustomer>)`.
	 * @param {Number} pIDCustomer
	 */
	impersonateCustomerAdmin(pIDCustomer)
	{
		const tmpIDCustomer = Number(pIDCustomer);
		if (!Number.isFinite(tmpIDCustomer) || (tmpIDCustomer < 1)) { return; }
		const tmpRestClient = this.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return; }
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		const fToast = (pMessage, pType) => { if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'error' }); } };
		tmpRestClient.getJSON(`Authentication/ImpersonateCustomerAdmin/${tmpIDCustomer}`, (pError, pBody) =>
		{
			if (pError)
			{
				fToast(`Could not impersonate: ${(pError && pError.message) ? pError.message : pError}`, 'error');
				return;
			}
			if (pBody && (pBody.Status === 'NoConfigAdmin'))
			{
				// No config admin for this customer yet — send the super-user to Grant Access to seed one. (The server
				// signals this via `Status`, not `Error`, so the rest client passes the body through instead of throwing.)
				fToast('This customer has no configuration admin yet — grant one in Grants first.', 'warning');
				if (typeof window !== 'undefined' && window.location) { window.location.hash = '#/GrantAccess/' + Number(tmpIDCustomer); }
				return;
			}
			if (!pBody || (pBody.Success === false))
			{
				fToast(`Could not impersonate: ${(pBody && pBody.Message) ? pBody.Message : 'the server declined the request.'}`, 'error');
				return;
			}
			// Success — reload to the start screen as the customer's configuration admin.
			if (typeof window !== 'undefined' && window.location)
			{
				window.location.hash = '#/';
				window.location.reload();
			}
		});
	}

	/**
	 * Impersonate a SPECIFIC account by IDUser (the account picker on #/ManageCustomers), then reload as that user.
	 * Same super-user gate + session-replacement as impersonateCustomerAdmin, but the caller chooses which account
	 * (the server route `Authentication/Impersonate/:IDUser` accepts any target for a level-5/customer-1 session).
	 * Referenced from injected HTML via `_Pict.providers.ManageCustomers.impersonateAccount(<IDUser>)`.
	 * @param {Number} pIDUser
	 */
	impersonateUser(pIDUser)
	{
		const tmpIDUser = Number(pIDUser);
		if (!Number.isFinite(tmpIDUser) || (tmpIDUser < 1)) { return; }
		const tmpRestClient = this.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return; }
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		const fToast = (pMessage, pType) => { if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'error' }); } };
		tmpRestClient.getJSON(`Authentication/Impersonate/${tmpIDUser}`, (pError, pBody) =>
		{
			if (pError)
			{
				fToast(`Could not sign in as that account: ${(pError && pError.message) ? pError.message : pError}`, 'error');
				return;
			}
			if (pBody && (pBody.Success === false))
			{
				fToast(`Could not sign in: ${(pBody && pBody.Message) ? pBody.Message : 'the server declined the request.'}`, 'error');
				return;
			}
			// Success — the session is now that account; reload to the start screen as them.
			if (typeof window !== 'undefined' && window.location)
			{
				window.location.hash = '#/';
				window.location.reload();
			}
		});
	}

	/**
	 * True when the session may manage the customer team — a level-5 super-user who also holds the "Manage Customer
	 * Team" entitlement (CustomerTeam-Update). Fail-open before the capability map loads (matching the app's other
	 * gates). UI gate only; this feature's writes ride the super-user's own access, and the data is UX/data only.
	 */
	_canManageCustomerTeam()
	{
		if (!this._isSuperUser()) { return false; }
		const tmpService = this.fable && this.fable.EffectivePermissionsService;
		if (!tmpService || (typeof tmpService.canHash !== 'function') || (typeof tmpService.currentUserReady !== 'function')) { return true; }
		if (!tmpService.currentUserReady()) { return true; }
		return !!tmpService.canHash('CustomerTeam-Update');
	}

	/** True when the session holds a CustomerAdmin role. UI gate only. */
	_hasCustomerAdmin()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		const tmpRoles = tmpUser && tmpUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin'));
	}

	/** True when the session can edit help content — the AcademyContent-Update capability granted by the
	 *  "Edit in-app help documentation" entitlement. UI gate only; the server enforces the actual writes. */
	_canEditAcademyContent()
	{
		const tmpService = this.fable && this.fable.EffectivePermissionsService;
		return !!(tmpService && (typeof tmpService.can === 'function') && tmpService.can('AcademyContent', 'Update'));
	}

	/**
	 * Which doc-edit mode the session gets, or null (read-only). A customer-1 session edits the CANONICAL rows; any
	 * other customer edits their own per-customer OVERRIDE. Editing is unlocked by the AcademyContent-Update
	 * capability (the "Edit in-app help documentation" entitlement) — with a CustomerAdmin role / customer-1
	 * super-user still qualifying so the gate never regresses for existing admins. The server enforces the writes.
	 * @returns {'canonical'|'override'|null}
	 */
	_platformDocsEditMode()
	{
		const tmpCanEdit = this._canEditAcademyContent() || this._hasCustomerAdmin() || this._isSuperUser();
		if (!tmpCanEdit) { return null; }
		return this.isHeadlightSession() ? 'canonical' : 'override';
	}

	/**
	 * Enter edit mode for a doc: fetch the caller's OWN AcademyContent row (the canonical row for a customer-1
	 * session, the customer's override otherwise) for its id + current markdown, then mount the editor. When the
	 * caller has no row yet (a customer editing a canonical topic for the first time) we seed from the canonical
	 * content so they start from the platform default; saving then creates their override.
	 * Referenced from injected HTML as `_Pict.PictApplication.editPlatformHelpDoc('<hash>')`.
	 * @param {string} pHash
	 */
	editPlatformHelpDoc(pHash)
	{
		const tmpMode = this._platformDocsEditMode();
		if (!tmpMode) { return; }
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function')) { return; }
		this.toggleHelp(true);
		this._activePlatformHelpHash = pHash;
		tmpContentView.displayContent(this._platformHelpHTML(pHash, '<p class="platform-help-loading">Loading the editor…</p>'));

		this._loadEditableDoc(pHash).then((pOwnRow) =>
		{
			if (this._activePlatformHelpHash !== pHash) { return; }
			const tmpRegistryDoc = libPlatformDocs.docByHash(pHash);
			this._editingDoc =
			{
				Hash: pHash,
				ID: (pOwnRow && pOwnRow.IDAcademyContent) || 0,   // 0 → save creates a new row (an override)
				Title: (pOwnRow && pOwnRow.Title) || (tmpRegistryDoc && tmpRegistryDoc.Title) || pHash,
				Mode: tmpMode,
				HasOverride: !!(pOwnRow && pOwnRow.IDAcademyContent && (tmpMode === 'override')),
				IsKnownTopic: !!tmpRegistryDoc,   // a registry (canonical) topic → the delete is a "revert"; else it's their own topic
			};
			const fSeed = (pStartingMarkdown) => { this._renderPlatformDocEditor(pStartingMarkdown || ''); };
			if (pOwnRow && (pOwnRow.Content != null)) { fSeed(pOwnRow.Content); }
			else { this._fetchAcademyDoc(pHash).then((pResolved) => fSeed((pResolved && pResolved.Content) || '')); }
		});
	}

	/**
	 * Fetch the caller's OWN AcademyContent row for a Hash — the id and current markdown the inline editor
	 * needs — or null when they have not authored an override.
	 *
	 * Resolved from PlatformDocument for the same reason as the other ownership lookups: a Meadow read by
	 * Hash fetches the canonical customer-1 row, which the caller does not own, and the MyCustomer post-fetch
	 * assertion then refuses the entire read. For a tenant that returned null on every topic with a platform
	 * default, which the editor reads as "no override" — so it would create a fresh row on each save instead
	 * of updating the one it already made.
	 * @param {string} pHash @returns {Promise<object|null>}
	 */
	_loadEditableDoc(pHash)
	{
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return Promise.resolve(null); }
		const tmpCustomerID = Number((this.AppData.CurrentUser && this.AppData.CurrentUser.CustomerID) || 0);
		return new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`PlatformDocument/${encodeURIComponent(pHash)}`, (pError, pBody) =>
			{
				if (pError || !pBody || Array.isArray(pBody) || !pBody.Hash) { return resolve(null); }
				const tmpServedCustomer = Number(pBody.IDCustomer || 0);
				if (!tmpServedCustomer || !tmpCustomerID || (tmpServedCustomer !== tmpCustomerID)) { return resolve(null); }
				return resolve(pBody);
			});
		});
	}

	/** Render the editor shell into the panel and mount the markdown editor seeded with pMarkdown. */
	_renderPlatformDocEditor(pMarkdown)
	{
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function')) { return; }
		tmpContentView.displayContent(this._platformDocEditorHTML());
		this._mountPlatformDocEditor(pMarkdown);
	}

	/** The editor shell: a toolbar (title + mode badge + Save/Cancel), an optional error/confirm row, the mount. */
	_platformDocEditorHTML()
	{
		const fEscape = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
		const tmpDoc = this._editingDoc || {};
		const tmpCanonical = (tmpDoc.Mode === 'canonical');
		const tmpModeChip = tmpCanonical
			? '<span class="platform-help-editmode platform-help-editmode-canonical">Platform default</span>'
			: '<span class="platform-help-editmode platform-help-editmode-override">Your customer</span>';
		const tmpNote = tmpCanonical
			? 'Editing the platform-default documentation — visible to every customer.'
			: 'Editing your customer’s copy. It overrides the platform default for your users only.';
		const tmpError = tmpDoc.Error
			? `<p class="platform-help-editnote" style="color:var(--theme-color-status-danger, #c0392b);">${fEscape(tmpDoc.Error)}</p>`
			: '';
		// A known (registry) topic's delete reverts to the platform default; the caller's own new topic is just deleted.
		const tmpKnown = !!tmpDoc.IsKnownTopic;
		const tmpDeleteLabel = tmpKnown ? 'Revert to platform default' : 'Delete this topic';
		const tmpConfirmQ = tmpKnown ? 'Delete your customer’s copy and revert to the platform default?' : 'Delete this topic for your customer?';
		let tmpRevertArea = '';
		if (tmpDoc.ConfirmRevert)
		{
			tmpRevertArea = '<div class="platform-help-editbar" style="margin-top:0.7rem;">'
				+ `<span>${fEscape(tmpConfirmQ)}</span><span class="platform-help-editbar-spacer"></span>`
				+ `<button class="platform-help-btn platform-help-btn-danger" onclick="_Pict.PictApplication.confirmRevertPlatformHelpDoc(); return false;">${fEscape(tmpKnown ? 'Revert' : 'Delete')}</button>`
				+ '<button class="platform-help-btn platform-help-btn-ghost" onclick="_Pict.PictApplication.cancelRevertPlatformHelpDoc(); return false;">Keep it</button></div>';
		}
		else if (tmpDoc.HasOverride)
		{
			tmpRevertArea = '<div class="platform-help-editbar" style="margin-top:0.7rem;">'
				+ `<button class="platform-help-btn platform-help-btn-danger" onclick="_Pict.PictApplication.revertPlatformHelpDoc(); return false;">${fEscape(tmpDeleteLabel)}</button></div>`;
		}
		return `${PLATFORM_HELP_STYLE}<div class="platform-help">`
			+ `<div class="platform-help-editbar"><strong>${fEscape(tmpDoc.Title || '')}</strong> ${tmpModeChip}<span class="platform-help-editbar-spacer"></span>`
			+ '<button class="platform-help-btn platform-help-btn-primary" onclick="_Pict.PictApplication.savePlatformHelpDoc(); return false;">Save</button>'
			+ '<button class="platform-help-btn platform-help-btn-ghost" onclick="_Pict.PictApplication.cancelPlatformHelpEdit(); return false;">Cancel</button></div>'
			+ `<p class="platform-help-editnote">${fEscape(tmpNote)}</p>`
			+ tmpError
			+ '<div id="PlatformDoc-Editor-Mount" class="platform-help-editmount"></div>'
			+ tmpRevertArea
			+ '</div>';
	}

	/** Mount (or re-render) the markdown editor into the shell, seeded with the doc's markdown. */
	_mountPlatformDocEditor(pMarkdown)
	{
		if (!this.pict.AppData.PlatformDocEditor) { this.pict.AppData.PlatformDocEditor = {}; }
		this.pict.AppData.PlatformDocEditor.Segments = [ { Content: (pMarkdown == null) ? '' : String(pMarkdown) } ];
		if (!this.pict.views['PlatformDoc-Editor'])
		{
			this.pict.addView('PlatformDoc-Editor',
				{
					ViewIdentifier: 'PlatformDoc-Editor',
					TargetElementAddress: '#PlatformDoc-Editor-Mount',
					ContentDataAddress: 'AppData.PlatformDocEditor.Segments',
					AutoRender: false,
					EnableRichPreview: true,
					ReadOnly: false,
				}, libPlatformDocMarkdownEditor);
		}
		try { this.pict.views['PlatformDoc-Editor'].render(); }
		catch (pError) { this.pict.log.warn('Platform doc markdown editor failed to render', { error: String(pError) }); }
	}

	/** Read the edited markdown back out of the editor. */
	_capturePlatformDocEditor()
	{
		const tmpView = this.pict.views['PlatformDoc-Editor'];
		if (tmpView && (typeof tmpView.marshalFromView === 'function')) { try { tmpView.marshalFromView(); } catch (pError) { /* editor gone */ } }
		const tmpSegments = (this.pict.AppData.PlatformDocEditor && this.pict.AppData.PlatformDocEditor.Segments) || [];
		return tmpSegments.map((pSeg) => (pSeg && pSeg.Content) || '').join('\n\n');
	}

	/**
	 * Save the editor's markdown to AcademyContent: PUT the existing row (the canonical row, or an existing
	 * override), or POST a new override (IDCustomer omitted → the server stamps the caller's customer). On success
	 * reload + re-render read mode; on error keep the editor open with an inline message. Referenced from HTML.
	 */
	savePlatformHelpDoc()
	{
		const tmpDoc = this._editingDoc;
		if (!tmpDoc) { return; }
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return; }
		const tmpMarkdown = this._capturePlatformDocEditor();
		const tmpRecord = { Hash: tmpDoc.Hash, Title: tmpDoc.Title, Content: tmpMarkdown };
		const fDone = (pError) =>
		{
			if (pError)
			{
				this.pict.log.warn('Platform doc save failed', { hash: tmpDoc.Hash, error: String(pError) });
				tmpDoc.Error = 'Could not save — the server rejected the write (you may not have permission at this scope).';
				this._renderPlatformDocEditor(tmpMarkdown);
				return;
			}
			if (this._academyHelpCache) { this._academyHelpCache[tmpDoc.Hash] = undefined; }
			this._editingDoc = null;
			this._renderPlatformHelp(tmpDoc.Hash);
		};
		if (tmpDoc.ID) { tmpRestClient.putJSON('AcademyContent', Object.assign({ IDAcademyContent: tmpDoc.ID }, tmpRecord), fDone); }
		else { tmpRestClient.postJSON('AcademyContent', tmpRecord, fDone); }
	}

	/** Leave edit mode without saving. Referenced from injected HTML. */
	cancelPlatformHelpEdit()
	{
		const tmpHash = this._editingDoc ? this._editingDoc.Hash : this._activePlatformHelpHash;
		this._editingDoc = null;
		this._renderPlatformHelp(tmpHash);
	}

	/** Arm the two-step revert confirm (customer override only). Referenced from injected HTML. */
	revertPlatformHelpDoc()
	{
		if (!this._editingDoc || !this._editingDoc.HasOverride) { return; }
		this._editingDoc.ConfirmRevert = true;
		this._renderPlatformDocEditor(this._capturePlatformDocEditor());
	}

	/** Cancel the revert confirm, staying in the editor. Referenced from injected HTML. */
	cancelRevertPlatformHelpDoc()
	{
		if (!this._editingDoc) { return; }
		this._editingDoc.ConfirmRevert = false;
		this._renderPlatformDocEditor(this._capturePlatformDocEditor());
	}

	/**
	 * Delete the caller's own override row → revert to the platform default. The bespoke read endpoint then serves
	 * the canonical customer-1 content again. Referenced from injected HTML.
	 */
	confirmRevertPlatformHelpDoc()
	{
		const tmpDoc = this._editingDoc;
		if (!tmpDoc || !tmpDoc.ID || !tmpDoc.HasOverride) { return; }
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient || (typeof tmpRestClient.deleteEntity !== 'function')) { return; }
		tmpRestClient.deleteEntity('AcademyContent', tmpDoc.ID, (pError) =>
		{
			if (pError)
			{
				this.pict.log.warn('Platform doc revert failed', { hash: tmpDoc.Hash, error: String(pError) });
				tmpDoc.ConfirmRevert = false;
				tmpDoc.Error = 'Could not revert — the server rejected the delete.';
				this._renderPlatformDocEditor(this._capturePlatformDocEditor());
				return;
			}
			if (this._academyHelpCache) { this._academyHelpCache[tmpDoc.Hash] = undefined; }
			this._editingDoc = null;
			this._renderPlatformHelp(tmpDoc.Hash);
		});
	}

	/**
	 * Begin adding a new topic to an area (editors only): show an inline title form in the panel. Referenced from HTML.
	 * @param {string} pArea
	 */
	addPlatformHelpTopic(pArea)
	{
		if (!this._platformDocsEditMode() || !pArea) { return; }
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function')) { return; }
		// Capture the route we're adding from so the new topic can be route-bound. If the page has no resolved help
		// yet, the new topic becomes its help (auto-bound as primary); otherwise it just joins the area's topic list.
		const tmpRoute = ((typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '').replace(/^#/, '') || '/';
		this._addingTopic = { Area: pArea, Route: tmpRoute, BindPrimary: !this._helpHashForRoute(tmpRoute), Error: '' };
		this.toggleHelp(true);
		tmpContentView.displayContent(this._platformNewTopicHTML());
		try { const tmpInput = document.getElementById('PlatformDoc-NewTopic-Title'); if (tmpInput) { tmpInput.focus(); } }
		catch (pError) { /* no focusable input */ }
	}

	/** The inline new-topic form (title input + Create/Cancel). */
	_platformNewTopicHTML()
	{
		const fEscape = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
		const tmpAdd = this._addingTopic || {};
		const tmpNote = (this._platformDocsEditMode() === 'canonical')
			? 'New platform-default topic — visible to every customer once you save it.'
			: 'New topic for your customer only.';
		const tmpError = tmpAdd.Error
			? `<p class="platform-help-editnote" style="color:var(--theme-color-status-danger, #c0392b);">${fEscape(tmpAdd.Error)}</p>`
			: '';
		return `${PLATFORM_HELP_STYLE}<div class="platform-help">`
			+ '<div class="platform-help-editbar"><strong>Add a topic</strong></div>'
			+ `<p class="platform-help-editnote">${fEscape(tmpNote)}</p>`
			+ tmpError
			+ '<div class="platform-help-editbar"><input id="PlatformDoc-NewTopic-Title" type="text" class="platform-help-input" placeholder="Topic title" />'
			+ '<button class="platform-help-btn platform-help-btn-primary" onclick="_Pict.PictApplication.createPlatformHelpTopic(); return false;">Create</button>'
			+ '<button class="platform-help-btn platform-help-btn-ghost" onclick="_Pict.PictApplication.cancelAddPlatformHelpTopic(); return false;">Cancel</button></div>'
			+ '</div>';
	}

	/** Re-render the new-topic form (e.g. to surface a validation/save error). */
	_renderNewTopicForm()
	{
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (tmpContentView && (typeof tmpContentView.displayContent === 'function')) { tmpContentView.displayContent(this._platformNewTopicHTML()); }
	}

	/**
	 * Create the new topic: slug the title into a unique `Doc-<Area>-<Slug>` hash, POST a starter AcademyContent
	 * row (a customer-1 session creates a canonical topic; a customer creates their own), then open it in the
	 * editor. Referenced from injected HTML.
	 */
	createPlatformHelpTopic()
	{
		const tmpAdd = this._addingTopic;
		if (!tmpAdd) { return; }
		let tmpTitle = '';
		try { const tmpInput = document.getElementById('PlatformDoc-NewTopic-Title'); tmpTitle = tmpInput ? String(tmpInput.value || '').trim() : ''; }
		catch (pError) { tmpTitle = ''; }
		const tmpSlug = tmpTitle.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(/\s+/).filter((pWord) => pWord.length > 0)
			.map((pWord) => pWord.charAt(0).toUpperCase() + pWord.slice(1)).join('');
		if (!tmpTitle || !tmpSlug)
		{
			tmpAdd.Error = 'Give the topic a title with at least one letter or number.';
			this._renderNewTopicForm();
			return;
		}
		// Uniquify the hash against the area's known (registry) + discovered topics.
		const tmpExisting = {};
		libPlatformDocs.docsInArea(tmpAdd.Area).forEach((pDoc) => { tmpExisting[pDoc.Hash] = true; });
		((this._platformAreaIndexCache && this._platformAreaIndexCache[tmpAdd.Area]) || []).forEach((pDoc) => { if (pDoc && pDoc.Hash) { tmpExisting[pDoc.Hash] = true; } });
		let tmpFinalSlug = tmpSlug;
		let tmpCounter = 2;
		while (tmpExisting[`Doc-${tmpAdd.Area}-${tmpFinalSlug}`]) { tmpFinalSlug = tmpSlug + tmpCounter; tmpCounter++; }
		const tmpHash = `Doc-${tmpAdd.Area}-${tmpFinalSlug}`;
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient) { return; }
		tmpRestClient.postJSON('AcademyContent', { Hash: tmpHash, Title: tmpTitle, Content: `# ${tmpTitle}\n\n` }, (pError) =>
		{
			if (pError)
			{
				this.pict.log.warn('Platform doc topic create failed', { hash: tmpHash, error: String(pError) });
				tmpAdd.Error = 'Could not create the topic — the server rejected the write (you may not have permission).';
				this._renderNewTopicForm();
				return;
			}
			// Drop the area index cache so the new topic is re-discovered. When the page had no help yet, bind the
			// new topic to this route so F1 opens it here; then open it in the editor.
			if (this._platformAreaIndexCache) { this._platformAreaIndexCache[tmpAdd.Area] = undefined; }
			const tmpRoute = tmpAdd.Route;
			const tmpBindPrimary = !!tmpAdd.BindPrimary;
			this._addingTopic = null;
			if (tmpBindPrimary && tmpRoute)
			{
				this._bindRouteToHashAsync(tmpRoute, tmpHash, () => this.editPlatformHelpDoc(tmpHash));
			}
			else
			{
				this.editPlatformHelpDoc(tmpHash);
			}
		});
	}

	/** Dismiss the new-topic form, back to the current doc. Referenced from injected HTML. */
	cancelAddPlatformHelpTopic()
	{
		this._addingTopic = null;
		// Return to the platform topic we were viewing; if there was none (the header "Add help for this page" flow
		// on a file-based route), re-resolve the route's normal help instead of rendering a blank/stale panel.
		if (this._activePlatformHelpHash) { this._renderPlatformHelp(this._activePlatformHelpHash); }
		else { this.updateHelpForRoute(); }
	}

	/**
	 * Bind the topic being viewed to the current route so F1 opens it on this page (persisted in AcademyContent).
	 * Referenced from injected HTML as `_Pict.PictApplication.bindCurrentHelpToRoute('<hash>')`.
	 * @param {string} pHash
	 */
	bindCurrentHelpToRoute(pHash)
	{
		if (!this._platformDocsEditMode() || !pHash) { return; }
		const tmpRoute = ((typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '').replace(/^#/, '') || '/';
		this._bindRouteToHashAsync(tmpRoute, pHash, () => this._renderPlatformHelp(pHash));
	}

	/** A help Area slug derived from a route (the first path segment) — the namespace for its `Doc-<Area>-<Topic>` hashes. */
	_deriveHelpAreaFromRoute(pRoute)
	{
		const tmpFirst = String(pRoute || '/').replace(/^\/+/, '').split('/')[0] || '';
		return tmpFirst.replace(/[^A-Za-z0-9]+/g, '') || 'General';
	}

	/**
	 * The header "Add help for this page" entry point — available on ANY route (works whether the page currently
	 * shows a platform doc, a file-based doc, or nothing). Creates a topic in an area derived from the route and,
	 * when the page has no platform topic yet, binds the new topic to the route so F1 opens it here.
	 */
	addHelpForCurrentRoute()
	{
		if (!this._platformDocsEditMode()) { return; }
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (!tmpContentView || (typeof tmpContentView.displayContent !== 'function')) { return; }
		const tmpRoute = ((typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '').replace(/^#/, '') || '/';
		this.toggleHelp(true);
		// We're taking over the shared panel with our own content; flag it so leaving the route restores app docs.
		this._helpShowingForeignDocs = true;
		// Not viewing a platform topic while the add form is up — so Cancel re-resolves the route rather than
		// restoring a stale topic from a previously-visited platform route.
		this._activePlatformHelpHash = '';
		this._addingTopic = { Area: this._deriveHelpAreaFromRoute(tmpRoute), Route: tmpRoute, BindPrimary: !this._helpHashForRoute(tmpRoute), Error: '' };
		// The content body only exists after the content view has rendered once (a file-based route may not have
		// created it). Render it, then show the new-topic form in place.
		if ((typeof document !== 'undefined') && !document.getElementById('InlineDoc-Content-Body') && (typeof tmpContentView.render === 'function'))
		{
			tmpContentView.render();
		}
		tmpContentView.displayContent(this._platformNewTopicHTML());
		try { const tmpInput = document.getElementById('PlatformDoc-NewTopic-Title'); if (tmpInput) { tmpInput.focus(); } }
		catch (pError) { /* no focusable input */ }
	}

	/** Show/hide the header "Add help for this page" button by the current edit gate. */
	_syncAddPageHelpButton()
	{
		if (typeof document === 'undefined') { return; }
		const tmpButton = document.getElementById('PlatformConfig-AddPageHelp');
		if (!tmpButton) { return; }
		if (this._platformDocsEditMode()) { tmpButton.classList.remove('is-hidden'); }
		else { tmpButton.classList.add('is-hidden'); }
	}

	// ── Navigation style (pict-section-navigation) ─────────────────────────────

	/** The persisted navigation style ('Sidebar' | 'Hub' | 'List' | 'Palette'); default List. */
	get navigationStyle()
	{
		let tmpStyle = null;
		try { tmpStyle = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage.getItem(NAV_STYLE_STORAGE_KEY) : null; }
		catch (pError) { tmpStyle = null; }
		const tmpProvider = this.pict.providers['Pict-Navigation'];
		const tmpValid = tmpProvider ? tmpProvider.getRenderTypes().some((pType) => pType.Key === tmpStyle) : false;
		return tmpValid ? tmpStyle : NAV_STYLE_DEFAULT;
	}

	/** Persist + apply a navigation style. Returns the applied style. */
	setNavigationStyle(pStyle)
	{
		try { if (typeof window !== 'undefined' && window.localStorage) { window.localStorage.setItem(NAV_STYLE_STORAGE_KEY, pStyle); } }
		catch (pError) { /* private mode — non-fatal */ }
		this.pict.AppData.NavRenderType = pStyle;
		this.applyNavigationStyle(pStyle);
		return pStyle;
	}

	/**
	 * Apply a navigation style to the app shell. Rail styles (Sidebar) dock the
	 * persistent navigation rail and shift the content right; page styles (Hub/List/
	 * Palette) undock it (the Configurations screen renders the nav as a home screen).
	 *
	 * @param {string} [pStyle]
	 */
	applyNavigationStyle(pStyle)
	{
		const tmpProvider = this.pict.providers['Pict-Navigation'];
		if (!tmpProvider) { return; }
		const tmpStyle = pStyle || this.navigationStyle;
		if (tmpProvider.isRailRenderType(tmpStyle))
		{
			tmpProvider.renderSidebarDocked('#PlatformConfig-NavSidebar');
			this.pict.ContentAssignment.addClass('body', 'pc-nav-docked');
			this.syncNavSidebarActive();
		}
		else
		{
			this.pict.ContentAssignment.removeClass('body', 'pc-nav-docked');
		}
	}

	/** Move the docked rail's active highlight to match the current route (no-op for page styles). */
	syncNavSidebarActive()
	{
		const tmpProvider = this.pict.providers['Pict-Navigation'];
		if (!tmpProvider || typeof tmpProvider.isRailRenderType !== 'function' || !tmpProvider.isRailRenderType(this.navigationStyle)) { return; }
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
		tmpProvider.setActiveByRoute(tmpHash);
		const tmpView = this.pict.views['Navigation-Sidebar-Docked'];
		if (tmpView && typeof tmpView.syncActive === 'function') { tmpView.syncActive(); }
	}

	/**
	 * Paint (or clear) the return-context banner. When a user jumps from a Project/Contract Type editor into a
	 * prototype's first-class editor, ProjectContractTypes-Provider stows `AppData.PrototypeSetupContext`; this shows a
	 * bar with a link back to the type. Back on the type editor route (the ReturnHash), the context is cleared.
	 * Called from the router `before` hook so it stays correct on every route.
	 */
	syncTemplateContextBanner()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement('#PlatformConfig-TemplateContextBanner').length === 0) { return; }
		const tmpCtx = this.pict.AppData.PrototypeSetupContext;
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
		const tmpOnType = !!(tmpCtx && tmpCtx.ReturnHash && tmpHash && (tmpHash === tmpCtx.ReturnHash));
		if (!tmpCtx || !tmpCtx.ReturnHash || tmpOnType)
		{
			if (tmpOnType) { this.pict.AppData.PrototypeSetupContext = null; }
			tmpCA.assignContent('#PlatformConfig-TemplateContextBanner', '');
			return;
		}
		const tmpName = String(tmpCtx.TypeName || 'this type').replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
		tmpCA.assignContent('#PlatformConfig-TemplateContextBanner',
			`<div style="background:var(--theme-color-brand-primary,#2e7d74);color:#fff;padding:0.5rem 1.25rem;font-size:0.85rem;display:flex;align-items:center;gap:0.7rem;flex-wrap:wrap;">`
			+ `<i class="fas fa-wand-magic-sparkles"></i><span>Editing the template for <b>${tmpName}</b></span>`
			+ `<a href="${tmpCtx.ReturnHash}" style="color:#fff;font-weight:700;text-decoration:underline;">← Back to type</a></div>`);
	}

	/**
	 * One-time wiring of the navigation shell: bind the ⌘K/Ctrl+K launcher hotkey and the
	 * F1 help hotkey, then apply the stored navigation style (docking the rail if Sidebar).
	 */
	_initializeNavigationShell()
	{
		if (this._navShellInitialized) { return; }
		this._navShellInitialized = true;

		const tmpProvider = this.pict.providers['Pict-Navigation'];
		if (tmpProvider && typeof tmpProvider.bindLauncherHotkey === 'function')
		{
			// ⌘K / Ctrl+K opens the command-palette launcher overlay; Escape closes it.
			tmpProvider.bindLauncherHotkey();
		}
		this._bindGlobalHelpHotkey();
		this.applyNavigationStyle(this.navigationStyle);
	}

	/**
	 * F1 toggles the documentation panel. A browser-level global shortcut has no inline
	 * handler equivalent, so this addEventListener is a documented exception to the pict
	 * "inline handlers only" rule (same category as the navigation launcher hotkey).
	 */
	_bindGlobalHelpHotkey()
	{
		if (typeof document === 'undefined' || this._helpHotkeyBound) { return; }
		this._helpHotkeyBound = true;
		document.addEventListener('keydown', (pEvent) =>
		{
			if (pEvent && pEvent.key === 'F1')
			{
				pEvent.preventDefault();
				this.toggleHelp();
			}
		}, true);
	}

	/** True when the logged-in session belongs to the Headlight customer (CustomerID === 1). */
	isHeadlightSession()
	{
		const tmpUser = this.pict.AppData.CurrentUser;
		return !!(tmpUser && Number(tmpUser.CustomerID) === 1);
	}

	/**
	 * Install the pict-section-navigation global filter. The filter runs on every nav surface —
	 * the sidebar, the topic/list pages, and the ⌘K launcher search — so it is the single place to
	 * gate visibility. Gates today:
	 *   - `Hidden: true` (item OR its category) drops the entry everywhere. A category-level Hidden
	 *     drops all of its children, which leaves the category with zero items so it disappears too.
	 *     Used to pilot Analytics out of the nav (see navigation-graph.js).
	 *   - `Headlight: true` items (e.g. Customers) are gated to the Headlight session. The predicate
	 *     reads the session live, so it stays correct no matter when CustomerID is populated.
	 *   - `SuperUser: true` items (e.g. Grant Access) are platform-super-user only; `AlsoSuperUser: true`
	 *     items are always shown to super-users. Otherwise super-users pass the SAME capability gate as
	 *     everyone else (which they satisfy as allow-all), so a HeadLight super-admin keeps the full
	 *     capability-gated management surface for its own tenant instead of a stripped platform-only one.
	 *   - the capability gate (`_navItemPermitted`): a Module, an explicit Capability, or `<Entity>-Read`
	 *     derived from a `#/PSRS/<Entity>/List` route.
	 */
	_applyCustomerNavGate()
	{
		const tmpProvider = this.pict.providers['Pict-Navigation'];
		if (!tmpProvider || typeof tmpProvider.setGlobalFilter !== 'function') { return; }
		tmpProvider.setGlobalFilter((pItem, pCategory) =>
		{
			if (pItem.Hidden || (pCategory && pCategory.Hidden)) { return false; }
			const tmpSuperUser = this._isSuperUser();
			// `SuperUser: true` destinations (e.g. Grant Access) are platform-super-user only.
			if (pItem.SuperUser && !tmpSuperUser) { return false; }
			// Super-users always keep their platform tiles (SuperUser / AlsoSuperUser) regardless of capability.
			if (tmpSuperUser && ((pItem.SuperUser === true) || (pItem.AlsoSuperUser === true))) { return true; }
			// Everyone else — INCLUDING level-5 HeadLight super-users, for every non-platform tile — passes the normal
			// Headlight / module / capability gate. Super-users satisfy the capability checks (EffectivePermissionsService
			// treats IsSuperUser as allow-all), so a HeadLight (customer-1) super-admin sees the full management surface
			// again — Users, Access & Permissions, Entitlements, project assignments — and administers HeadLight's own
			// tenant; every other customer's users still see only the tiles their granted capabilities permit.
			return (!pItem.Headlight || this.isHeadlightSession())
				&& (!pItem.CustomerModule || this._customerHasModule(pItem.CustomerModule))
				&& this._navItemPermitted(pItem);
		});
	}

	/**
	 * A micro app whose manifest names `Access.RequiredModule` (the LIMS micro app: 'LIMS') signs out a user the
	 * customer's permission enforcement refuses that module, as legacy apps do through permissions-control. Every
	 * other session, and the config app itself, passes.
	 * @return {Promise<boolean>} false when the user was refused and is being signed out
	 */
	async _microAppAccessAllowedAsync()
	{
		const tmpMicroApp = this.pict.providers.MicroApp;
		const tmpModule = tmpMicroApp && tmpMicroApp.manifest && tmpMicroApp.manifest.Access && tmpMicroApp.manifest.Access.RequiredModule;
		if (!tmpModule || this._isSuperUser())
		{
			return true;
		}
		const tmpCurrentUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		const tmpEffectivePermissions = this.fable.EffectivePermissionsService;
		const tmpHasPermission = !!(tmpEffectivePermissions && tmpEffectivePermissions.currentUserReady() && tmpEffectivePermissions.canModule(tmpModule));
		if (tmpHasPermission)
		{
			return true;
		}
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		const tmpConfiguration = await new Promise((resolve) =>
		{
			tmpRestClient.getJSON(`Configuration/For/PermissionsEnforcement/In/Customer/${tmpCurrentUser.CustomerID}`, (pError, pBody) => resolve(pError ? null : pBody));
		});
		const tmpEnforcement = libModuleAccess.resolveModuleEnforcement(tmpConfiguration, tmpCurrentUser.CustomerID, tmpModule);
		if (!libModuleAccess.isModuleAccessDenied(tmpEnforcement, tmpHasPermission))
		{
			return true;
		}
		const tmpAppName = (tmpMicroApp.manifest.Branding && tmpMicroApp.manifest.Branding.Title) || tmpMicroApp.manifest.Name || 'this app';
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast({ type: 'is-danger', message: `You don't have access to ${tmpAppName}. Ask your administrator if you need it.` });
		}
		this.logout();
		return false;
	}

	/**
	 * Does the current customer have a Module enabled (a CustomerModuleJoin for the session's customer with `now`
	 * inside [StartDate, EndDate])? This is the ENTITY-level per-customer enablement (a level-5 admin toggles it
	 * in Manage Customers), NOT the role-permission `Module-<X>-Access` gate that EffectivePermissionsService
	 * .canModule() checks. Fail-CLOSED: unknown until loadCustomerModulesAsync() has populated AppData
	 * (awaited at login before the nav gate installs), so a gated download never flashes before it's confirmed.
	 * @param {String} pModuleHash
	 * @return {Boolean}
	 */
	_customerHasModule(pModuleHash)
	{
		const tmpState = this.pict.AppData.CustomerModules;
		if (!tmpState || !tmpState.Ready) { return false; }
		return (tmpState.Hashes || []).indexOf(pModuleHash) > -1;
	}

	/**
	 * Load the current customer's enabled modules into AppData.CustomerModules. Reads are customer-scoped on the
	 * server, so this returns only the session customer's CustomerModuleJoin rows; we keep the ones whose
	 * [StartDate, EndDate] window contains now. Best-effort — a failure yields an empty (fail-closed) set.
	 * @return {Promise<{Ready:Boolean, Hashes:Array}>}
	 */
	async loadCustomerModulesAsync()
	{
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		try
		{
			const tmpRows = await new Promise((resolve, reject) =>
			{
				tmpRestClient.getJSON('CustomerModuleJoins/0/500', (pError, pBody) => pError ? reject(pError) : resolve(pBody));
			});
			const tmpNow = (new Date()).toISOString();
			const tmpHashes = (Array.isArray(tmpRows) ? tmpRows : [])
				.filter((pRow) => (!pRow.StartDate || String(pRow.StartDate) <= tmpNow) && (!pRow.EndDate || String(pRow.EndDate) >= tmpNow))
				.map((pRow) => pRow.ModuleHash)
				.filter((pHash) => !!pHash);
			this.pict.AppData.CustomerModules = { Ready: true, Hashes: tmpHashes };
		}
		catch (pError)
		{
			this.pict.AppData.CustomerModules = { Ready: true, Hashes: [] };
		}
		return this.pict.AppData.CustomerModules;
	}

	/**
	 * Permission-expression layer: may the current user see this nav entry? Gated by the resolved capability
	 * map — an explicit `Module`/`Capability` on the entry, else derived from a `#/PSRS/<Entity>/List` route
	 * (needs `<Entity>-Read`). Entries with no derivable capability (bespoke topics) are always shown, and
	 * everything shows until the capability map has loaded (fail-open; the server stays authoritative).
	 * @param {Object} pItem
	 * @return {Boolean}
	 */
	_navItemPermitted(pItem)
	{
		const tmpEffectivePermissions = this.fable.EffectivePermissionsService;
		if (!tmpEffectivePermissions || !tmpEffectivePermissions.currentUserReady())
		{
			return true;
		}
		if (pItem.Module)
		{
			return tmpEffectivePermissions.canModule(pItem.Module);
		}
		if (pItem.Capability)
		{
			return tmpEffectivePermissions.canHash(pItem.Capability);
		}
		const tmpMatch = /#?\/PSRS\/([A-Za-z0-9_]+)\/(List|Read)/.exec(String(pItem.Route || ''));
		if (tmpMatch)
		{
			return tmpEffectivePermissions.canHash(`${tmpMatch[1]}-Read`);
		}
		return true;
	}

	/**
	 * Role-aware dashboards, embedded: the shared dashboard host plus this app's answer to each action a dashboard can
	 * take. Micro apps inherit these; a dashboard never needs to know which app it is in.
	 */
	installDashboards()
	{
		const tmpDashboards = libHeadlightDashboards.install(this.pict,
			{
				AppChrome: false,
				RouteBase: '/Dashboards',
				DestinationAddress: '#PlatformConfiguration-Application-Container',
				// A micro app that lands on /Dashboards names its own home for people with no landing dashboard.
				getFallbackRoute: () =>
				{
					const tmpMicroApp = this.pict.providers.MicroApp;
					return (tmpMicroApp && tmpMicroApp.manifest && tmpMicroApp.manifest.Dashboards && tmpMicroApp.manifest.Dashboards.FallbackRoute) || null;
				},
				// A micro app can show each dashboard's Description under its title.
				getShowDescriptions: () =>
				{
					const tmpMicroApp = this.pict.providers.MicroApp;
					return !!(tmpMicroApp && tmpMicroApp.manifest && tmpMicroApp.manifest.Dashboards && (tmpMicroApp.manifest.Dashboards.ShowDescriptions === true));
				},
				// A micro app guards /PSRS/:RecordSet/* by its Entities list; a dashboard the person was granted gets its
				// recordset added so the list's paging and search links work.
				onRecordSetPrepared: (pRecordSet) =>
				{
					const tmpMicroApp = this.pict.providers.MicroApp;
					const tmpEntities = tmpMicroApp && tmpMicroApp.manifest && tmpMicroApp.manifest.Routes && tmpMicroApp.manifest.Routes.Entities;
					if (Array.isArray(tmpEntities) && tmpEntities.length > 0 && !tmpEntities.includes(pRecordSet))
					{
						tmpEntities.push(pRecordSet);
					}
				},
			});
		const fNavigate = (pRoute) =>
		{
			libWorkspaceConfigs.noteRouteChange(window.location.hash, pRoute);
			return this.pict.providers.PictRouter.navigate(pRoute);
		};
		tmpDashboards.actions.registerHandler('Sample.Open', (pAction) => fNavigate(`/Sample/Workspace/${pAction.IDSample}`));
		tmpDashboards.actions.registerHandler('Sample.Receive', (pAction) => fNavigate(`/Sample/Workspace/${pAction.IDSample}`));
		tmpDashboards.actions.registerHandler('Project.Open', (pAction) => fNavigate(`/Project/Workspace/${pAction.IDProject}`));
		tmpDashboards.actions.registerHandler('Material.Open', (pAction) => fNavigate(`/Material/Workspace/${pAction.IDMaterial}`));
		// A sample's form opens in that sample's Documents tab, where the rest of its work is; anything else renders in a new tab.
		tmpDashboards.actions.registerHandler('Document.Open', (pAction) =>
		{
			const tmpSampleWorkspace = this.pict.providers.SampleWorkspace;
			if ((pAction.IDSample > 0) && tmpSampleWorkspace && (typeof tmpSampleWorkspace.openSampleDocument === 'function'))
			{
				tmpSampleWorkspace.openSampleDocument(pAction.IDSample, pAction.IDDocument);
				return;
			}
			this.pict.HeadlightDocumentRender.openDocument(pAction.IDDocument, { target: '_blank' });
		});
		tmpDashboards.actions.registerHandler('Sample.Create', (pAction) =>
		{
			const tmpSampleWorkspace = this.pict.providers.SampleWorkspace;
			if (tmpSampleWorkspace && typeof tmpSampleWorkspace.prefillNew === 'function')
			{
				tmpSampleWorkspace.prefillNew({ IDProject: pAction.IDProject, IDLineItem: pAction.IDLineItem, IDMaterial: pAction.IDMaterial, SampleType: pAction.SampleType });
			}
			fNavigate('/PSRS/Sample/Create');
		});
	}

	/** Resolve a topic config from a route like '/Users' (leading '#' optional), or null. */
	topicForRoute(pRoute)
	{
		const tmpRoute = '#' + String(pRoute || '').replace(/^#/, '');
		const tmpConfigs = libNavigationTopics.TOPIC_CONFIGS;
		return Object.keys(tmpConfigs).map((pKey) => tmpConfigs[pKey]).find((pTopic) => pTopic.Route === tmpRoute) || null;
	}

	/**
	 * Fetch the platform KPI counts into AppData.DashboardStats — used by the Dashboard view
	 * and the nav-page jumbotrons. Mirrors the Dashboard loader so counts are available even
	 * when the Dashboard itself is never visited. Re-renders the active nav page on completion
	 * so its KPI values update from placeholders to real numbers.
	 */
	loadDashboardStatsAsync(fCallback)
	{
		const fDone = (typeof fCallback === 'function') ? fCallback : (() => {});
		if (!this.pict.AppData.DashboardStats)
		{
			this.pict.AppData.DashboardStats = {};
			libNavigationTopics.KPI_ENDPOINTS.forEach((pStat) => { this.pict.AppData.DashboardStats[pStat.Key] = '...'; });
		}
		const tmpSession = this.pict.AppData.CurrentUser;
		if (tmpSession)
		{
			this.pict.AppData.DashboardStats.UserName = `${tmpSession.NameFirst || ''} ${tmpSession.NameLast || ''}`.trim() || 'User';
		}
		const tmpRestClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		if (!tmpRestClient)
		{
			this.pict.log.warn('HeadlightRestClient not available for KPI counts.');
			return fDone();
		}

		let tmpCompleted = 0;
		const tmpTotal = libNavigationTopics.KPI_ENDPOINTS.length;
		libNavigationTopics.KPI_ENDPOINTS.forEach((pStat) =>
		{
			tmpRestClient.getJSON(pStat.Endpoint, (pError, pBody) =>
			{
				this.pict.AppData.DashboardStats[pStat.Key] = (!pError && pBody != null) ? (pBody.Count != null ? pBody.Count : pBody) : '--';
				// Re-paint as each count lands (debounced) so the jumbotron fills progressively and never
				// blocks on the slowest — or a non-responding — endpoint. The old all-complete gate left the
				// strip on placeholders until every count returned (or forever, if one never called back),
				// which is the "KPIs don't load until reload" race.
				this._scheduleKpiRefresh();
				if (++tmpCompleted >= tmpTotal)
				{
					fDone();
				}
			});
		});
	}

	/**
	 * Re-paint the active nav page's KPI strip from the latest DashboardStats. Targets the strip DOM
	 * in place (not a full view render) so it can't be dropped by the route latest-wins guard and does
	 * not rebuild the whole nav. Works for the Settings full browser and the scoped topic pages alike.
	 */
	_refreshNavPageKpis()
	{
		const tmpRoute = (typeof window !== 'undefined' && window.location && window.location.hash ? window.location.hash : '').replace(/^#/, '');
		const tmpTopic = (tmpRoute === '/Settings' || tmpRoute === '/Configurations' || tmpRoute === '' || tmpRoute === '/')
			? libNavigationTopics.TOPIC_CONFIGS.Settings
			: this.topicForRoute(tmpRoute);
		if (!tmpTopic) { return; }
		this.pict.AppData.NavKpis = libNavigationTopics.buildKpiRecords(this.pict.AppData.DashboardStats, tmpTopic.KpiKeys, this.isHeadlightSession());
		if (this.pict.ContentAssignment.getElement('.nav-kpi-strip').length > 0)
		{
			const tmpHTML = (this.pict.AppData.NavKpis || []).map((pKpi) => this.pict.parseTemplateByHash('Nav-Kpi-Card', pKpi)).join('');
			this.pict.ContentAssignment.assignContent('.nav-kpi-strip', tmpHTML);
		}
	}

	/** Debounced trigger for _refreshNavPageKpis — coalesces the rapid per-count updates into one paint. */
	_scheduleKpiRefresh()
	{
		if (this._kpiRefreshTimer) { clearTimeout(this._kpiRefreshTimer); }
		this._kpiRefreshTimer = setTimeout(() => { this._kpiRefreshTimer = null; this._refreshNavPageKpis(); }, 100);
	}

	/**
	 * Wrap the PSRS MeadowEndpoints providers for the named recordsets with the
	 * Settings translation adapter. Each provider's `getRecord` / `getRecords`
	 * gets `toDisplay` applied to outgoing records; `createRecord` / `updateRecord`
	 * apply `fromDisplay` to incoming records.
	 *
	 * @param {Array<string>} pRecordSetNames
	 */
	_installSettingsAdapterForRecordSets(pRecordSetNames)
	{
		const tmpMetaController = this.pict.services.PictSectionRecordSet;
		if (!tmpMetaController || !tmpMetaController.recordSetProviders)
		{
			this.pict.log.error('SettingsAdapter wiring: PictSectionRecordSet service or providers not available.');
			return;
		}
		for (const tmpRecordSet of pRecordSetNames)
		{
			const tmpProvider = tmpMetaController.recordSetProviders[tmpRecordSet];
			if (!tmpProvider)
			{
				this.pict.log.warn(`SettingsAdapter wiring: provider for ${tmpRecordSet} not found — skipping.`);
				continue;
			}
			const tmpOriginalGetRecord = tmpProvider.getRecord ? tmpProvider.getRecord.bind(tmpProvider) : null;
			const tmpOriginalGetRecordByGUID = tmpProvider.getRecordByGUID ? tmpProvider.getRecordByGUID.bind(tmpProvider) : null;
			const tmpOriginalReadRecord = tmpProvider.readRecord ? tmpProvider.readRecord.bind(tmpProvider) : null;
			const tmpOriginalGetRecords = tmpProvider.getRecords ? tmpProvider.getRecords.bind(tmpProvider) : null;
			const tmpOriginalCreate = tmpProvider.createRecord ? tmpProvider.createRecord.bind(tmpProvider) : null;
			const tmpOriginalUpdate = tmpProvider.updateRecord ? tmpProvider.updateRecord.bind(tmpProvider) : null;
			if (tmpOriginalGetRecord)
			{
				tmpProvider.getRecord = async (pIDOrGuid) =>
				{
					const tmpRecord = await tmpOriginalGetRecord(pIDOrGuid);
					libContractProjectTypesSettingsAdapter.toDisplay(tmpRecord);
					await this._loadTypeStatusesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadTypeDefaultDatesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadContractTypeAllowedProjectTypesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadPrototypeIntoDisplay(tmpRecordSet, tmpRecord);
					return tmpRecord;
				};
			}
			if (tmpOriginalGetRecordByGUID)
			{
				tmpProvider.getRecordByGUID = async (pGuid) =>
				{
					const tmpRecord = await tmpOriginalGetRecordByGUID(pGuid);
					libContractProjectTypesSettingsAdapter.toDisplay(tmpRecord);
					await this._loadTypeStatusesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadTypeDefaultDatesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadContractTypeAllowedProjectTypesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadPrototypeIntoDisplay(tmpRecordSet, tmpRecord);
					return tmpRecord;
				};
			}
			if (tmpOriginalReadRecord)
			{
				tmpProvider.readRecord = async (pIDOrGuid) =>
				{
					const tmpRecord = await tmpOriginalReadRecord(pIDOrGuid);
					libContractProjectTypesSettingsAdapter.toDisplay(tmpRecord);
					await this._loadTypeStatusesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadTypeDefaultDatesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadContractTypeAllowedProjectTypesIntoDisplay(tmpRecordSet, tmpRecord);
					await this._loadPrototypeIntoDisplay(tmpRecordSet, tmpRecord);
					return tmpRecord;
				};
			}
			if (tmpOriginalGetRecords)
			{
				tmpProvider.getRecords = async (pOptions) =>
				{
					const tmpResult = await tmpOriginalGetRecords(pOptions);
					if (tmpResult && Array.isArray(tmpResult.Records))
					{
						tmpResult.Records.forEach((pRecord) => libContractProjectTypesSettingsAdapter.toDisplay(pRecord));
					}
					else if (Array.isArray(tmpResult))
					{
						tmpResult.forEach((pRecord) => libContractProjectTypesSettingsAdapter.toDisplay(pRecord));
					}
					return tmpResult;
				};
			}
			if (tmpOriginalCreate)
			{
				tmpProvider.createRecord = async (pRecord) =>
				{
					// ProjectType / ContractType: route through the canonical comprehension
					// save (the type + its prototype shell, then statuses; contract types also
					// carry default-date definitions). The spreadsheet ImportProvider uses the
					// same provider, so editor- and import-created types are identical.
					if (tmpRecordSet === 'ProjectType')
					{
						return this._typeSaveProvider.saveProjectType(pRecord, true);
					}
					if (tmpRecordSet === 'ContractType')
					{
						return this._typeSaveProvider.saveContractType(pRecord, true);
					}
					return tmpOriginalCreate(pRecord);
				};
			}
			if (tmpOriginalUpdate)
			{
				tmpProvider.updateRecord = async (pRecord) =>
				{
					if (tmpRecordSet === 'ProjectType')
					{
						return this._typeSaveProvider.saveProjectType(pRecord, false);
					}
					if (tmpRecordSet === 'ContractType')
					{
						return this._typeSaveProvider.saveContractType(pRecord, false);
					}
					return tmpOriginalUpdate(pRecord);
				};
			}
			this.pict.log.info(`SettingsAdapter wired to ${tmpRecordSet} PSRS provider.`);
		}
	}

	/**
	 * Wrap the User recordset provider's createRecord so a new user's "Login Password" actually establishes a
	 * login credential. The generic create form exposes LoginPassword (a raw User column), but POSTing it to
	 * /1.0/User does NOT set the credential the login checks — that is only done by POST /1.0/Password (exactly
	 * what the bespoke "Reset Password" action calls). So: capture the entered password, keep it OUT of the User
	 * POST (never persist the plaintext column), create the user, then set the credential in the follow-up call
	 * the server honors. On failure the user still exists (password-less, same as before this fix) and the admin
	 * is told to use Reset Password. Idempotent.
	 */
	_installUserPasswordOnCreate()
	{
		const tmpMetaController = this.pict.services.PictSectionRecordSet;
		if (!tmpMetaController || !tmpMetaController.recordSetProviders) { return; }
		const tmpProvider = tmpMetaController.recordSetProviders['User'];
		if (!tmpProvider || (typeof tmpProvider.createRecord !== 'function') || tmpProvider._userPasswordOnCreateWrapped)
		{
			if (!tmpProvider) { this.pict.log.warn('User password-on-create wiring: User PSRS provider not found — skipping.'); }
			return;
		}
		tmpProvider._userPasswordOnCreateWrapped = true;
		const tmpOriginalCreate = tmpProvider.createRecord.bind(tmpProvider);
		tmpProvider.createRecord = async (pRecord) =>
		{
			let tmpPassword = '';
			let tmpRecordToCreate = pRecord;
			if (pRecord && pRecord.LoginPassword)
			{
				tmpPassword = String(pRecord.LoginPassword);
				tmpRecordToCreate = Object.assign({}, pRecord);
				delete tmpRecordToCreate.LoginPassword;   // don't persist the plaintext column; the credential is set below
			}
			const tmpResult = await tmpOriginalCreate(tmpRecordToCreate);
			const tmpIDUser = tmpResult && (tmpResult.IDUser || (tmpResult.Record && tmpResult.Record.IDUser));
			if (tmpPassword && tmpIDUser)
			{
				try
				{
					await this._setUserPasswordAsync(tmpIDUser, tmpPassword);
				}
				catch (pError)
				{
					this.pict.log.error(`User create: could not set the initial password for user ${tmpIDUser}: ${pError && pError.message ? pError.message : pError}`, pError);
					const tmpModal = this.pict.views['Pict-Section-Modal'];
					if (tmpModal && (typeof tmpModal.toast === 'function'))
					{
						tmpModal.toast('User created, but the login password could not be set — open the user and use “Reset Password”.', { type: 'warning', duration: 8000 });
					}
				}
			}
			return tmpResult;
		};
		this.pict.log.info('User password-on-create wired (POST /1.0/Password after User create).');
	}

	/**
	 * Upload a photo held by the form's ObservationPhoto field once the record saves (see RecordPhotoUploadAfterSave).
	 *
	 * @param {Array<string>} pRecordSetNames - recordsets whose entity has a photo field
	 */
	_installPhotoUploadAfterSave(pRecordSetNames)
	{
		libRecordPhotoUploadAfterSave.installPhotoUploadAfterSave(this.pict, pRecordSetNames);
	}

	/**
	 * Set a user's login credential via POST /1.0/Password (the same endpoint the UserWorkspace "Reset Password"
	 * action uses). Raw fetch with the session cookie — the JSON rest client can't carry this bespoke endpoint.
	 * @param {Number} pIDUser @param {String} pPassword @returns {Promise}
	 */
	_setUserPasswordAsync(pIDUser, pPassword)
	{
		const tmpClient = this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
		const tmpPrefix = (tmpClient && tmpClient.options && tmpClient.options.ServerURL) || '/1.0/';
		return fetch(`${tmpPrefix}Password`,
			{
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ IDUser: pIDUser, NewPassword: pPassword }),
			}).then((pResponse) =>
		{
			if (!pResponse.ok) { throw new Error(`server returned ${pResponse.status}`); }
			return pResponse.json().catch(() => null).then((pBody) =>
			{
				if (pBody && (pBody.Success === false)) { throw new Error(pBody.Error || 'password not set'); }
				return pBody;
			});
		});
	}

	/**
	 * @returns {*} The shared HeadlightRestClient used for the non-CRUD /Statuses
	 * endpoints, or null when it isn't registered.
	 */
	get _typeStatusRestClient()
	{
		return (this.fable && this.fable.HeadlightRestClient) ||
			(this.pict && this.pict.fable && this.pict.fable.HeadlightRestClient) ||
			(this.pict && this.pict.HeadlightRestClient) ||
			null;
	}

	/**
	 * The primary-key field name for a type recordset.
	 *
	 * @param {string} pEntity - 'ProjectType' | 'ContractType'
	 * @returns {string}
	 */
	_typeStatusIDField(pEntity)
	{
		return (pEntity === 'ContractType') ? 'IDContractType' : 'IDProjectType';
	}

	/**
	 * @returns {*} the shared TypeComprehensionSaveProvider fable service (owns the
	 * build-comprehension -> push -> pointer -> prune -> statuses save for both types).
	 */
	get _typeSaveProvider()
	{
		return (this.fable && this.fable.TypeComprehensionSaveProvider) ||
			(this.fable && this.fable.serviceManager && this.fable.serviceManager.instantiateServiceProvider('TypeComprehensionSaveProvider', {})) ||
			null;
	}

	/**
	 * Fetch a type's status lists from GET /1.0/{Entity}/:id/Statuses and write
	 * them into `record._DisplaySettings.*` so the editor textareas render them.
	 * No-ops (leaving empty display values) when the record has no id yet or the
	 * endpoint is unreachable.
	 *
	 * @param {string} pEntity
	 * @param {Object} pRecord
	 * @returns {Promise<Object>} the same record
	 */
	async _loadTypeStatusesIntoDisplay(pEntity, pRecord)
	{
		if (!pRecord)
		{
			return pRecord;
		}
		const tmpID = pRecord[this._typeStatusIDField(pEntity)];
		const tmpClient = this._typeStatusRestClient;
		if (!tmpID || !tmpClient)
		{
			return pRecord;
		}
		const tmpPayload = await new Promise((resolve) =>
		{
			tmpClient.getJSON(`${pEntity}/${tmpID}/Statuses`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.warn(`Could not load ${pEntity} statuses for ID ${tmpID}.`, { Error: pError.message });
					return resolve(null);
				}
				return resolve(pBody || null);
			});
		});
		if (!pRecord._DisplaySettings)
		{
			pRecord._DisplaySettings = {};
		}
		Object.assign(pRecord._DisplaySettings, libContractProjectTypesSettingsAdapter.statusPayloadToDisplay(tmpPayload || {}));
		return pRecord;
	}

	/**
	 * Fetch a contract type's allowed project types from
	 * GET /1.0/ContractType/:id/AllowedProjectTypes (a flat array of numeric
	 * IDProjectType values) and write two display values onto the record:
	 *   - `_DisplaySettings.AllowedProjectTypeIDs`       — comma-joined IDs; the
	 *     Edit picker (Entity ProjectType, ValueField IDProjectType) binds
	 *     this and renders each as its ProjectType Name.
	 *   - `_DisplaySettings.AllowedProjectTypeNamesText` — comma-joined Names,
	 *     for the read-only View manifest (no picker to resolve IDs → Names).
	 * No-ops for non-ContractType recordsets, and when the record has no id yet
	 * or the endpoint is unreachable.
	 *
	 * @param {string} pEntity
	 * @param {Object} pRecord
	 * @returns {Promise<Object>} the same record
	 */
	async _loadContractTypeAllowedProjectTypesIntoDisplay(pEntity, pRecord)
	{
		if (pEntity !== 'ContractType' || !pRecord)
		{
			return pRecord;
		}
		const tmpID = pRecord[this._typeStatusIDField(pEntity)];
		const tmpClient = this._typeStatusRestClient;
		if (!tmpID || !tmpClient)
		{
			return pRecord;
		}
		const tmpIDs = await new Promise((resolve) =>
		{
			tmpClient.getJSON(`${pEntity}/${tmpID}/AllowedProjectTypes`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.warn(`Could not load ${pEntity} allowed project types for ID ${tmpID}.`, { Error: pError.message });
					return resolve([]);
				}
				return resolve(Array.isArray(pBody) ? pBody.map((pV) => String(pV)) : []);
			});
		});
		// Resolve IDs → Names for the read-only View. The Edit picker resolves
		// names itself from its ProjectType option set, so this lookup is only
		// needed for the View text.
		const tmpNames = await new Promise((resolve) =>
		{
			if (tmpIDs.length === 0) return resolve([]);
			// Scope to this customer + a real page size — a bare `ProjectTypes` relies on the server default row cap
			// (~250) and can silently omit rows, leaving names blank on the read-only View for large catalogs.
			const tmpCustomerID = this.AppData.CurrentUser && this.AppData.CurrentUser.CustomerID;
			const tmpProjectTypesURL = tmpCustomerID
				? `ProjectTypes/FilteredTo/FBV~IDCustomer~EQ~${tmpCustomerID}/0/5000`
				: 'ProjectTypes/0/5000';
			tmpClient.getJSON(tmpProjectTypesURL, (pError, pBody) =>
			{
				if (pError || !Array.isArray(pBody)) return resolve([]);
				const tmpNameByID = {};
				for (const tmpType of pBody)
				{
					if (tmpType && tmpType.IDProjectType != null) tmpNameByID[String(tmpType.IDProjectType)] = tmpType.Name || '';
				}
				return resolve(tmpIDs.map((pID) => tmpNameByID[pID]).filter((pName) => pName && pName.length > 0));
			});
		});
		if (!pRecord._DisplaySettings)
		{
			pRecord._DisplaySettings = {};
		}
		Object.assign(pRecord._DisplaySettings,
			libContractProjectTypesSettingsAdapter.allowedProjectTypesPayloadToDisplay(tmpIDs, tmpNames));
		return pRecord;
	}

	/**
	 * Parse a type record's Settings defensively — it arrives as a parsed object
	 * (`Settings`) or, on some reads, a `SettingsJSON` string.
	 *
	 * @param {Object} pRecord
	 * @returns {Object}
	 */
	_parsePrototypeSettings(pRecord)
	{
		if (pRecord && pRecord.Settings && typeof pRecord.Settings === 'object')
		{
			return pRecord.Settings;
		}
		if (pRecord && typeof pRecord.SettingsJSON === 'string' && pRecord.SettingsJSON.length > 0)
		{
			try
			{
				const tmpParsed = JSON.parse(pRecord.SettingsJSON);
				return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {};
			}
			catch (pError)
			{
				return {};
			}
		}
		return {};
	}

	/**
	 * Surface the (normally hidden) prototype on the type's View screen: write the prototype
	 * project's / contract's name into `_DisplaySettings.PrototypeProjectName` /
	 * `PrototypeProjectName` resp. `PrototypeContractName`. No-ops when there is no prototype
	 * pointer yet or the rest client is unavailable.
	 *
	 * (Name only — the PSRS read view's template set doesn't render HTML/clickable inputs, and
	 * HPC has no in-app Contract view, so no "open" link is shown for either.)
	 *
	 * @param {string} pEntity - 'ProjectType' | 'ContractType'
	 * @param {Object} pRecord
	 * @returns {Promise<Object>} the same record
	 */
	async _loadPrototypeIntoDisplay(pEntity, pRecord)
	{
		if ((pEntity !== 'ProjectType' && pEntity !== 'ContractType') || !pRecord)
		{
			return pRecord;
		}
		const tmpClient = this._typeStatusRestClient;
		if (!tmpClient)
		{
			return pRecord;
		}
		const tmpSettings = this._parsePrototypeSettings(pRecord);
		const tmpIsContract = (pEntity === 'ContractType');
		const tmpProtoEntity = tmpIsContract ? 'Contract' : 'Project';
		const tmpProtoIDField = tmpIsContract ? 'IDContract' : 'IDProject';
		const tmpProtoID = Number(tmpIsContract ? tmpSettings.PrototypeIDContract : tmpSettings.PrototypeIDProject) || 0;
		if (!tmpProtoID)
		{
			return pRecord;
		}
		const tmpPrototype = await new Promise((resolve) =>
		{
			tmpClient.getEntitySet(tmpProtoEntity, `FBV~${tmpProtoIDField}~EQ~${tmpProtoID}`, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.warn(`Could not load prototype ${tmpProtoEntity} ${tmpProtoID}.`, { Error: pError.message });
					return resolve(null);
				}
				return resolve((Array.isArray(pRecords) && pRecords[0]) ? pRecords[0] : null);
			}, 0, 1);
		});
		if (!tmpPrototype)
		{
			return pRecord;
		}
		if (!pRecord._DisplaySettings)
		{
			pRecord._DisplaySettings = {};
		}
		const tmpName = (typeof tmpPrototype.Name === 'string') ? tmpPrototype.Name : '';
		if (tmpIsContract)
		{
			pRecord._DisplaySettings.PrototypeContractName = tmpName;
		}
		else
		{
			pRecord._DisplaySettings.PrototypeProjectName = tmpName;
		}
		return pRecord;
	}

	/**
	 * Read a type's default-date definitions directly off the ProjectTypeDefaultDate /
	 * ContractTypeDefaultDate entity (filtered by the type id) and write them into
	 * `record._DisplaySettings.DefaultDates` for the Tabular RecordSet input — the same
	 * way the prototype joins load. No-ops when the record has no id yet or the rest
	 * client is unavailable.
	 *
	 * @param {string} pEntity - 'ProjectType' | 'ContractType'
	 * @param {Object} pRecord
	 * @returns {Promise<Object>} the same record
	 */
	async _loadTypeDefaultDatesIntoDisplay(pEntity, pRecord)
	{
		if (!pRecord)
		{
			return pRecord;
		}
		const tmpIDField = this._typeStatusIDField(pEntity);
		const tmpID = pRecord[tmpIDField];
		const tmpClient = this._typeStatusRestClient;
		if (!tmpID || !tmpClient)
		{
			return pRecord;
		}
		const tmpDateEntity = (pEntity === 'ContractType') ? 'ContractTypeDefaultDate' : 'ProjectTypeDefaultDate';
		const tmpRows = await new Promise((resolve) =>
		{
			tmpClient.getEntitySet(tmpDateEntity, `FBV~${tmpIDField}~EQ~${tmpID}`, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.warn(`Could not load ${tmpDateEntity} for ${tmpIDField} ${tmpID}.`, { Error: pError.message });
					return resolve([]);
				}
				return resolve(Array.isArray(pRecords) ? pRecords : []);
			}, 0, 200000);
		});
		if (!pRecord._DisplaySettings)
		{
			pRecord._DisplaySettings = {};
		}
		const tmpDates = tmpRows.map((pRow) => ({
			Hash: (pRow && typeof pRow.Hash === 'string') ? pRow.Hash : '',
			Name: (pRow && typeof pRow.Name === 'string') ? pRow.Name : '',
		}));
		pRecord._DisplaySettings.DefaultDates = tmpDates;
		// The read-only View binds to this pre-formatted string: a plain text input over the
		// object array renders "[object Object],..." (see ContractType-View.json). The Edit grid
		// still reads the structured DefaultDates array above.
		pRecord._DisplaySettings.DefaultDatesText = this._formatDefaultDatesForDisplay(tmpDates);
		return pRecord;
	}

	/**
	 * Render the default-date type definitions as a human-readable, read-only string —
	 * "Name (Hash)" per entry (or whichever of the two is present), comma-joined.
	 *
	 * @param {Array<{Hash?: string, Name?: string}>} pDates
	 * @returns {string}
	 */
	_formatDefaultDatesForDisplay(pDates)
	{
		const tmpDates = Array.isArray(pDates) ? pDates : [];
		return tmpDates
			.map((pRow) =>
			{
				const tmpName = (pRow && typeof pRow.Name === 'string') ? pRow.Name.trim() : '';
				const tmpHash = (pRow && typeof pRow.Hash === 'string') ? pRow.Hash.trim() : '';
				if (tmpName && tmpHash) return `${tmpName} (${tmpHash})`;
				return tmpName || tmpHash;
			})
			.filter((pText) => pText.length > 0)
			.join(', ');
	}

	/** Read the first entity matching a single-field equals filter, or null. */
	_readEntityByFilter(pEntity, pField, pValue)
	{
		const tmpClient = this._typeStatusRestClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient) return resolve(null);
			tmpClient.getEntitySet(pEntity, `FBV~${pField}~EQ~${pValue}`, (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords) || pRecords.length === 0) return resolve(null);
				return resolve(pRecords[0]);
			}, 0, 50);
		});
	}

	/** Parse a SettingsJSON string into an object (tolerant). */
	_parseSettingsJSON(pSettingsJSON)
	{
		if (typeof pSettingsJSON !== 'string' || pSettingsJSON.length === 0) return {};
		try
		{
			const tmpParsed = JSON.parse(pSettingsJSON); return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {};
		}
		catch (pError)
		{
			return {};
		}
	}

	async onAfterLogout()
	{
		this.pict.ContentAssignment.addClass('#HeadlightLogin-Standard-Wrap', 'is-active');
		this.pict.ContentAssignment.removeClass('#HeadlightLogin-Standard-Wrap', 'is-hidden');
		this.pict.views.HeadlightLogin.customizeLoginPage(customLoginPageOptions);
		this.pict.views.HeadlightLogin.show();
	}
}

module.exports = PlatformConfiguration;

module.exports.default_configuration = (
	{
		Name: 'PlatformConfiguration',
		Hash: 'PlatformConfigurationApplication',
		InitializeOnLoad: true,
		AutoRender: false,
		// This is a ROUTER-DRIVEN application: the PlatformConfigurationRouter decides which
		// view occupies the application container, via resolveCurrentRoute() after login. The
		// base HeadlightConfigurableApplication still calls this.render() during its post-login
		// data-load cycle (onAfterLoadDataAsync), which paints MainViewportViewIdentifier into
		// the SAME container the router renders into. Pointing it at a real screen (it used to be
		// the KPI-tile 'PlatformConfigurationDashboard') made that screen flash on every load/deep-link
		// before the router resolved the intended route. 'Empty' renders nothing, so the router is the
		// single source of truth for what the user sees. (That Dashboard view has since been removed —
		// its stats were unreliable — and '/Dashboard' now redirects to the configuration home.)
		MainViewportViewIdentifier: 'Empty',
		Document:
	{
		Required: false,
	},
	});

module.exports.default_configuration.pict_configuration = (
	{
		'Product': 'PlatformConfiguration',
		'ProductVersion': '1.0.0',

		'PictApplicationConfiguration':
	{
		AutoRenderMainViewportViewAfterInitialize: false,
	},
		'IsHeadlightWebApplication': true,

		'Login':
	{
		Required: true,
	},

		'Manifests':
	{
		// List manifests
		'User-List': manifestUserList,
		'Customer-List': manifestCustomerList,
		'Organization-List': manifestOrganizationList,
		'Project-List': manifestProjectList,
		'Role-List': manifestRoleList,
		'ObservationManifest-List': manifestObservationManifestList,
		'ArtifactType-List': manifestArtifactTypeList,
		'ReportNamedInstance-List': manifestReportNamedInstanceList,
		'ObservationTag-List': manifestObservationTagList,
		'UserRoleJoin-List': manifestUserRoleJoinList,
		'RoleEdge-List': manifestRoleEdgeList,
		'RolePermission-List': manifestRolePermissionList,
		'ProjectTagType-List': manifestProjectTagTypeList,
		'DocumentApprovalWorkflow-List': manifestDocumentApprovalWorkflowList,
		'DocumentApprovalState-List': manifestDocumentApprovalStateList,
		'DocumentApprovalStep-List': manifestDocumentApprovalStepList,
		'ObservationApprovalWorkflow-List': manifestObservationApprovalWorkflowList,
		'ObservationApprovalState-List': manifestObservationApprovalStateList,
		'ObservationApprovalStep-List': manifestObservationApprovalStepList,
		'ContractType-List': manifestContractTypeList,
		'ProjectType-List': manifestProjectTypeList,
		'Contract-List': manifestContractList,

		// View (read) manifests
		'User-View': manifestUserView,
		'Customer-View': manifestCustomerView,
		'Organization-View': manifestOrganizationView,
		'Project-View': manifestProjectView,
		'Role-View': manifestRoleView,
		'ObservationManifest-View': manifestObservationManifestView,
		'ArtifactType-View': manifestArtifactTypeView,
		'ReportNamedInstance-View': manifestReportNamedInstanceView,
		'ObservationTag-View': manifestObservationTagView,
		'UserRoleJoin-View': manifestUserRoleJoinView,
		'RoleEdge-View': manifestRoleEdgeView,
		'RolePermission-View': manifestRolePermissionView,
		'ProjectTagType-View': manifestProjectTagTypeView,
		'DocumentApprovalWorkflow-View': manifestDocumentApprovalWorkflowView,
		'DocumentApprovalState-View': manifestDocumentApprovalStateView,
		'DocumentApprovalStep-View': manifestDocumentApprovalStepView,
		'ObservationApprovalWorkflow-View': manifestObservationApprovalWorkflowView,
		'ObservationApprovalState-View': manifestObservationApprovalStateView,
		'ObservationApprovalStep-View': manifestObservationApprovalStepView,
		'ContractType-View': manifestContractTypeView,
		'ProjectType-View': manifestProjectTypeView,

		// Edit manifests
		'User-Edit': manifestUserEdit,
		'Customer-Edit': manifestCustomerEdit,
		'Organization-Edit': manifestOrganizationEdit,
		'Organization-Create': manifestOrganizationCreate,
		'Project-Edit': manifestProjectEdit,
		'Role-Edit': manifestRoleEdit,
		'ObservationManifest-Edit': manifestObservationManifestEdit,
		'ArtifactType-Edit': manifestArtifactTypeEdit,
		'ReportNamedInstance-Edit': manifestReportNamedInstanceEdit,
		'ObservationTag-Edit': manifestObservationTagEdit,
		'UserRoleJoin-Edit': manifestUserRoleJoinEdit,
		'RoleEdge-Edit': manifestRoleEdgeEdit,
		'RolePermission-Edit': manifestRolePermissionEdit,
		'ProjectTagType-Edit': manifestProjectTagTypeEdit,
		'DocumentApprovalWorkflow-Edit': manifestDocumentApprovalWorkflowEdit,
		'DocumentApprovalState-Edit': manifestDocumentApprovalStateEdit,
		'DocumentApprovalStep-Edit': manifestDocumentApprovalStepEdit,
		'ObservationApprovalWorkflow-Edit': manifestObservationApprovalWorkflowEdit,
		'ObservationApprovalState-Edit': manifestObservationApprovalStateEdit,
		'ObservationApprovalStep-Edit': manifestObservationApprovalStepEdit,
		'ContractType-Edit': manifestContractTypeEdit,
		'ProjectType-Edit': manifestProjectTypeEdit,
	},

		// Field-decoration policy for the entity pickers the recordset module builds (the association
		// editors, the entity quick-filters, and the bulk-delete replacement picker). Opt-in per entity: a
		// decorated picker grows a ⚙ that lets a user pin an extra record column (e.g. VendorCode) onto each
		// dropdown row to tell same-named records apart (the "six Volkerts" problem). IDCustomer is dropped
		// everywhere (single-tenant noise); the picker hides the audit / value / text columns itself and the
		// recordset adds GUID<Entity>. Resolved per entity by pict-section-recordset resolveFieldDecoration().
		'FieldDecoration':
	{
		'Global': false,
		'Entities': { 'Organization': true, 'Contract': true, 'Project': true, 'LineItem': true },
		'IgnoreFields': [ 'IDCustomer' ],
	},

		'DefaultRecordSetConfigurations':
	[
		{
			'RecordSet': 'User',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'User',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'NameFirst', 'NameLast', 'Email', 'LoginID' ],
			// Quick Filters (opt-in across Headlight; see ComplexFilterProvider) — common user lookups + audit.
			'QuickFilters': [ 'NameLast', 'Email', { 'Field': 'CreateDate', 'Label': 'Created' }, { 'Field': 'CreatingIDUser', 'Label': 'Created by' } ],
			'RecordSetListColumns':
			[
				{ Key: 'NameFirst', DisplayName: 'First Name' },
				{ Key: 'NameLast', DisplayName: 'Last Name' },
				{ Key: 'Email', DisplayName: 'Email' },
				{ Key: 'LoginID', DisplayName: 'Login ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'User-List',
			'RecordSetListManifests': [ 'User-List' ],
			'RecordSetDashboardDefaultManifest': 'User-List',
			'RecordSetDashboardManifests': [ 'User-List' ],
			'RecordSetListHasExtraColumns': false,
			// The "Role" filter (IDRole). User.IDRole is ONLY the base level (0-5: Field Personnel / Office Staff /
			// …); the rich named roles an admin actually assigns (e.g. "Lab Technician") live in UserRoleJoin, so a
			// plain FBV~IDRole~EQ~<rich role> matches nobody. REPLACE the auto-generated FK-EQ clause with an
			// external-join-through-UserRoleJoin membership filter: pick one or more Roles, resolve the users who
			// HOLD them (UserRoleJoin.IDUser where IDRole IN picks), and INN the User list to those. Mirrors the LIMS
			// DestinationLab pattern (Sample→SampleLabJoin→Lab). NOTE: this covers customer-level (UserRoleJoin)
			// grants; base-level (User.IDRole) and project/lab (UserContextRole) grants are a separate concern.
			'FieldFilterClauses':
			{
				'IDRole':
				[
					{
						'FilterKey': 'IDRole',
						'ClauseKey': 'IDRole_Membership',
						'Label': 'Role',
						'DisplayName': 'Assigned to',
						'Type': 'ExternalJoinSelectedValueList',
						'ExternalFilterByColumns': [ 'Name' ],
						'MaximumSelectedExternalRecords': 20,
						'ExternalRecordDisplayTemplate': '{~D:Record.Data.Name~}',
						'CoreConnectionColumn': 'IDUser',
						'JoinTable': 'UserRoleJoin',
						'JoinTableExternalConnectionColumn': 'IDRole',
						'JoinTableCoreConnectionColumn': 'IDUser',
						'ExternalFilterByTable': 'Role',
						'ExternalFilterByTableConnectionColumn': 'IDRole',
						// Marker: UserRoleMembershipFilter.patchUserRoleMembershipFilter expands this clause at fetch time
						// into the FULL union of role holders (base User.IDRole + UserRoleJoin + UserContextRole). The
						// stock UserRoleJoin resolution above is the safe fallback if that expansion ever fails.
						'RoleMembershipUnion': true,
						'Ordinal': 1,
					},
				],
			},
			'RecordSetIgnoreFilterFields':
			[
				'IDUser',
				'GUIDUser',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'ExternalSyncDate',
				'ExternalSyncGUID',
				'IDCustomer',
				// Secrets + opaque internal fields — nonsense (and unsafe) to expose as filter options.
				'LoginPassword',
				'PasswordResetKey',
				'LastSessionID',
				'UIHash',
				'SettingsJSON',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'User-View',
			'RecordSetReadManifestsView': [ 'User-View' ],
			'RecordSetReadDefaultManifestEdit': 'User-Edit',
			'RecordSetReadManifestsEdit': [ 'User-Edit' ],
		},
		{
			'RecordSet': 'ObservationManifest',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ObservationManifest',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'DisplayName', 'Category' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'DisplayName', DisplayName: 'Display Name' },
				{ Key: 'Category', DisplayName: 'Category' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ObservationManifest-List',
			'RecordSetListManifests': [ 'ObservationManifest-List' ],
			'RecordSetDashboardDefaultManifest': 'ObservationManifest-List',
			'RecordSetDashboardManifests': [ 'ObservationManifest-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDObservationManifest',
				'GUIDObservationManifest',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'SettingsJSON',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ObservationManifest-View',
			'RecordSetReadManifestsView': [ 'ObservationManifest-View' ],
			'RecordSetReadDefaultManifestEdit': 'ObservationManifest-Edit',
			'RecordSetReadManifestsEdit': [ 'ObservationManifest-Edit' ],
		},
		{
			'RecordSet': 'ArtifactType',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ArtifactType',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ArtifactType-List',
			'RecordSetListManifests': [ 'ArtifactType-List' ],
			'RecordSetDashboardDefaultManifest': 'ArtifactType-List',
			'RecordSetDashboardManifests': [ 'ArtifactType-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDArtifactType',
				'GUIDArtifactType',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'Schemata',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ArtifactType-View',
			'RecordSetReadManifestsView': [ 'ArtifactType-View' ],
			'RecordSetReadDefaultManifestEdit': 'ArtifactType-Edit',
			'RecordSetReadManifestsEdit': [ 'ArtifactType-Edit' ],
		},
		{
			'RecordSet': 'ReportNamedInstance',

			// Reuse the forms app's rich metadata editor (Fields | JSON pict-section-code) for View/Edit/Create
			// instead of the generic PSRS form. The List stays on PSRS for browsing + filtering. PSRS delegates
			// via RecordSet-RecordBaseView.delegateToCustomView -> the view's onRecordSetCustomView().
			'RecordSetCustomViews': { 'View': 'ReportNamedInstanceCreate', 'Edit': 'ReportNamedInstanceCreate', 'Create': 'ReportNamedInstanceCreate' },

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ReportNamedInstance',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Hash' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Hash', DisplayName: 'Hash' },
				{ Key: 'DefaultReport', DisplayName: 'Default' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ReportNamedInstance-List',
			'RecordSetListManifests': [ 'ReportNamedInstance-List' ],
			'RecordSetDashboardDefaultManifest': 'ReportNamedInstance-List',
			'RecordSetDashboardManifests': [ 'ReportNamedInstance-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDReportNamedInstance',
				'GUIDReportNamedInstance',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'OptionsJSON',
				// Internal sort key — not a meaningful thing for a user to filter reports by.
				'Ordinal',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ReportNamedInstance-View',
			'RecordSetReadManifestsView': [ 'ReportNamedInstance-View' ],
			'RecordSetReadDefaultManifestEdit': 'ReportNamedInstance-Edit',
			'RecordSetReadManifestsEdit': [ 'ReportNamedInstance-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'Project',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Project',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Description', 'Status', 'ExternalProjectID' ],
			// Quick Filters (opt-in across Headlight; see ComplexFilterProvider) — common project lookups + audit.
			'QuickFilters': [ 'Name', 'Status', { 'Field': 'CreateDate', 'Label': 'Created' }, { 'Field': 'CreatingIDUser', 'Label': 'Created by' } ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Status', DisplayName: 'Status' },
				{ Key: 'ExternalProjectID', DisplayName: 'External ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'Project-List',
			'RecordSetListManifests': [ 'Project-List' ],
			'RecordSetDashboardDefaultManifest': 'Project-List',
			'RecordSetDashboardManifests': [ 'Project-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDProject',
				'GUIDProject',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'ExternalSyncDate',
				'ExternalSyncGUID',
				'IDCustomer',
				'IDProjectType',
				'IDContract',
				'IDOrganization',
				'CustomProperties',
				'Description',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'Project-View',
			'RecordSetReadManifestsView': [ 'Project-View' ],
			'RecordSetReadDefaultManifestEdit': 'Project-Edit',
			'RecordSetReadManifestsEdit': [ 'Project-Edit' ],

			// The Project view manages its connections via association tabs (the bespoke "Project view"):
			// Observation Types links the manifests to this project with per-row Journal/Spreadsheet/Ordinal
			// config + "Add defaults". More project connections (forms, tags, …) become further tabs here.
			'ReadLayout': 'Tab',
			'RecordSetReadTabs':
			[
				{ Type: 'Association', Association: 'ProjectObservationManifest', Title: 'Observation Types', PickerMode: 'single' },
			],
		},
		{
			'RecordSet': 'Contract',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Contract',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Description', 'Status', 'PrimaryProjectNumber', 'ExternalContractID' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'PrimaryProjectNumber', DisplayName: 'Primary Project #' },
				{ Key: 'Status', DisplayName: 'Status' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'Contract-List',
			'RecordSetListManifests': [ 'Contract-List' ],
			'RecordSetDashboardDefaultManifest': 'Contract-List',
			'RecordSetDashboardManifests': [ 'Contract-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDContract',
				'GUIDContract',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'ExternalSyncDate',
			],
			// The bespoke ContractWorkspace owns the read (the router GUID bridge intercepts
			// /PSRS/Contract/View); the list above is just the browse surface.
		},
		{
			'RecordSet': 'Role',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Role',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Description', 'PermissionLevel', 'Type' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'PermissionLevel', DisplayName: 'Permission Level' },
				{ Key: 'Type', DisplayName: 'Type' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'Role-List',
			'RecordSetListManifests': [ 'Role-List' ],
			'RecordSetDashboardDefaultManifest': 'Role-List',
			'RecordSetDashboardManifests': [ 'Role-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDRole',
				'GUIDRole',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'LinkedIDUser',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'Role-View',
			'RecordSetReadManifestsView': [ 'Role-View' ],
			'RecordSetReadDefaultManifestEdit': 'Role-Edit',
			'RecordSetReadManifestsEdit': [ 'Role-Edit' ],
		},
		{
			'RecordSet': 'ObservationTag',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ObservationTag',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Tag', 'TagType', 'Description' ],
			'RecordSetListColumns':
			[
				{ Key: 'Tag', DisplayName: 'Tag' },
				{ Key: 'TagType', DisplayName: 'Tag Type' },
				{ Key: 'Global', DisplayName: 'Global' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ObservationTag-List',
			'RecordSetListManifests': [ 'ObservationTag-List' ],
			'RecordSetDashboardDefaultManifest': 'ObservationTag-List',
			'RecordSetDashboardManifests': [ 'ObservationTag-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDObservationTag',
				'GUIDObservationTag',
				'IDCustomer',
				'IDOrganization',
				'IDBidItem',
				'IDLineItem',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ObservationTag-View',
			'RecordSetReadManifestsView': [ 'ObservationTag-View' ],
			'RecordSetReadDefaultManifestEdit': 'ObservationTag-Edit',
			'RecordSetReadManifestsEdit': [ 'ObservationTag-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'Organization',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Organization',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Type', 'City', 'State', 'VendorCode' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Type', DisplayName: 'Type' },
				{ Key: 'City', DisplayName: 'City' },
				{ Key: 'State', DisplayName: 'State' },
				{ Key: 'Approved', DisplayName: 'Approved' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'Organization-List',
			'RecordSetListManifests': [ 'Organization-List' ],
			'RecordSetDashboardDefaultManifest': 'Organization-List',
			'RecordSetDashboardManifests': [ 'Organization-List' ],
			'RecordSetListHasExtraColumns': false,
			// Show the list "+ New" affordance, like the catalog master lists. Those get their create button
			// from MasterListRegistry.applyEntryToLiveConfig (which gates it by can(entity,'Create')); this
			// hand-written literal is not a catalog entry, so it opts in here directly. A `true` here still
			// yields to the permission gate if that path ever covers literals (it only overrides an explicit
			// `false`). Create routes through the sectioned Organization-Create manifest (below), not the raw
			// schema form.
			'RecordSetListShowCreateButton': true,
			'RecordSetIgnoreFilterFields':
			[
				'IDOrganization',
				'GUIDOrganization',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'ExternalSyncDate',
				'ExternalOrganizationID',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'Organization-View',
			'RecordSetReadManifestsView': [ 'Organization-View' ],
			'RecordSetReadDefaultManifestEdit': 'Organization-Edit',
			'RecordSetReadManifestsEdit': [ 'Organization-Edit' ],
			// Honor the View manifest's own InputTypes instead of PSRS's blanket View-mode read-only rewrite,
			// so the boolean fields (Approved, Disadvantaged Business) render as checkboxes rather than "0"/"1"
			// text. Every non-boolean View descriptor is already declared `ReadOnly`, so the view stays read-only
			// except those checkboxes (which have no Save action to persist a toggle).
			'RecordSetReadOverrideReadOnly': true,
			// Route "+ New" through a dedicated, sectioned create manifest (Identity / Details / Contact /
			// Address) instead of the auto-generated raw-schema form — same gating pattern ContractType /
			// ProjectType use. `RecordSetCreateManifestOnly` is what PSRS checks before honoring the create
			// manifest; without it the schema fallback wins.
			'RecordSetCreateManifestOnly': true,
			'RecordSetCreateDefaultManifest': 'Organization-Create',
			'RecordSetCreateManifests': [ 'Organization-Create' ],

			// Split layout: the record on the left, its connection tabs on the right. The "Addresses" tab is a
			// bespoke master/detail editor (OrganizationAddressTab) for the org's OrganizationAddress children.
			'ReadLayout': 'Split',
			'RecordSetReadTabs':
			[
				{ 'Type': 'View', 'View': 'OrganizationAddresses', 'Title': 'Addresses' },
				// Materials this organization supplies/produces — the reciprocal of the Material read view's
				// "Organizations" tab. Each relationship's ExpirationDate is editable inline (set / update /
				// backfill / clear) via the MaterialOrganizationAssociationEditor subclass registered for the
				// Organization side; this literal's tabs are otherwise deliberately minimal, so it is added here.
				{ 'Type': 'Association', 'Association': 'MaterialOrganization', 'Title': 'Materials' },
			],
		},
		{
			'RecordSet': 'UserRoleJoin',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'UserRoleJoin',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'IDUser', 'IDRole' ],
			'RecordSetListColumns':
			[
				{ Key: 'IDUser', DisplayName: 'User ID' },
				{ Key: 'IDRole', DisplayName: 'Role ID' },
				{ Key: 'ProxyIDRole', DisplayName: 'Proxy Role ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'UserRoleJoin-List',
			'RecordSetListManifests': [ 'UserRoleJoin-List' ],
			'RecordSetDashboardDefaultManifest': 'UserRoleJoin-List',
			'RecordSetDashboardManifests': [ 'UserRoleJoin-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDUserRoleJoin',
				'GUIDUserRoleJoin',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'UserRoleJoin-View',
			'RecordSetReadManifestsView': [ 'UserRoleJoin-View' ],
			'RecordSetReadDefaultManifestEdit': 'UserRoleJoin-Edit',
			'RecordSetReadManifestsEdit': [ 'UserRoleJoin-Edit' ],
		},
		{
			'RecordSet': 'RoleEdge',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'RoleEdge',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'ParentIDRole', 'ChildIDRole' ],
			'RecordSetListColumns':
			[
				{ Key: 'ParentIDRole', DisplayName: 'Parent Role ID' },
				{ Key: 'ChildIDRole', DisplayName: 'Child Role ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'RoleEdge-List',
			'RecordSetListManifests': [ 'RoleEdge-List' ],
			'RecordSetDashboardDefaultManifest': 'RoleEdge-List',
			'RecordSetDashboardManifests': [ 'RoleEdge-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDRoleEdge',
				'GUIDRoleEdge',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'RoleEdge-View',
			'RecordSetReadManifestsView': [ 'RoleEdge-View' ],
			'RecordSetReadDefaultManifestEdit': 'RoleEdge-Edit',
			'RecordSetReadManifestsEdit': [ 'RoleEdge-Edit' ],
		},
		{
			'RecordSet': 'RolePermission',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'RolePermission',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Hash', 'Context' ],
			'RecordSetListColumns':
			[
				{ Key: 'Hash', DisplayName: 'Permission Hash' },
				{ Key: 'Allowed', DisplayName: 'Allowed' },
				{ Key: 'IDRole', DisplayName: 'Role ID' },
				{ Key: 'Context', DisplayName: 'Context' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'RolePermission-List',
			'RecordSetListManifests': [ 'RolePermission-List' ],
			'RecordSetDashboardDefaultManifest': 'RolePermission-List',
			'RecordSetDashboardManifests': [ 'RolePermission-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDRolePermission',
				'GUIDRolePermission',
				'IDContext',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'RolePermission-View',
			'RecordSetReadManifestsView': [ 'RolePermission-View' ],
			'RecordSetReadDefaultManifestEdit': 'RolePermission-Edit',
			'RecordSetReadManifestsEdit': [ 'RolePermission-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'Customer',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Customer',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Code' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Code', DisplayName: 'Code' },
				{ Key: 'Enabled', DisplayName: 'Enabled' },
				{ Key: 'Timezone', DisplayName: 'Timezone' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'Customer-List',
			'RecordSetListManifests': [ 'Customer-List' ],
			'RecordSetDashboardDefaultManifest': 'Customer-List',
			'RecordSetDashboardManifests': [ 'Customer-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDCustomer',
				'GUIDCustomer',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'CustomProperties',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'Customer-View',
			'RecordSetReadManifestsView': [ 'Customer-View' ],
			'RecordSetReadDefaultManifestEdit': 'Customer-Edit',
			'RecordSetReadManifestsEdit': [ 'Customer-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'ProjectTagType',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ProjectTagType',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'TagType', 'Name' ],
			'RecordSetListColumns':
			[
				{ Key: 'TagType', DisplayName: 'Tag Type' },
				{ Key: 'Name', DisplayName: 'Display Name' },
				{ Key: 'Ordinal', DisplayName: 'Ordinal' },
				{ Key: 'IDProject', DisplayName: 'Project ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ProjectTagType-List',
			'RecordSetListManifests': [ 'ProjectTagType-List' ],
			'RecordSetDashboardDefaultManifest': 'ProjectTagType-List',
			'RecordSetDashboardManifests': [ 'ProjectTagType-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDProjectTagType',
				'GUIDProjectTagType',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ProjectTagType-View',
			'RecordSetReadManifestsView': [ 'ProjectTagType-View' ],
			'RecordSetReadDefaultManifestEdit': 'ProjectTagType-Edit',
			'RecordSetReadManifestsEdit': [ 'ProjectTagType-Edit' ],
		},
		{
			'RecordSet': 'DocumentApprovalWorkflow',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'DocumentApprovalWorkflow',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'FromStatus', 'ToStatus' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Transition Name' },
				{ Key: 'FromStatus', DisplayName: 'From Status' },
				{ Key: 'ToStatus', DisplayName: 'To Status' },
				{ Key: 'IDProject', DisplayName: 'Project ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'DocumentApprovalWorkflow-List',
			'RecordSetListManifests': [ 'DocumentApprovalWorkflow-List' ],
			'RecordSetDashboardDefaultManifest': 'DocumentApprovalWorkflow-List',
			'RecordSetDashboardManifests': [ 'DocumentApprovalWorkflow-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDDocumentApprovalWorkflow',
				'IDCustomer',
				'EdgeOptions',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'DocumentApprovalWorkflow-View',
			'RecordSetReadManifestsView': [ 'DocumentApprovalWorkflow-View' ],
			'RecordSetReadDefaultManifestEdit': 'DocumentApprovalWorkflow-Edit',
			'RecordSetReadManifestsEdit': [ 'DocumentApprovalWorkflow-Edit' ],
		},
		{
			'RecordSet': 'DocumentApprovalState',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'DocumentApprovalState',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Hash' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'State Name' },
				{ Key: 'Hash', DisplayName: 'Hash' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'DocumentApprovalState-List',
			'RecordSetListManifests': [ 'DocumentApprovalState-List' ],
			'RecordSetDashboardDefaultManifest': 'DocumentApprovalState-List',
			'RecordSetDashboardManifests': [ 'DocumentApprovalState-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDDocumentApprovalState',
				'GUIDDocumentApprovalState',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'DocumentApprovalState-View',
			'RecordSetReadManifestsView': [ 'DocumentApprovalState-View' ],
			'RecordSetReadDefaultManifestEdit': 'DocumentApprovalState-Edit',
			'RecordSetReadManifestsEdit': [ 'DocumentApprovalState-Edit' ],
		},
		{
			'RecordSet': 'DocumentApprovalStep',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'DocumentApprovalStep',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Ordinal' ],
			'RecordSetListColumns':
			[
				{ Key: 'IDDocumentApprovalState', DisplayName: 'State ID' },
				{ Key: 'Ordinal', DisplayName: 'Ordinal' },
				{ Key: 'IDRole', DisplayName: 'Role ID' },
				{ Key: 'IncludeHigherRoles', DisplayName: 'Higher Roles' },
				{ Key: 'PerformLock', DisplayName: 'Lock' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'DocumentApprovalStep-List',
			'RecordSetListManifests': [ 'DocumentApprovalStep-List' ],
			'RecordSetDashboardDefaultManifest': 'DocumentApprovalStep-List',
			'RecordSetDashboardManifests': [ 'DocumentApprovalStep-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDDocumentApprovalStep',
				'GUIDDocumentApprovalStep',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'DocumentApprovalStep-View',
			'RecordSetReadManifestsView': [ 'DocumentApprovalStep-View' ],
			'RecordSetReadDefaultManifestEdit': 'DocumentApprovalStep-Edit',
			'RecordSetReadManifestsEdit': [ 'DocumentApprovalStep-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'ObservationApprovalWorkflow',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ObservationApprovalWorkflow',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'FromStatus', 'ToStatus' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Transition Name' },
				{ Key: 'FromStatus', DisplayName: 'From Status' },
				{ Key: 'ToStatus', DisplayName: 'To Status' },
				{ Key: 'IDProject', DisplayName: 'Project ID' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ObservationApprovalWorkflow-List',
			'RecordSetListManifests': [ 'ObservationApprovalWorkflow-List' ],
			'RecordSetDashboardDefaultManifest': 'ObservationApprovalWorkflow-List',
			'RecordSetDashboardManifests': [ 'ObservationApprovalWorkflow-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDObservationApprovalWorkflow',
				'IDCustomer',
				'EdgeOptions',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ObservationApprovalWorkflow-View',
			'RecordSetReadManifestsView': [ 'ObservationApprovalWorkflow-View' ],
			'RecordSetReadDefaultManifestEdit': 'ObservationApprovalWorkflow-Edit',
			'RecordSetReadManifestsEdit': [ 'ObservationApprovalWorkflow-Edit' ],
		},
		{
			'RecordSet': 'ObservationApprovalState',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ObservationApprovalState',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Hash' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'State Name' },
				{ Key: 'Hash', DisplayName: 'Hash' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ObservationApprovalState-List',
			'RecordSetListManifests': [ 'ObservationApprovalState-List' ],
			'RecordSetDashboardDefaultManifest': 'ObservationApprovalState-List',
			'RecordSetDashboardManifests': [ 'ObservationApprovalState-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDObservationApprovalState',
				'GUIDObservationApprovalState',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ObservationApprovalState-View',
			'RecordSetReadManifestsView': [ 'ObservationApprovalState-View' ],
			'RecordSetReadDefaultManifestEdit': 'ObservationApprovalState-Edit',
			'RecordSetReadManifestsEdit': [ 'ObservationApprovalState-Edit' ],
		},
		{
			'RecordSet': 'ObservationApprovalStep',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ObservationApprovalStep',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Ordinal', 'ObservationType' ],
			'RecordSetListColumns':
			[
				{ Key: 'IDObservationApprovalState', DisplayName: 'State ID' },
				{ Key: 'Ordinal', DisplayName: 'Ordinal' },
				{ Key: 'ObservationType', DisplayName: 'Obs Type' },
				{ Key: 'IDRole', DisplayName: 'Role ID' },
				{ Key: 'IncludeHigherRoles', DisplayName: 'Higher Roles' },
				{ Key: 'PerformLock', DisplayName: 'Lock' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ObservationApprovalStep-List',
			'RecordSetListManifests': [ 'ObservationApprovalStep-List' ],
			'RecordSetDashboardDefaultManifest': 'ObservationApprovalStep-List',
			'RecordSetDashboardManifests': [ 'ObservationApprovalStep-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDObservationApprovalStep',
				'GUIDObservationApprovalStep',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ObservationApprovalStep-View',
			'RecordSetReadManifestsView': [ 'ObservationApprovalStep-View' ],
			'RecordSetReadDefaultManifestEdit': 'ObservationApprovalStep-Edit',
			'RecordSetReadManifestsEdit': [ 'ObservationApprovalStep-Edit' ],
			// View honors the manifest InputTypes so boolean fields render as checkboxes, not "0"/"1" (no pickers here).
			'RecordSetReadOverrideReadOnly': true,
		},
		{
			'RecordSet': 'ContractType',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ContractType',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Hash', 'Description' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Hash', DisplayName: 'Hash' },
				{ Key: 'Description', DisplayName: 'Description' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ContractType-List',
			'RecordSetListManifests': [ 'ContractType-List' ],
			'RecordSetDashboardDefaultManifest': 'ContractType-List',
			'RecordSetDashboardManifests': [ 'ContractType-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDContractType',
				'GUIDContractType',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'SettingsJSON',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ContractType-View',
			'RecordSetReadManifestsView': [ 'ContractType-View' ],
			'RecordSetReadDefaultManifestEdit': 'ContractType-Edit',
			'RecordSetReadManifestsEdit': [ 'ContractType-Edit' ],
			// Route the "+ New" affordance through the same friendly Settings editor
			// used for Edit, instead of the PSRS auto-generated raw-schema form.
			// `RecordSetCreateManifestOnly` is the gate PSRS checks before honoring
			// `RecordSetCreateDefaultManifest`; without it the schema fallback wins.
			'RecordSetCreateManifestOnly': true,
			'RecordSetCreateDefaultManifest': 'ContractType-Edit',
			'RecordSetCreateManifests': [ 'ContractType-Edit' ],
		},
		{
			'RecordSet': 'ProjectType',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'ProjectType',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'Hash', 'Description' ],
			'RecordSetListColumns':
			[
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'Hash', DisplayName: 'Hash' },
				{ Key: 'Description', DisplayName: 'Description' },
				{ Key: 'Ordinal', DisplayName: 'Order' },
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListDefaultManifest': 'ProjectType-List',
			'RecordSetListManifests': [ 'ProjectType-List' ],
			'RecordSetDashboardDefaultManifest': 'ProjectType-List',
			'RecordSetDashboardManifests': [ 'ProjectType-List' ],
			'RecordSetListHasExtraColumns': false,
			'RecordSetIgnoreFilterFields':
			[
				'IDProjectType',
				'GUIDProjectType',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDCustomer',
				'IDProgram',
				'SettingsJSON',
			],

			'RecordSetReadManifestOnly': true,
			'RecordSetReadDefaultManifestView': 'ProjectType-View',
			'RecordSetReadManifestsView': [ 'ProjectType-View' ],
			'RecordSetReadDefaultManifestEdit': 'ProjectType-Edit',
			'RecordSetReadManifestsEdit': [ 'ProjectType-Edit' ],
			// Route the "+ New" affordance through the same friendly Settings editor
			// used for Edit, instead of the PSRS auto-generated raw-schema form.
			// `RecordSetCreateManifestOnly` is the gate PSRS checks before honoring
			// `RecordSetCreateDefaultManifest`; without it the schema fallback wins.
			'RecordSetCreateManifestOnly': true,
			'RecordSetCreateDefaultManifest': 'ProjectType-Edit',
			'RecordSetCreateManifests': [ 'ProjectType-Edit' ],
		},
	].concat(libMasterListBuilder.buildRecordSetConfigurations(libMasterListCatalog))
	// Lightweight recordset registrations for the FK-picker targets the generated manifests reference but that
	// aren't catalog/literal record sets (OrganizationAddress, AccountType, MixType, …) — enough for the picker
	// to resolve options + display names. Duplicates of real literals (Organization/Contract/Project) are
	// dropped by the dedupe below (first occurrence wins).
	.concat(libCatalogManifests.PICKER_TARGETS.map((pTarget) =>
		({ 'RecordSet': pTarget.Entity, 'RecordSetType': 'MeadowEndpoint', 'RecordSetMeadowEntity': pTarget.Entity, 'RecordSetURLPrefix': '/1.0/', 'SearchFields': pTarget.SearchFields })))
	.map((pRecordSetConfiguration) =>
	{
		const tmpDefaults = { 'RowClickOpensRecord': true };
		if (PSRS_FOREIGN_KEY_ENTITIES[pRecordSetConfiguration.RecordSet]) { tmpDefaults.ForeignKeyEntities = PSRS_FOREIGN_KEY_ENTITIES[pRecordSetConfiguration.RecordSet]; }
		if (PSRS_FIELD_LABELS[pRecordSetConfiguration.RecordSet]) { tmpDefaults.FieldLabels = PSRS_FIELD_LABELS[pRecordSetConfiguration.RecordSet]; }
		const tmpConfig = Object.assign(tmpDefaults, pRecordSetConfiguration);
		// Catalog entities with generated manifests (build-catalog-manifests.js): route read + create through
		// them so the forms are sectioned/curated (Data Audit section, entity pickers, boolean checkboxes)
		// rather than the raw /Schema form. OverrideReadOnly lets a view with boolean fields render checkboxes.
		const tmpCatalogManifest = CATALOG_MANIFEST_ENTITIES.get(tmpConfig.RecordSet);
		if (tmpCatalogManifest)
		{
			const tmpEntity = tmpConfig.RecordSet;
			tmpConfig.RecordSetReadManifestOnly = true;
			tmpConfig.RecordSetReadDefaultManifestView = `${ tmpEntity }-View`;
			tmpConfig.RecordSetReadManifestsView = [ `${ tmpEntity }-View` ];
			tmpConfig.RecordSetReadDefaultManifestEdit = `${ tmpEntity }-Edit`;
			tmpConfig.RecordSetReadManifestsEdit = [ `${ tmpEntity }-Edit` ];
			tmpConfig.RecordSetCreateManifestOnly = true;
			tmpConfig.RecordSetCreateDefaultManifest = `${ tmpEntity }-Create`;
			tmpConfig.RecordSetCreateManifests = [ `${ tmpEntity }-Create` ];
			if (tmpCatalogManifest.OverrideReadOnly) { tmpConfig.RecordSetReadOverrideReadOnly = true; }
			// Externally-synced master lists (NoCreate on the catalog entry, e.g. PhysicalAsset): suppress the list
			// "+ New" button. Edit/Create routes are bridged to the workspace/list in the router.
			const tmpRawCatalogEntry = libMasterListCatalog.find((pEntry) => pEntry && pEntry.Entity === tmpEntity);
			if (tmpRawCatalogEntry && tmpRawCatalogEntry.NoCreate) { tmpConfig.RecordSetListShowCreateButton = false; }
			// Curated list columns (name / codes / type / key text — never raw FK ids or 0/1 booleans).
			if (!tmpConfig.RecordSetListColumns && Array.isArray(tmpCatalogManifest.ListColumns) && (tmpCatalogManifest.ListColumns.length > 0))
			{
				tmpConfig.RecordSetListColumns = tmpCatalogManifest.ListColumns;
			}
		}
		return tmpConfig;
	})
	// A picker target that is already a full literal/catalog config keeps that config — drop the lightweight
	// duplicate (first occurrence wins, so the richer literal/catalog entry survives).
	.filter((pConfig, pIndex, pArray) => (pArray.findIndex((pOther) => pOther.RecordSet === pConfig.RecordSet) === pIndex)),
	});

// Register the generated catalog master-list manifests (build-catalog-manifests.js) into the manifest map the
// recordset module reads via getManifest. Assigned after the literal so the config wiring above can reference
// them by hash; done before the merge guard below so they get the same ReferenceManifests normalization.
Object.assign(module.exports.default_configuration.pict_configuration.Manifests, libCatalogManifests.MANIFESTS);

// PSRS merge guard. pict-section-form's `bootstrapPictFormViewsFromManifest` merges a newly
// bootstrapped manifest into the previously bootstrapped one and writes into the prior
// manifest's `ReferenceManifests` WITHOUT first checking that object exists. So bootstrapping
// an Edit manifest that declares ReferenceManifests (the ProjectType / ContractType default-date
// grids) right after any manifest that does NOT — e.g. opening a ContractType from its View —
// throws "Cannot read properties of undefined (reading '<reference-manifest>')". Normalize every
// registered manifest to declare a ReferenceManifests object so the merge is always safe. The
// require()d manifest objects are shared singletons, so mutating them here normalizes what PSRS
// reads via getManifest. Remove once the upstream framework guards the merge.
for (const tmpManifest of Object.values((module.exports.default_configuration
	&& module.exports.default_configuration.pict_configuration
	&& module.exports.default_configuration.pict_configuration.Manifests) || {}))
{
	if (tmpManifest && typeof tmpManifest === 'object' && !tmpManifest.ReferenceManifests)
	{
		tmpManifest.ReferenceManifests = {};
	}
}
